// src/ScreenTimeAnalytics.js

import React, { useState, useEffect, useRef } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts';
import { Link } from 'react-router-dom';
import NavBar from './NavBar';
import { FaClock } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './ScreenTimeAnalyticsPageTest.css';
import './AnalyticsPage.css';
import './AgentAssist.css';
import { getAllSBUs, getAllTracks, getGroupMapping, GROUP_MAPPING } from './groupMapping';
import { getUserMapping, getAllUserSBUs, getAllUserTracks, USERMAIL_MAPPING, loadUserMappingFromCSV } from './userMapping';

const FilterTypeButtons = ({ filterType, onFilterTypeChange }) => (
  <div className="filter-type-group">
    <button
      className={filterType === 'month' ? 'filter-type-btn active' : 'filter-type-btn'}
      onClick={() => onFilterTypeChange('month')}
    >
      Month
    </button>
    <button
      className={filterType === 'date' ? 'filter-type-btn active' : 'filter-type-btn'}
      onClick={() => onFilterTypeChange('date')}
    >
      Date
    </button>
  </div>
);

const MonthSelector = ({ year, month, onYearChange, onMonthChange, label = "Select Month:" }) => {
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const monthIndex = typeof month === 'number' ? month - 1 : monthNames.indexOf(month);
  const selectedDate = new Date(parseInt(year), monthIndex);
  
  return (
    <div className="month-selector">
      <label className="filter-label">{label}</label>
      <DatePicker
        selected={selectedDate}
        onChange={(date) => {
          if (date) {
            onYearChange(date.getFullYear());
            const newMonth = monthNames[date.getMonth()];
            onMonthChange(newMonth);
          }
        }}
        dateFormat="MM/yyyy"
        showMonthYearPicker
        className="date-input"
        placeholderText="Select Month"
        popperPlacement="bottom-start"
        style={{ minWidth: '200px' }}
      />
    </div>
  );
};

const DateRangeSelector = ({ fromDate, toDate, onFromDateChange, onToDateChange }) => (
  <div className="date-selector">
    <div className="date-range-container">
      <div className="date-group">
        <label className="filter-label-nowrap">From:</label>
        <DatePicker
          selected={fromDate ? new Date(fromDate) : null}
          onChange={(date) => {
            if (date) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              onFromDateChange(`${year}-${month}-${day}`);
            }
          }}
          dateFormat="yyyy-MM-dd"
          className="date-input"
          placeholderText="From Date"
          popperPlacement="bottom-start"
        />
      </div>
      <div className="date-group">
        <label className="filter-label-nowrap">To:</label>
        <DatePicker
          selected={toDate ? new Date(toDate) : null}
          onChange={(date) => {
            if (date) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              onToDateChange(`${year}-${month}-${day}`);
            }
          }}
          dateFormat="yyyy-MM-dd"
          className="date-input"
          placeholderText="To Date"
          popperPlacement="bottom-start"
        />
      </div>
    </div>
  </div>
);

const ScreenTimeBarChart = ({ data, dataKey = "screenTime", fill, height = 250, displayNameKey = "name", formatScreenTime }) => (
  <div className="chart-container">
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey={displayNameKey}
          stroke="#6b7280"
          interval={0}
          height={70}
          tick={({ x, y, payload }) => {
            const label = payload.value || '';
            const maxChars = 10;
            const words = label.split(/[\s&]+/);
            const lines = [];
            let current = '';
            words.forEach(w => {
              if (current && (current + ' ' + w).length > maxChars) { lines.push(current); current = w; }
              else { current = current ? current + ' ' + w : w; }
            });
            if (current) lines.push(current);
            return (<g transform={`translate(${x},${y})`}>{lines.map((line, i) => (<text key={i} x={0} y={0} dy={10 + i * 11} textAnchor="middle" fill="#6b7280" fontSize={8}>{line}</text>))}</g>);
          }}
        />
        <YAxis
          stroke="#6b7280"
          style={{ fontSize: '12px' }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
          formatter={(value) => [formatScreenTime(value), 'Screen Time']}
        />
        <Legend />
        <Bar
          dataKey={dataKey}
          fill={fill}
          barSize={12}
          radius={[8, 8, 0, 0]}
          name="Screen Time (hrs)"
        />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

// Emails to exclude from all analytics data
const EXCLUDED_EMAILS = new Set([
  'admin@example.com',
  'user@example.com', 
]);

const isExcludedEmail = (email) => {
  if (!email) return false;
  return EXCLUDED_EMAILS.has(email.toLowerCase().trim());
};

const UserAnalyticsTest = ({ userName, userEmail, onLogout, canViewUserAnalytics = false }) => {
  // Load saved filters from localStorage
  const loadSavedFilters = () => {
    try {
      const saved = localStorage.getItem('screenTimeFilters');
      return saved ? JSON.parse(saved) : {};
    } catch (err) {
      console.error('Error loading saved filters:', err);
      return {};
    }
  };

  const savedFilters = loadSavedFilters();

  // Assignment Group filters
  const [selectedGroupMonth, setSelectedGroupMonth] = useState(savedFilters.selectedGroupMonth || 'January');
  const [selectedGroupWeek, setSelectedGroupWeek] = useState(savedFilters.selectedGroupWeek || '1');
  const [selectedGroupFromDate, setSelectedGroupFromDate] = useState(savedFilters.selectedGroupFromDate || '2026-01-01');
  const [selectedGroupToDate, setSelectedGroupToDate] = useState(savedFilters.selectedGroupToDate || '2026-01-09');
  const [selectedGroupYear, setSelectedGroupYear] = useState(savedFilters.selectedGroupYear || '2026');
  const [selectedGroup, setSelectedGroup] = useState(savedFilters.selectedGroup || null);
  const [groupFilterType, setGroupFilterType] = useState(savedFilters.groupFilterType || 'month');
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  
  // Team filters
  const [selectedTeamMonth, setSelectedTeamMonth] = useState(savedFilters.selectedTeamMonth || 'January');
  const [selectedTeamWeek, setSelectedTeamWeek] = useState(savedFilters.selectedTeamWeek || '1');
  const [selectedTeamFromDate, setSelectedTeamFromDate] = useState(savedFilters.selectedTeamFromDate || '2026-01-01');
  const [selectedTeamToDate, setSelectedTeamToDate] = useState(savedFilters.selectedTeamToDate || '2026-01-09');
  const [selectedTeamYear, setSelectedTeamYear] = useState(savedFilters.selectedTeamYear || '2026');
  const [teamFilterType, setTeamFilterType] = useState(savedFilters.teamFilterType || 'month');

  // All Teams Combined filters
  const [selectedAllTeamsMonth, setSelectedAllTeamsMonth] = useState(savedFilters.selectedAllTeamsMonth || 'January');
  const [selectedAllTeamsWeek, setSelectedAllTeamsWeek] = useState(savedFilters.selectedAllTeamsWeek || '1');
  const [selectedAllTeamsFromDate, setSelectedAllTeamsFromDate] = useState(savedFilters.selectedAllTeamsFromDate || '2026-01-01');
  const [selectedAllTeamsToDate, setSelectedAllTeamsToDate] = useState(savedFilters.selectedAllTeamsToDate || '2026-01-09');
  const [selectedAllTeamsYear, setSelectedAllTeamsYear] = useState(savedFilters.selectedAllTeamsYear || '2026');
  const [allTeamsFilterType, setAllTeamsFilterType] = useState(savedFilters.allTeamsFilterType || 'month');

  // Positive Feedback filters
  const [selectedPositiveFeedbackMonth, setSelectedPositiveFeedbackMonth] = useState(savedFilters.selectedPositiveFeedbackMonth || 'January');
  const [selectedPositiveFeedbackWeek, setSelectedPositiveFeedbackWeek] = useState(savedFilters.selectedPositiveFeedbackWeek || '1');
  const [selectedPositiveFeedbackFromDate, setSelectedPositiveFeedbackFromDate] = useState(savedFilters.selectedPositiveFeedbackFromDate || '2026-01-01');
  const [selectedPositiveFeedbackToDate, setSelectedPositiveFeedbackToDate] = useState(savedFilters.selectedPositiveFeedbackToDate || '2026-01-09');
  const [selectedPositiveFeedbackYear, setSelectedPositiveFeedbackYear] = useState(savedFilters.selectedPositiveFeedbackYear || '2026');
  const [positiveFeedbackFilterType, setPositiveFeedbackFilterType] = useState(savedFilters.positiveFeedbackFilterType || 'month');
  const [positiveFeedbackSBU, setPositiveFeedbackSBU] = useState([]);
  const [showPositiveFeedbackSBUDropdown, setShowPositiveFeedbackSBUDropdown] = useState(false);
  const [positiveFeedbackTrack, setPositiveFeedbackTrack] = useState([]);
  const [showPositiveFeedbackTrackDropdown, setShowPositiveFeedbackTrackDropdown] = useState(false);
  const [positiveFeedbackAvailableTracks, setPositiveFeedbackAvailableTracks] = useState([]);

  // Negative Feedback filters
  const [selectedNegativeFeedbackMonth, setSelectedNegativeFeedbackMonth] = useState(savedFilters.selectedNegativeFeedbackMonth || 'January');
  const [selectedNegativeFeedbackWeek, setSelectedNegativeFeedbackWeek] = useState(savedFilters.selectedNegativeFeedbackWeek || '1');
  const [selectedNegativeFeedbackFromDate, setSelectedNegativeFeedbackFromDate] = useState(savedFilters.selectedNegativeFeedbackFromDate || '2026-01-01');
  const [selectedNegativeFeedbackToDate, setSelectedNegativeFeedbackToDate] = useState(savedFilters.selectedNegativeFeedbackToDate || '2026-01-09');
  const [selectedNegativeFeedbackYear, setSelectedNegativeFeedbackYear] = useState(savedFilters.selectedNegativeFeedbackYear || '2026');
  const [negativeFeedbackFilterType, setNegativeFeedbackFilterType] = useState(savedFilters.negativeFeedbackFilterType || 'month');
  const [negativeFeedbackSBU, setNegativeFeedbackSBU] = useState([]);
  const [showNegativeFeedbackSBUDropdown, setShowNegativeFeedbackSBUDropdown] = useState(false);
  const [negativeFeedbackTrack, setNegativeFeedbackTrack] = useState([]);
  const [showNegativeFeedbackTrackDropdown, setShowNegativeFeedbackTrackDropdown] = useState(false);
  const [negativeFeedbackAvailableTracks, setNegativeFeedbackAvailableTracks] = useState([]);

  // Login Count filters
  const [loginCountFilterType, setLoginCountFilterType] = useState(savedFilters.loginCountFilterType || 'month');
  const [loginCountMonth, setLoginCountMonth] = useState(savedFilters.loginCountMonth || 'January');
  const [loginCountYear, setLoginCountYear] = useState(savedFilters.loginCountYear || '2026');
  const [loginCountFromDate, setLoginCountFromDate] = useState(savedFilters.loginCountFromDate || '2026-01-01');
  const [loginCountToDate, setLoginCountToDate] = useState(savedFilters.loginCountToDate || '2026-01-09');
  const [loginCountSBU, setLoginCountSBU] = useState([]);
  const [showLoginCountSBUDropdown, setShowLoginCountSBUDropdown] = useState(false);
  const [loginCountTrack, setLoginCountTrack] = useState([]);
  const [showLoginCountTrackDropdown, setShowLoginCountTrackDropdown] = useState(false);
  const [loginCountAvailableTracks, setLoginCountAvailableTracks] = useState([]);
  const [loginCountData, setLoginCountData] = useState([]);

  // Ticket Process filters
  const [ticketProcessFilterType, setTicketProcessFilterType] = useState(savedFilters.ticketProcessFilterType || 'month');
  const [ticketProcessMonth, setTicketProcessMonth] = useState(savedFilters.ticketProcessMonth || 'January');
  const [ticketProcessYear, setTicketProcessYear] = useState(savedFilters.ticketProcessYear || '2026');
  const [ticketProcessFromDate, setTicketProcessFromDate] = useState(savedFilters.ticketProcessFromDate || '2026-01-01');
  const [ticketProcessToDate, setTicketProcessToDate] = useState(savedFilters.ticketProcessToDate || '2026-01-09');
  const [ticketProcessSBU, setTicketProcessSBU] = useState([]);
  const [showTicketProcessSBUDropdown, setShowTicketProcessSBUDropdown] = useState(false);
  const [ticketProcessTrack, setTicketProcessTrack] = useState([]);
  const [showTicketProcessTrackDropdown, setShowTicketProcessTrackDropdown] = useState(false);
  const [ticketProcessAvailableTracks, setTicketProcessAvailableTracks] = useState([]);
  const [ticketProcessData, setTicketProcessData] = useState([]);

  // Data states
  const [assignmentGroupData, setAssignmentGroupData] = useState([]);
  const [teamScreenTimeData, setTeamScreenTimeData] = useState([]);
  const [allTeamsData, setAllTeamsData] = useState([]);
  const [positiveFeedbackData, setPositiveFeedbackData] = useState([]);
  const [negativeFeedbackData, setNegativeFeedbackData] = useState([]);

  // Login Trends filters
  const [loginTrendsFilterType, setLoginTrendsFilterType] = useState('month');
  const [loginTrendsMonth, setLoginTrendsMonth] = useState(savedFilters.loginTrendsMonth || 'February');
  const [loginTrendsYear, setLoginTrendsYear] = useState(savedFilters.loginTrendsYear || '2026');
  const [loginTrendsWeek, setLoginTrendsWeek] = useState('4');
  const [loginTrendsSBU, setLoginTrendsSBU] = useState([]);
  const [showLoginTrendsSBUDropdown, setShowLoginTrendsSBUDropdown] = useState(false);
  const [loginTrendsTrack, setLoginTrendsTrack] = useState([]);
  const [showLoginTrendsTrackDropdown, setShowLoginTrendsTrackDropdown] = useState(false);
  const [loginTrendsAvailableTracks, setLoginTrendsAvailableTracks] = useState([]);
  const [loginTrendsData, setLoginTrendsData] = useState([]);
  // Login Trends: store period keys for dynamic bar rendering
  const [loginTrendsPeriods, setLoginTrendsPeriods] = useState([]);

  // Ticket Process Trends filters
  const [ticketTrendsFilterType, setTicketTrendsFilterType] = useState('month');
  const [ticketTrendsMonth, setTicketTrendsMonth] = useState(savedFilters.ticketTrendsMonth || 'February');
  const [ticketTrendsYear, setTicketTrendsYear] = useState(savedFilters.ticketTrendsYear || '2026');
  const [ticketTrendsWeek, setTicketTrendsWeek] = useState('4');
  const [ticketTrendsSBU, setTicketTrendsSBU] = useState([]);
  const [showTicketTrendsSBUDropdown, setShowTicketTrendsSBUDropdown] = useState(false);
  const [ticketTrendsTrack, setTicketTrendsTrack] = useState([]);
  const [showTicketTrendsTrackDropdown, setShowTicketTrendsTrackDropdown] = useState(false);
  const [ticketTrendsAvailableTracks, setTicketTrendsAvailableTracks] = useState([]);
  const [ticketTrendsData, setTicketTrendsData] = useState([]);
  const [ticketTrendsPeriods, setTicketTrendsPeriods] = useState([]);

  // Feedback Trends filters
  const [feedbackTrendsFilterType, setFeedbackTrendsFilterType] = useState('month');
  const [feedbackTrendsMonth, setFeedbackTrendsMonth] = useState(savedFilters.feedbackTrendsMonth || 'February');
  const [feedbackTrendsYear, setFeedbackTrendsYear] = useState(savedFilters.feedbackTrendsYear || '2026');
  const [feedbackTrendsWeek, setFeedbackTrendsWeek] = useState('4');
  const [feedbackTrendsSBU, setFeedbackTrendsSBU] = useState([]);
  const [showFeedbackTrendsSBUDropdown, setShowFeedbackTrendsSBUDropdown] = useState(false);
  const [feedbackTrendsTrack, setFeedbackTrendsTrack] = useState([]);
  const [showFeedbackTrendsTrackDropdown, setShowFeedbackTrendsTrackDropdown] = useState(false);
  const [feedbackTrendsAvailableTracks, setFeedbackTrendsAvailableTracks] = useState([]);
  const [feedbackTrendsData, setFeedbackTrendsData] = useState([]);
  const [feedbackTrendsPeriods, setFeedbackTrendsPeriods] = useState([]);

  // Areas to Improved Trends filters
  const [areasTrendsFilterType, setAreasTrendsFilterType] = useState('month');
  const [areasTrendsMonth, setAreasTrendsMonth] = useState(savedFilters.areasTrendsMonth || 'February');
  const [areasTrendsYear, setAreasTrendsYear] = useState(savedFilters.areasTrendsYear || '2026');
  const [areasTrendsWeek, setAreasTrendsWeek] = useState('4');
  const [areasTrendsSBU, setAreasTrendsSBU] = useState([]);
  const [showAreasTrendsSBUDropdown, setShowAreasTrendsSBUDropdown] = useState(false);
  const [areasTrendsTrack, setAreasTrendsTrack] = useState([]);
  const [showAreasTrendsTrackDropdown, setShowAreasTrendsTrackDropdown] = useState(false);
  const [areasTrendsAvailableTracks, setAreasTrendsAvailableTracks] = useState([]);
  const [areasTrendsData, setAreasTrendsData] = useState([]);
  const [areasTrendsPeriods, setAreasTrendsPeriods] = useState([]);

  // Screen Time Trends filters
  const [screenTimeTrendsFilterType, setScreenTimeTrendsFilterType] = useState('month');
  const [screenTimeTrendsMonth, setScreenTimeTrendsMonth] = useState(savedFilters.screenTimeTrendsMonth || 'February');
  const [screenTimeTrendsYear, setScreenTimeTrendsYear] = useState(savedFilters.screenTimeTrendsYear || '2026');
  const [screenTimeTrendsWeek, setScreenTimeTrendsWeek] = useState('4');
  const [screenTimeTrendsSBU, setScreenTimeTrendsSBU] = useState([]);
  const [showScreenTimeTrendsSBUDropdown, setShowScreenTimeTrendsSBUDropdown] = useState(false);
  const [screenTimeTrendsTrack, setScreenTimeTrendsTrack] = useState([]);
  const [showScreenTimeTrendsTrackDropdown, setShowScreenTimeTrendsTrackDropdown] = useState(false);
  const [screenTimeTrendsAvailableTracks, setScreenTimeTrendsAvailableTracks] = useState([]);
  const [screenTimeTrendsData, setScreenTimeTrendsData] = useState([]);
  const [screenTimeTrendsPeriods, setScreenTimeTrendsPeriods] = useState([]);

  const [availableGroups, setAvailableGroups] = useState([]);
  const [availableSBUs, setAvailableSBUs] = useState([]);
  const [availableTracks, setAvailableTracks] = useState([]);
  const [availableUserSBUs, setAvailableUserSBUs] = useState([]);
  const [availableUserTracks, setAvailableUserTracks] = useState([]);
  const [selectedSBU, setSelectedSBU] = useState([]);
  const [showSBUDropdown, setShowSBUDropdown] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState([]);
  const [showTrackDropdown, setShowTrackDropdown] = useState(false);

  // Single global filter controls (shown once below header)
  const [globalFilterType, setGlobalFilterType] = useState(savedFilters.globalFilterType || 'month');
  const [globalMonth, setGlobalMonth] = useState(savedFilters.globalMonth || 'January');
  const [globalYear, setGlobalYear] = useState(savedFilters.globalYear || '2026');
  const [globalWeek, setGlobalWeek] = useState(savedFilters.globalWeek || '1');
  const [globalFromDate, setGlobalFromDate] = useState(savedFilters.globalFromDate || '2026-01-01');
  const [globalToDate, setGlobalToDate] = useState(savedFilters.globalToDate || '2026-01-09');
  const [globalSBUValue, setGlobalSBUValue] = useState(savedFilters.globalSBUValue || 'all');
  const [globalTrackValue, setGlobalTrackValue] = useState(savedFilters.globalTrackValue || 'all');
  
  // Backend data state
  const [ticketsData, setTicketsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [aiFeedbackData, setAiFeedbackData] = useState([]);
  const [sessionData, setSessionData] = useState([]);
  const [processButtonData, setProcessButtonData] = useState([]);

  // Month and week options
  const monthOptions = [
    { value: 'January', label: 'January' },
    { value: 'February', label: 'February' },
    { value: 'March', label: 'March' },
    { value: 'April', label: 'April' },
    { value: 'May', label: 'May' },
    { value: 'June', label: 'June' },
    { value: 'July', label: 'July' },
    { value: 'August', label: 'August' },
    { value: 'September', label: 'September' },
    { value: 'October', label: 'October' },
    { value: 'November', label: 'November' },
    { value: 'December', label: 'December' }
  ];

  const getGlobalTrackOptions = () => {
    if (globalSBUValue === 'all') {
      return [
        { value: 'all', label: 'All Tracks' },
        ...getAllUserTracks().map(track => ({ value: track, label: track }))
      ];
    }

    const tracksInSBU = new Set();
    Object.values(USERMAIL_MAPPING || {}).forEach(mapping => {
      if (mapping.sbu === globalSBUValue) {
        tracksInSBU.add(mapping.track);
      }
    });

    return [
      { value: 'all', label: 'All Tracks' },
      ...getAllUserTracks()
        .filter(track => tracksInSBU.has(track))
        .map(track => ({ value: track, label: track }))
    ];
  };

  const globalTrackOptions = getGlobalTrackOptions();

  // Ref for dropdown to handle click outside
  const groupDropdownRef = useRef(null);
  const positiveFeedbackSBUDropdownRef = useRef(null);
  const positiveFeedbackTrackDropdownRef = useRef(null);
  const negativeFeedbackSBUDropdownRef = useRef(null);
  const negativeFeedbackTrackDropdownRef = useRef(null);
  const trackDropdownRef = useRef(null);
  const sbuDropdownRef = useRef(null);
  const loginCountSBUDropdownRef = useRef(null);
  const loginCountTrackDropdownRef = useRef(null);
  const ticketProcessSBUDropdownRef = useRef(null);
  const ticketProcessTrackDropdownRef = useRef(null);
  const loginTrendsSBUDropdownRef = useRef(null);
  const loginTrendsTrackDropdownRef = useRef(null);
  const ticketTrendsSBUDropdownRef = useRef(null);
  const ticketTrendsTrackDropdownRef = useRef(null);
  const feedbackTrendsSBUDropdownRef = useRef(null);
  const feedbackTrendsTrackDropdownRef = useRef(null);
  const areasTrendsSBUDropdownRef = useRef(null);
  const areasTrendsTrackDropdownRef = useRef(null);
  const screenTimeTrendsSBUDropdownRef = useRef(null);
  const screenTimeTrendsTrackDropdownRef = useRef(null);

  // Group selection handlers
  const handleSelectAllGroups = () => {
    if (isAllGroupsSelected()) {
      setSelectedGroup([]);
    } else {
      setSelectedGroup(availableGroups.filter(g => g.value !== 'all'));
    }
  };

  const handleToggleGroup = (group) => {
    const currentGroups = Array.isArray(selectedGroup) ? selectedGroup : [];
    const isSelected = currentGroups.some(g => g.value === group.value);
    
    if (isSelected) {
      setSelectedGroup(currentGroups.filter(g => g.value !== group.value));
    } else {
      setSelectedGroup([...currentGroups, group]);
    }
  };

  const isAllGroupsSelected = () => {
    const actualGroups = availableGroups.filter(g => g.value !== 'all');
    const currentGroups = Array.isArray(selectedGroup) ? selectedGroup : [];
    return actualGroups.length > 0 && currentGroups.length === actualGroups.length;
  };

  const getSelectedGroupLabel = () => {
    if (!selectedGroup || selectedGroup.length === 0) return 'Select Groups...';
    // Check if "All Groups" option is selected
    if (selectedGroup.length === 1 && selectedGroup[0].value === 'all') return 'All Groups';
    if (isAllGroupsSelected()) return 'All Groups';
    return `${selectedGroup.length} Group${selectedGroup.length > 1 ? 's' : ''} Selected`;
  };

  // Positive Feedback SBU handlers
  const handleSelectAllPositiveFeedbackSBU = () => {
    if (isAllPositiveFeedbackSBUSelected()) { setPositiveFeedbackSBU([]); }
    else { setPositiveFeedbackSBU(availableUserSBUs.filter(s => s.value !== 'all')); }
  };
  const handleTogglePositiveFeedbackSBU = (sbu) => {
    const current = Array.isArray(positiveFeedbackSBU) ? positiveFeedbackSBU : [];
    const isSelected = current.some(s => s.value === sbu.value);
    setPositiveFeedbackSBU(isSelected ? current.filter(s => s.value !== sbu.value) : [...current, sbu]);
  };
  const isAllPositiveFeedbackSBUSelected = () => {
    const actual = availableUserSBUs.filter(s => s.value !== 'all');
    const current = Array.isArray(positiveFeedbackSBU) ? positiveFeedbackSBU : [];
    return actual.length > 0 && current.length === actual.length;
  };
  const getSelectedPositiveFeedbackSBULabel = () => {
    if (!positiveFeedbackSBU || positiveFeedbackSBU.length === 0) return 'Select SBU...';
    if (isAllPositiveFeedbackSBUSelected()) return 'All SBU';
    return `${positiveFeedbackSBU.length} SBU Selected`;
  };

  // Positive Feedback Track handlers
  const handleSelectAllPositiveFeedbackTrack = () => {
    setPositiveFeedbackTrack([]); setShowPositiveFeedbackTrackDropdown(false);
  };
  const handleTogglePositiveFeedbackTrack = (track) => {
    setPositiveFeedbackTrack([track]); setShowPositiveFeedbackTrackDropdown(false);
  };
  const isAllPositiveFeedbackTrackSelected = () => {
    return false;
  };
  const getSelectedPositiveFeedbackTrackLabel = () => {
    if (!positiveFeedbackTrack || positiveFeedbackTrack.length === 0) return 'Select Track...';
    return positiveFeedbackTrack[0].label;
  };

  // Negative Feedback SBU handlers
  const handleSelectAllNegativeFeedbackSBU = () => {
    if (isAllNegativeFeedbackSBUSelected()) { setNegativeFeedbackSBU([]); }
    else { setNegativeFeedbackSBU(availableUserSBUs.filter(s => s.value !== 'all')); }
  };
  const handleToggleNegativeFeedbackSBU = (sbu) => {
    const current = Array.isArray(negativeFeedbackSBU) ? negativeFeedbackSBU : [];
    const isSelected = current.some(s => s.value === sbu.value);
    setNegativeFeedbackSBU(isSelected ? current.filter(s => s.value !== sbu.value) : [...current, sbu]);
  };
  const isAllNegativeFeedbackSBUSelected = () => {
    const actual = availableUserSBUs.filter(s => s.value !== 'all');
    const current = Array.isArray(negativeFeedbackSBU) ? negativeFeedbackSBU : [];
    return actual.length > 0 && current.length === actual.length;
  };
  const getSelectedNegativeFeedbackSBULabel = () => {
    if (!negativeFeedbackSBU || negativeFeedbackSBU.length === 0) return 'Select SBU...';
    if (isAllNegativeFeedbackSBUSelected()) return 'All SBU';
    return `${negativeFeedbackSBU.length} SBU Selected`;
  };

  // Negative Feedback Track handlers
  const handleSelectAllNegativeFeedbackTrack = () => {
    setNegativeFeedbackTrack([]); setShowNegativeFeedbackTrackDropdown(false);
  };
  const handleToggleNegativeFeedbackTrack = (track) => {
    setNegativeFeedbackTrack([track]); setShowNegativeFeedbackTrackDropdown(false);
  };
  const isAllNegativeFeedbackTrackSelected = () => {
    return false;
  };
  const getSelectedNegativeFeedbackTrackLabel = () => {
    if (!negativeFeedbackTrack || negativeFeedbackTrack.length === 0) return 'Select Track...';
    return negativeFeedbackTrack[0].label;
  };

  // Track selection handlers
  const handleSelectAllTracks = () => {
    setSelectedTrack([]); setShowTrackDropdown(false);
  };

  const handleToggleTrack = (track) => {
    setSelectedTrack([track]); setShowTrackDropdown(false);
  };

  const isAllTracksSelected = () => {
    return false;
  };

  const getSelectedTrackLabel = () => {
    if (!selectedTrack || selectedTrack.length === 0) return 'Select Track...';
    return selectedTrack[0].label;
  };

  // SBU selection handlers
  const handleSelectAllSBU = () => {
    if (isAllSBUSelected()) {
      setSelectedSBU([]);
    } else {
      setSelectedSBU(availableSBUs.filter(s => s.value !== 'all'));
    }
  };

  const handleToggleSBU = (sbu) => {
    const currentSBUs = Array.isArray(selectedSBU) ? selectedSBU : [];
    const isSelected = currentSBUs.some(s => s.value === sbu.value);
    
    if (isSelected) {
      setSelectedSBU(currentSBUs.filter(s => s.value !== sbu.value));
    } else {
      setSelectedSBU([...currentSBUs, sbu]);
    }
  };

  const isAllSBUSelected = () => {
    const actualSBUs = availableSBUs.filter(s => s.value !== 'all');
    const currentSBUs = Array.isArray(selectedSBU) ? selectedSBU : [];
    return actualSBUs.length > 0 && currentSBUs.length === actualSBUs.length;
  };

  const getSelectedSBULabel = () => {
    if (!selectedSBU || selectedSBU.length === 0) return 'Select SBU...';
    if (isAllSBUSelected()) return 'All SBU';
    return `${selectedSBU.length} SBU Selected`;
  };

  // Login Count SBU handlers
  const handleSelectAllLoginCountSBU = () => {
    if (isAllLoginCountSBUSelected()) {
      setLoginCountSBU([]);
    } else {
      setLoginCountSBU(availableUserSBUs.filter(s => s.value !== 'all'));
    }
  };

  const handleToggleLoginCountSBU = (sbu) => {
    const current = Array.isArray(loginCountSBU) ? loginCountSBU : [];
    const isSelected = current.some(s => s.value === sbu.value);
    if (isSelected) {
      setLoginCountSBU(current.filter(s => s.value !== sbu.value));
    } else {
      setLoginCountSBU([...current, sbu]);
    }
  };

  const isAllLoginCountSBUSelected = () => {
    const actual = availableUserSBUs.filter(s => s.value !== 'all');
    const current = Array.isArray(loginCountSBU) ? loginCountSBU : [];
    return actual.length > 0 && current.length === actual.length;
  };

  const getSelectedLoginCountSBULabel = () => {
    if (!loginCountSBU || loginCountSBU.length === 0) return 'Select SBU...';
    if (isAllLoginCountSBUSelected()) return 'All SBU';
    return `${loginCountSBU.length} SBU Selected`;
  };

  // Login Count Track handlers
  const handleSelectAllLoginCountTrack = () => {
    setLoginCountTrack([]); setShowLoginCountTrackDropdown(false);
  };

  const handleToggleLoginCountTrack = (track) => {
    setLoginCountTrack([track]); setShowLoginCountTrackDropdown(false);
  };

  const isAllLoginCountTrackSelected = () => {
    return false;
  };

  const getSelectedLoginCountTrackLabel = () => {
    if (!loginCountTrack || loginCountTrack.length === 0) return 'Select Track...';
    return loginCountTrack[0].label;
  };

  // Login Trends SBU handlers
  const handleSelectAllLoginTrendsSBU = () => {
    if (isAllLoginTrendsSBUSelected()) {
      setLoginTrendsSBU([]);
    } else {
      setLoginTrendsSBU(availableUserSBUs.filter(s => s.value !== 'all'));
    }
  };

  const handleToggleLoginTrendsSBU = (sbu) => {
    const current = Array.isArray(loginTrendsSBU) ? loginTrendsSBU : [];
    const isSelected = current.some(s => s.value === sbu.value);
    if (isSelected) {
      setLoginTrendsSBU(current.filter(s => s.value !== sbu.value));
    } else {
      setLoginTrendsSBU([...current, sbu]);
    }
  };

  const isAllLoginTrendsSBUSelected = () => {
    const actual = availableUserSBUs.filter(s => s.value !== 'all');
    const current = Array.isArray(loginTrendsSBU) ? loginTrendsSBU : [];
    return actual.length > 0 && current.length === actual.length;
  };

  const getSelectedLoginTrendsSBULabel = () => {
    if (!loginTrendsSBU || loginTrendsSBU.length === 0) return 'Select SBU...';
    if (isAllLoginTrendsSBUSelected()) return 'All SBU';
    return `${loginTrendsSBU.length} SBU Selected`;
  };

  // Login Trends Track handlers
  const handleSelectAllLoginTrendsTrack = () => {
    setLoginTrendsTrack([]); setShowLoginTrendsTrackDropdown(false);
  };

  const handleToggleLoginTrendsTrack = (track) => {
    setLoginTrendsTrack([track]); setShowLoginTrendsTrackDropdown(false);
  };

  const isAllLoginTrendsTrackSelected = () => {
    return false;
  };

  const getSelectedLoginTrendsTrackLabel = () => {
    if (!loginTrendsTrack || loginTrendsTrack.length === 0) return 'Select Track...';
    return loginTrendsTrack[0].label;
  };

  // Ticket Trends SBU handlers
  const handleSelectAllTicketTrendsSBU = () => {
    if (isAllTicketTrendsSBUSelected()) {
      setTicketTrendsSBU([]);
    } else {
      setTicketTrendsSBU(availableUserSBUs.filter(s => s.value !== 'all'));
    }
  };

  const handleToggleTicketTrendsSBU = (sbu) => {
    const current = Array.isArray(ticketTrendsSBU) ? ticketTrendsSBU : [];
    const isSelected = current.some(s => s.value === sbu.value);
    if (isSelected) {
      setTicketTrendsSBU(current.filter(s => s.value !== sbu.value));
    } else {
      setTicketTrendsSBU([...current, sbu]);
    }
  };

  const isAllTicketTrendsSBUSelected = () => {
    const actual = availableUserSBUs.filter(s => s.value !== 'all');
    const current = Array.isArray(ticketTrendsSBU) ? ticketTrendsSBU : [];
    return actual.length > 0 && current.length === actual.length;
  };

  const getSelectedTicketTrendsSBULabel = () => {
    if (!ticketTrendsSBU || ticketTrendsSBU.length === 0) return 'Select SBU...';
    if (isAllTicketTrendsSBUSelected()) return 'All SBU';
    return `${ticketTrendsSBU.length} SBU Selected`;
  };

  // Ticket Trends Track handlers
  const handleSelectAllTicketTrendsTrack = () => {
    setTicketTrendsTrack([]); setShowTicketTrendsTrackDropdown(false);
  };

  const handleToggleTicketTrendsTrack = (track) => {
    setTicketTrendsTrack([track]); setShowTicketTrendsTrackDropdown(false);
  };

  const isAllTicketTrendsTrackSelected = () => {
    return false;
  };

  const getSelectedTicketTrendsTrackLabel = () => {
    if (!ticketTrendsTrack || ticketTrendsTrack.length === 0) return 'Select Track...';
    return ticketTrendsTrack[0].label;
  };

  // Feedback Trends SBU handlers
  const handleSelectAllFeedbackTrendsSBU = () => {
    if (isAllFeedbackTrendsSBUSelected()) { setFeedbackTrendsSBU([]); } else { setFeedbackTrendsSBU(availableUserSBUs.filter(s => s.value !== 'all')); }
  };
  const handleToggleFeedbackTrendsSBU = (sbu) => {
    const current = Array.isArray(feedbackTrendsSBU) ? feedbackTrendsSBU : [];
    const isSelected = current.some(s => s.value === sbu.value);
    if (isSelected) { setFeedbackTrendsSBU(current.filter(s => s.value !== sbu.value)); } else { setFeedbackTrendsSBU([...current, sbu]); }
  };
  const isAllFeedbackTrendsSBUSelected = () => {
    const actual = availableUserSBUs.filter(s => s.value !== 'all');
    const current = Array.isArray(feedbackTrendsSBU) ? feedbackTrendsSBU : [];
    return actual.length > 0 && current.length === actual.length;
  };
  const getSelectedFeedbackTrendsSBULabel = () => {
    if (!feedbackTrendsSBU || feedbackTrendsSBU.length === 0) return 'Select SBU...';
    if (isAllFeedbackTrendsSBUSelected()) return 'All SBU';
    return `${feedbackTrendsSBU.length} SBU Selected`;
  };
  // Feedback Trends Track handlers
  const handleSelectAllFeedbackTrendsTrack = () => {
    setFeedbackTrendsTrack([]); setShowFeedbackTrendsTrackDropdown(false);
  };
  const handleToggleFeedbackTrendsTrack = (track) => {
    setFeedbackTrendsTrack([track]); setShowFeedbackTrendsTrackDropdown(false);
  };
  const isAllFeedbackTrendsTrackSelected = () => {
    return false;
  };
  const getSelectedFeedbackTrendsTrackLabel = () => {
    if (!feedbackTrendsTrack || feedbackTrendsTrack.length === 0) return 'Select Track...';
    return feedbackTrendsTrack[0].label;
  };

  // Areas Trends SBU handlers
  const handleSelectAllAreasTrendsSBU = () => {
    if (isAllAreasTrendsSBUSelected()) { setAreasTrendsSBU([]); } else { setAreasTrendsSBU(availableUserSBUs.filter(s => s.value !== 'all')); }
  };
  const handleToggleAreasTrendsSBU = (sbu) => {
    const current = Array.isArray(areasTrendsSBU) ? areasTrendsSBU : [];
    const isSelected = current.some(s => s.value === sbu.value);
    if (isSelected) { setAreasTrendsSBU(current.filter(s => s.value !== sbu.value)); } else { setAreasTrendsSBU([...current, sbu]); }
  };
  const isAllAreasTrendsSBUSelected = () => {
    const actual = availableUserSBUs.filter(s => s.value !== 'all');
    const current = Array.isArray(areasTrendsSBU) ? areasTrendsSBU : [];
    return actual.length > 0 && current.length === actual.length;
  };
  const getSelectedAreasTrendsSBULabel = () => {
    if (!areasTrendsSBU || areasTrendsSBU.length === 0) return 'Select SBU...';
    if (isAllAreasTrendsSBUSelected()) return 'All SBU';
    return `${areasTrendsSBU.length} SBU Selected`;
  };
  // Areas Trends Track handlers
  const handleSelectAllAreasTrendsTrack = () => {
    setAreasTrendsTrack([]); setShowAreasTrendsTrackDropdown(false);
  };
  const handleToggleAreasTrendsTrack = (track) => {
    setAreasTrendsTrack([track]); setShowAreasTrendsTrackDropdown(false);
  };
  const isAllAreasTrendsTrackSelected = () => {
    return false;
  };
  const getSelectedAreasTrendsTrackLabel = () => {
    if (!areasTrendsTrack || areasTrendsTrack.length === 0) return 'Select Track...';
    return areasTrendsTrack[0].label;
  };

  // Screen Time Trends SBU handlers
  const handleSelectAllScreenTimeTrendsSBU = () => {
    if (isAllScreenTimeTrendsSBUSelected()) { setScreenTimeTrendsSBU([]); } else { setScreenTimeTrendsSBU(availableUserSBUs.filter(s => s.value !== 'all')); }
  };
  const handleToggleScreenTimeTrendsSBU = (sbu) => {
    const current = Array.isArray(screenTimeTrendsSBU) ? screenTimeTrendsSBU : [];
    const isSelected = current.some(s => s.value === sbu.value);
    if (isSelected) { setScreenTimeTrendsSBU(current.filter(s => s.value !== sbu.value)); } else { setScreenTimeTrendsSBU([...current, sbu]); }
  };
  const isAllScreenTimeTrendsSBUSelected = () => {
    const actual = availableUserSBUs.filter(s => s.value !== 'all');
    const current = Array.isArray(screenTimeTrendsSBU) ? screenTimeTrendsSBU : [];
    return actual.length > 0 && current.length === actual.length;
  };
  const getSelectedScreenTimeTrendsSBULabel = () => {
    if (!screenTimeTrendsSBU || screenTimeTrendsSBU.length === 0) return 'Select SBU...';
    if (isAllScreenTimeTrendsSBUSelected()) return 'All SBU';
    return `${screenTimeTrendsSBU.length} SBU Selected`;
  };
  // Screen Time Trends Track handlers
  const handleToggleScreenTimeTrendsTrack = (track) => {
    setScreenTimeTrendsTrack([track]); setShowScreenTimeTrendsTrackDropdown(false);
  };
  const getSelectedScreenTimeTrendsTrackLabel = () => {
    if (!screenTimeTrendsTrack || screenTimeTrendsTrack.length === 0) return 'Select Track...';
    return screenTimeTrendsTrack[0].label;
  };

  // Ticket Process SBU handlers
  const handleSelectAllTicketProcessSBU = () => {
    if (isAllTicketProcessSBUSelected()) {
      setTicketProcessSBU([]);
    } else {
      setTicketProcessSBU(availableUserSBUs.filter(s => s.value !== 'all'));
    }
  };

  const handleToggleTicketProcessSBU = (sbu) => {
    const current = Array.isArray(ticketProcessSBU) ? ticketProcessSBU : [];
    const isSelected = current.some(s => s.value === sbu.value);
    if (isSelected) {
      setTicketProcessSBU(current.filter(s => s.value !== sbu.value));
    } else {
      setTicketProcessSBU([...current, sbu]);
    }
  };

  const isAllTicketProcessSBUSelected = () => {
    const actual = availableUserSBUs.filter(s => s.value !== 'all');
    const current = Array.isArray(ticketProcessSBU) ? ticketProcessSBU : [];
    return actual.length > 0 && current.length === actual.length;
  };

  const getSelectedTicketProcessSBULabel = () => {
    if (!ticketProcessSBU || ticketProcessSBU.length === 0) return 'Select SBU...';
    if (isAllTicketProcessSBUSelected()) return 'All SBU';
    return `${ticketProcessSBU.length} SBU Selected`;
  };

  // Ticket Process Track handlers
  const handleSelectAllTicketProcessTrack = () => {
    setTicketProcessTrack([]); setShowTicketProcessTrackDropdown(false);
  };

  const handleToggleTicketProcessTrack = (track) => {
    setTicketProcessTrack([track]); setShowTicketProcessTrackDropdown(false);
  };

  const isAllTicketProcessTrackSelected = () => {
    return false;
  };

  const getSelectedTicketProcessTrackLabel = () => {
    if (!ticketProcessTrack || ticketProcessTrack.length === 0) return 'Select Track...';
    return ticketProcessTrack[0].label;
  };

  // Save filters to localStorage whenever they change
  useEffect(() => {
    const filtersToSave = {
      selectedGroupMonth,
      selectedGroupWeek,
      selectedGroupFromDate,
      selectedGroupToDate,
      selectedGroup,
      groupFilterType,
      selectedTeamMonth,
      selectedTeamWeek,
      selectedTeamFromDate,
      selectedTeamToDate,
      teamFilterType,
      selectedAllTeamsMonth,
      selectedAllTeamsWeek,
      selectedAllTeamsFromDate,
      selectedAllTeamsToDate,
      allTeamsFilterType,
      selectedPositiveFeedbackMonth,
      selectedPositiveFeedbackWeek,
      selectedPositiveFeedbackFromDate,
      selectedPositiveFeedbackToDate,
      positiveFeedbackSBU,
      positiveFeedbackTrack,
      positiveFeedbackFilterType,
      selectedNegativeFeedbackMonth,
      selectedNegativeFeedbackWeek,
      selectedNegativeFeedbackFromDate,
      selectedNegativeFeedbackToDate,
      negativeFeedbackSBU,
      negativeFeedbackTrack,
      negativeFeedbackFilterType,
      selectedSBU,
      loginCountFilterType,
      loginCountMonth,
      loginCountYear,
      loginCountFromDate,
      loginCountToDate,
      ticketProcessFilterType,
      ticketProcessMonth,
      ticketProcessYear,
      ticketProcessFromDate,
      ticketProcessToDate,
      globalFilterType,
      globalMonth,
      globalYear,
      globalWeek,
      globalFromDate,
      globalToDate,
      globalSBUValue,
      globalTrackValue
    };
    
    try {
      localStorage.setItem('screenTimeFilters', JSON.stringify(filtersToSave));
    } catch (err) {
      console.error('Error saving filters:', err);
    }
  }, [
    selectedGroupMonth, selectedGroupWeek, selectedGroupFromDate, selectedGroupToDate, selectedGroup, groupFilterType,
    selectedTeamMonth, selectedTeamWeek, selectedTeamFromDate, selectedTeamToDate, teamFilterType,
    selectedAllTeamsMonth, selectedAllTeamsWeek, selectedAllTeamsFromDate, selectedAllTeamsToDate, allTeamsFilterType,
    selectedPositiveFeedbackMonth, selectedPositiveFeedbackWeek, selectedPositiveFeedbackFromDate, selectedPositiveFeedbackToDate, positiveFeedbackSBU, positiveFeedbackTrack, positiveFeedbackFilterType,
    selectedNegativeFeedbackMonth, selectedNegativeFeedbackWeek, selectedNegativeFeedbackFromDate, selectedNegativeFeedbackToDate, negativeFeedbackSBU, negativeFeedbackTrack, negativeFeedbackFilterType,
    selectedSBU,
    loginCountFilterType, loginCountMonth, loginCountYear, loginCountFromDate, loginCountToDate,
    ticketProcessFilterType, ticketProcessMonth, ticketProcessYear, ticketProcessFromDate, ticketProcessToDate,
    globalFilterType, globalMonth, globalYear, globalWeek, globalFromDate, globalToDate, globalSBUValue, globalTrackValue
  ]);

  // Sync the single global filter to every dashboard filter state
  useEffect(() => {
    const selectedSBUOption = globalSBUValue === 'all'
      ? []
      : availableUserSBUs.filter(s => s.value === globalSBUValue);
    const selectedTrackOption = globalTrackValue === 'all'
      ? []
      : availableUserTracks.filter(t => t.value === globalTrackValue);

    setSelectedSBU(selectedSBUOption);
    setSelectedTrack(selectedTrackOption);

    setLoginCountSBU(selectedSBUOption);
    setTicketProcessSBU(selectedSBUOption);
    setPositiveFeedbackSBU(selectedSBUOption);
    setNegativeFeedbackSBU(selectedSBUOption);
    setLoginTrendsSBU(selectedSBUOption);
    setTicketTrendsSBU(selectedSBUOption);
    setFeedbackTrendsSBU(selectedSBUOption);
    setAreasTrendsSBU(selectedSBUOption);
    setScreenTimeTrendsSBU(selectedSBUOption);

    setLoginCountTrack(selectedTrackOption);
    setTicketProcessTrack(selectedTrackOption);
    setPositiveFeedbackTrack(selectedTrackOption);
    setNegativeFeedbackTrack(selectedTrackOption);
    setLoginTrendsTrack(selectedTrackOption);
    setTicketTrendsTrack(selectedTrackOption);
    setFeedbackTrendsTrack(selectedTrackOption);
    setAreasTrendsTrack(selectedTrackOption);
    setScreenTimeTrendsTrack(selectedTrackOption);

    const nonTrendType = globalFilterType === 'week' ? 'month' : globalFilterType;
    const trendsType = globalFilterType === 'date' ? 'month' : globalFilterType;

    setGroupFilterType(nonTrendType);
    setTeamFilterType(nonTrendType);
    setAllTeamsFilterType(nonTrendType);
    setPositiveFeedbackFilterType(nonTrendType);
    setNegativeFeedbackFilterType(nonTrendType);
    setLoginCountFilterType(nonTrendType);
    setTicketProcessFilterType(nonTrendType);

    setSelectedGroupMonth(globalMonth);
    setSelectedTeamMonth(globalMonth);
    setSelectedAllTeamsMonth(globalMonth);
    setSelectedPositiveFeedbackMonth(globalMonth);
    setSelectedNegativeFeedbackMonth(globalMonth);
    setLoginCountMonth(globalMonth);
    setTicketProcessMonth(globalMonth);

    setSelectedGroupYear(globalYear);
    setSelectedTeamYear(globalYear);
    setSelectedAllTeamsYear(globalYear);
    setSelectedPositiveFeedbackYear(globalYear);
    setSelectedNegativeFeedbackYear(globalYear);
    setLoginCountYear(globalYear);
    setTicketProcessYear(globalYear);

    setSelectedGroupWeek(globalWeek);
    setSelectedTeamWeek(globalWeek);
    setSelectedAllTeamsWeek(globalWeek);
    setSelectedPositiveFeedbackWeek(globalWeek);
    setSelectedNegativeFeedbackWeek(globalWeek);

    setSelectedGroupFromDate(globalFromDate);
    setSelectedGroupToDate(globalToDate);
    setSelectedTeamFromDate(globalFromDate);
    setSelectedTeamToDate(globalToDate);
    setSelectedAllTeamsFromDate(globalFromDate);
    setSelectedAllTeamsToDate(globalToDate);
    setSelectedPositiveFeedbackFromDate(globalFromDate);
    setSelectedPositiveFeedbackToDate(globalToDate);
    setSelectedNegativeFeedbackFromDate(globalFromDate);
    setSelectedNegativeFeedbackToDate(globalToDate);
    setLoginCountFromDate(globalFromDate);
    setLoginCountToDate(globalToDate);
    setTicketProcessFromDate(globalFromDate);
    setTicketProcessToDate(globalToDate);

    setLoginTrendsFilterType(trendsType);
    setTicketTrendsFilterType(trendsType);
    setFeedbackTrendsFilterType(trendsType);
    setAreasTrendsFilterType(trendsType);
    setScreenTimeTrendsFilterType(trendsType);

    setLoginTrendsMonth(globalMonth);
    setTicketTrendsMonth(globalMonth);
    setFeedbackTrendsMonth(globalMonth);
    setAreasTrendsMonth(globalMonth);
    setScreenTimeTrendsMonth(globalMonth);

    setLoginTrendsYear(globalYear);
    setTicketTrendsYear(globalYear);
    setFeedbackTrendsYear(globalYear);
    setAreasTrendsYear(globalYear);
    setScreenTimeTrendsYear(globalYear);

    setLoginTrendsWeek(globalWeek);
    setTicketTrendsWeek(globalWeek);
    setFeedbackTrendsWeek(globalWeek);
    setAreasTrendsWeek(globalWeek);
    setScreenTimeTrendsWeek(globalWeek);
  }, [globalFilterType, globalMonth, globalYear, globalWeek, globalFromDate, globalToDate, globalSBUValue, globalTrackValue, availableUserSBUs, availableUserTracks]);

  useEffect(() => {
    const validTrackValues = globalTrackOptions.map(option => option.value);
    if (!validTrackValues.includes(globalTrackValue)) {
      setGlobalTrackValue('all');
    }
  }, [globalSBUValue, globalTrackValue]);

  // Fetch AI feedback and session data on mount
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        // Fetch AI feedback data
        const feedbackResponse = await fetch('/api/feedback-all');
        if (feedbackResponse.ok) {
          const feedbackData = await feedbackResponse.json();
          if (feedbackData.success) {
            console.log('AI Feedback Data Loaded:', feedbackData.feedback.length, 'entries');
            const filteredFeedback = feedbackData.feedback.filter(f => !isExcludedEmail(f.userEmail));
            console.log('AI Feedback Data Loaded:', filteredFeedback.length, 'entries (excluded:', feedbackData.feedback.length - filteredFeedback.length, ')');
            console.log('Thumbs Up:', filteredFeedback.filter(f => f.feedbackType === 'thumbs_up').length);
            console.log('Thumbs Down:', filteredFeedback.filter(f => f.feedbackType === 'thumbs_down').length);
            setAiFeedbackData(filteredFeedback);
          }
        }
        
        // Fetch session data with timestamps for filtering
        const sessionResponse = await fetch('/api/sessions/all');
        if (sessionResponse.ok) {
          const sessionDataResult = await sessionResponse.json();
          if (sessionDataResult.success) {
            const filteredSessions = sessionDataResult.data.filter(s => !isExcludedEmail(s.userEmail));
            console.log('Session Data Loaded:', filteredSessions.length, 'sessions (excluded:', sessionDataResult.data.length - filteredSessions.length, ')');
            setSessionData(filteredSessions);
          }
        }

        // Fetch process button click data
        const processResponse = await fetch('/api/analytics/all-process-clicks');
        if (processResponse.ok) {
          const processResult = await processResponse.json();
          if (processResult.success) {
            const filteredClicks = processResult.clicks.filter(c => !isExcludedEmail(c.userEmail));
            console.log('Process Button Data Loaded:', filteredClicks.length, 'clicks (excluded:', processResult.clicks.length - filteredClicks.length, ')');
            setProcessButtonData(filteredClicks);
          }
        }
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      }
    };
    
    fetchAnalyticsData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (groupDropdownRef.current && !groupDropdownRef.current.contains(event.target)) {
        setShowGroupDropdown(false);
      }
      if (positiveFeedbackSBUDropdownRef.current && !positiveFeedbackSBUDropdownRef.current.contains(event.target)) {
        setShowPositiveFeedbackSBUDropdown(false);
      }
      if (positiveFeedbackTrackDropdownRef.current && !positiveFeedbackTrackDropdownRef.current.contains(event.target)) {
        setShowPositiveFeedbackTrackDropdown(false);
      }
      if (negativeFeedbackSBUDropdownRef.current && !negativeFeedbackSBUDropdownRef.current.contains(event.target)) {
        setShowNegativeFeedbackSBUDropdown(false);
      }
      if (negativeFeedbackTrackDropdownRef.current && !negativeFeedbackTrackDropdownRef.current.contains(event.target)) {
        setShowNegativeFeedbackTrackDropdown(false);
      }
      if (trackDropdownRef.current && !trackDropdownRef.current.contains(event.target)) {
        setShowTrackDropdown(false);
      }
      if (sbuDropdownRef.current && !sbuDropdownRef.current.contains(event.target)) {
        setShowSBUDropdown(false);
      }
      if (loginCountSBUDropdownRef.current && !loginCountSBUDropdownRef.current.contains(event.target)) {
        setShowLoginCountSBUDropdown(false);
      }
      if (loginCountTrackDropdownRef.current && !loginCountTrackDropdownRef.current.contains(event.target)) {
        setShowLoginCountTrackDropdown(false);
      }
      if (ticketProcessSBUDropdownRef.current && !ticketProcessSBUDropdownRef.current.contains(event.target)) {
        setShowTicketProcessSBUDropdown(false);
      }
      if (ticketProcessTrackDropdownRef.current && !ticketProcessTrackDropdownRef.current.contains(event.target)) {
        setShowTicketProcessTrackDropdown(false);
      }
      if (loginTrendsSBUDropdownRef.current && !loginTrendsSBUDropdownRef.current.contains(event.target)) {
        setShowLoginTrendsSBUDropdown(false);
      }
      if (loginTrendsTrackDropdownRef.current && !loginTrendsTrackDropdownRef.current.contains(event.target)) {
        setShowLoginTrendsTrackDropdown(false);
      }
      if (ticketTrendsSBUDropdownRef.current && !ticketTrendsSBUDropdownRef.current.contains(event.target)) {
        setShowTicketTrendsSBUDropdown(false);
      }
      if (ticketTrendsTrackDropdownRef.current && !ticketTrendsTrackDropdownRef.current.contains(event.target)) {
        setShowTicketTrendsTrackDropdown(false);
      }
      if (feedbackTrendsSBUDropdownRef.current && !feedbackTrendsSBUDropdownRef.current.contains(event.target)) {
        setShowFeedbackTrendsSBUDropdown(false);
      }
      if (feedbackTrendsTrackDropdownRef.current && !feedbackTrendsTrackDropdownRef.current.contains(event.target)) {
        setShowFeedbackTrendsTrackDropdown(false);
      }
      if (areasTrendsSBUDropdownRef.current && !areasTrendsSBUDropdownRef.current.contains(event.target)) {
        setShowAreasTrendsSBUDropdown(false);
      }
      if (areasTrendsTrackDropdownRef.current && !areasTrendsTrackDropdownRef.current.contains(event.target)) {
        setShowAreasTrendsTrackDropdown(false);
      }
      if (screenTimeTrendsSBUDropdownRef.current && !screenTimeTrendsSBUDropdownRef.current.contains(event.target)) {
        setShowScreenTimeTrendsSBUDropdown(false);
      }
      if (screenTimeTrendsTrackDropdownRef.current && !screenTimeTrendsTrackDropdownRef.current.contains(event.target)) {
        setShowScreenTimeTrendsTrackDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch data from backend
  const fetchData = async () => {
    const API_URL = "/api/tickets";
    const response = await fetch(API_URL, { method: "GET" });
    return response;
  };

  // Calculate screen time from ticket data (time from opened to resolved/closed)
  const calculateScreenTime = (ticket) => {
    if (!ticket.opened_at) return 0;
    
    const openedDate = new Date(ticket.opened_at);
    let endDate;
    
    // Use resolved_at if available, otherwise use updated_at or current time
    if (ticket.resolved_at) {
      endDate = new Date(ticket.resolved_at);
    } else if (ticket.updated_at) {
      endDate = new Date(ticket.updated_at);
    } else {
      endDate = new Date();
    }
    
    // Calculate hours difference
    const hoursDiff = (endDate - openedDate) / (1000 * 60 * 60);
    return Math.max(0, hoursDiff); // Ensure non-negative
  };

  // Filter tickets based on date/week/month
  const filterTicketsByDate = (tickets, filterType, month, week, fromDate, toDate) => {
    if (!tickets || tickets.length === 0) return [];
    
    return tickets.filter(ticket => {
      const ticketDate = new Date(ticket.opened_at || ticket.receivedOn);
      if (!ticket.opened_at && !ticket.receivedOn) return false;
      
      if (filterType === 'date') {
        // Filter by date range
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return ticketDate >= from && ticketDate <= to;
      } else if (filterType === 'week') {
        // Filter by week number
        const weekNum = parseInt(week);
        const startOfYear = new Date(ticketDate.getFullYear(), 0, 1);
        const daysSinceStart = Math.floor((ticketDate - startOfYear) / (1000 * 60 * 60 * 24));
        const ticketWeek = Math.ceil((daysSinceStart + startOfYear.getDay() + 1) / 7);
        return ticketWeek === weekNum;
      } else if (filterType === 'month') {
        // Filter by month
        const monthIndex = monthOptions.findIndex(m => m.value === month);
        return ticketDate.getMonth() === monthIndex;
      }
      return true;
    });
  };

  // Generate positive feedback data (Satisfied/Not Satisfied)
  const generatePositiveFeedbackData = (filterType, month, fromDate, toDate, year, sbus, tracks) => {
    // Filter AI feedback data by timestamp
    let filteredFeedback = aiFeedbackData;
    
    if (filterType === 'month' && month && year) {
      filteredFeedback = aiFeedbackData.filter(f => {
        if (!f.timestamp) return false;
        const feedbackDate = new Date(f.timestamp);
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return feedbackDate.getFullYear() === parseInt(year) && feedbackDate.getMonth() === monthNum;
      });
    } else if (filterType === 'date' && fromDate && toDate) {
      filteredFeedback = aiFeedbackData.filter(f => {
        if (!f.timestamp) return false;
        const feedbackDate = new Date(f.timestamp);
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return feedbackDate >= from && feedbackDate <= to;
      });
    }
    
    // Filter by SBU & Track (using userMapping by email)
    if (sbus && Array.isArray(sbus) && sbus.length > 0) {
      const sbuValues = sbus.map(s => s.value);
      const trackValues = (tracks && Array.isArray(tracks) && tracks.length > 0) ? tracks.map(t => t.value) : null;
      filteredFeedback = filteredFeedback.filter(f => {
        const mapping = getUserMapping(f.userEmail);
        if (!mapping) return false;
        if (!sbuValues.includes(mapping.sbu)) return false;
        if (trackValues && !trackValues.includes(mapping.track)) return false;
        return true;
      });
    }
    
    // Use filtered data from AI feedback table
    const satisfied = filteredFeedback.filter(f => f.feedbackType === 'thumbs_up').length;
    const notSatisfied = filteredFeedback.filter(f => f.feedbackType === 'thumbs_down').length;
    
    const feedbackData = [
      { name: 'Satisfied', count: satisfied, fill: 'url(#satisfiedGradient)' },
      { name: 'Not Satisfied', count: notSatisfied, fill: 'url(#notSatisfiedGradient)' }
    ];
    
    return feedbackData;
  };

  // Generate negative feedback data (Areas to be improved)
  const generateNegativeFeedbackData = (filterType, month, fromDate, toDate, year, sbus, tracks) => {
    // Filter AI feedback data by timestamp
    let filteredFeedback = aiFeedbackData;
    
    if (filterType === 'month' && month && year) {
      filteredFeedback = aiFeedbackData.filter(f => {
        if (!f.timestamp) return false;
        const feedbackDate = new Date(f.timestamp);
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return feedbackDate.getFullYear() === parseInt(year) && feedbackDate.getMonth() === monthNum;
      });
    } else if (filterType === 'date' && fromDate && toDate) {
      filteredFeedback = aiFeedbackData.filter(f => {
        if (!f.timestamp) return false;
        const feedbackDate = new Date(f.timestamp);
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return feedbackDate >= from && feedbackDate <= to;
      });
    }
    
    // Filter by SBU & Track (using userMapping by email)
    if (sbus && Array.isArray(sbus) && sbus.length > 0) {
      const sbuValues = sbus.map(s => s.value);
      const trackValues = (tracks && Array.isArray(tracks) && tracks.length > 0) ? tracks.map(t => t.value) : null;
      filteredFeedback = filteredFeedback.filter(f => {
        const mapping = getUserMapping(f.userEmail);
        if (!mapping) return false;
        if (!sbuValues.includes(mapping.sbu)) return false;
        if (trackValues && !trackValues.includes(mapping.track)) return false;
        return true;
      });
    }
    
    // Use filtered data from AI feedback table - group by reason
    const reasonCounts = {};
    
    filteredFeedback
      .filter(f => f.feedbackType === 'thumbs_down' && f.reason)
      .forEach(f => {
        const reason = f.reason;
        reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
      });
    
    // Convert to array format for chart
    const improvementAreas = Object.keys(reasonCounts).map(reason => ({
      name: reason,
      count: reasonCounts[reason]
    }));
    
    // Sort by count descending
    improvementAreas.sort((a, b) => b.count - a.count);
    
    return improvementAreas;
  };

  // Generate Feedback Trends data - Satisfied/Not Satisfied grouped by track over 4 time periods
  const generateFeedbackTrendsData = (filterType, month, year, week, sbus, tracks) => {
    if (!aiFeedbackData || aiFeedbackData.length === 0) return { data: [], periods: [] };
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const shortMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const selectedSBUValues = Array.isArray(sbus) && sbus.length > 0 ? sbus.map(s => s.value) : [];
    const selectedTrackValues = Array.isArray(tracks) && tracks.length > 0 ? tracks.map(t => t.value) : [];

    const periods = [];
    if (filterType === 'month') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      for (let i = 0; i <= 3; i++) {
        let m = selectedMonthIdx - i; let y = selectedYear;
        if (m < 0) { m += 12; y -= 1; }
        periods.push({ key: `${i}|${shortMonthNames[m]} ${y}`, filter: (f) => { if (!f.timestamp) return false; const d = new Date(f.timestamp); return d.getFullYear() === y && d.getMonth() === m; } });
      }
    } else if (filterType === 'week') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      const selectedWeekNum = parseInt(week);
      const firstOfMonth = new Date(selectedYear, selectedMonthIdx, 1);
      const selectedWeekStart = new Date(firstOfMonth);
      selectedWeekStart.setDate(1 + (selectedWeekNum - 1) * 7);
      for (let i = 0; i <= 3; i++) {
        const wStart = new Date(selectedWeekStart); wStart.setDate(wStart.getDate() - (i * 7));
        const wEnd = new Date(wStart); wEnd.setDate(wEnd.getDate() + 6); wEnd.setHours(23, 59, 59, 999);
        const startLabel = `${wStart.getDate()} ${shortMonthNames[wStart.getMonth()]}`;
        const endLabel = `${wEnd.getDate()} ${shortMonthNames[wEnd.getMonth()]}`;
        const wStartCopy = new Date(wStart); const wEndCopy = new Date(wEnd);
        periods.push({ key: `${i}|${startLabel}-${endLabel}`, filter: (f) => { if (!f.timestamp) return false; const d = new Date(f.timestamp); return d >= wStartCopy && d <= wEndCopy; } });
      }
    }
    const periodKeys = periods.map(p => p.key);

    // Filter by SBU/Track (using userMapping by email)
    let filtered = aiFeedbackData;
    if (selectedSBUValues.length > 0) {
      filtered = filtered.filter(f => { const m = getUserMapping(f.userEmail); return selectedSBUValues.includes(m.sbu); });
    }
    if (selectedTrackValues.length > 0) {
      filtered = filtered.filter(f => { const m = getUserMapping(f.userEmail); return selectedTrackValues.includes(m.track); });
    }

    const showUsers = selectedTrackValues.length > 0;
    if (!showUsers) {
      // Group by track: each track gets Satisfied/Not Satisfied per period
      // Build: [{name: 'Track', 'Jan 2026 👍': x, 'Jan 2026 👎': y, ...}]
      const trackTotals = {};
      filtered.forEach(f => {
        const track = getUserMapping(f.userEmail).track || 'Unknown';
        const inAny = periods.some(p => p.filter(f));
        if (inAny) trackTotals[track] = (trackTotals[track] || 0) + 1;
      });
      const top5 = Object.entries(trackTotals).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([t]) => t);
      // For feedback we show Satisfied & Not Satisfied per period
      const satisfiedKeys = periodKeys.map(k => `${k} 👍`);
      const notSatisfiedKeys = periodKeys.map(k => `${k} 👎`);
      const allKeys = [];
      periodKeys.forEach(k => { allKeys.push(`${k} 👍`); allKeys.push(`${k} 👎`); });
      const data = top5.map(track => {
        const row = { name: track };
        periods.forEach(period => {
          const trackFeedback = filtered.filter(f => { return getUserMapping(f.userEmail).track === track && period.filter(f); });
          row[`${period.key} 👍`] = trackFeedback.filter(f => f.feedbackType === 'thumbs_up').length;
          row[`${period.key} 👎`] = trackFeedback.filter(f => f.feedbackType === 'thumbs_down').length;
        });
        return row;
      });
      return { data, periods: allKeys };
    } else {
      const userTotals = {};
      filtered.forEach(f => {
        const userName = f.userName || f.userEmail?.split('@')[0] || 'Unknown';
        const inAny = periods.some(p => p.filter(f));
        if (inAny) userTotals[userName] = (userTotals[userName] || 0) + 1;
      });
      const top5 = Object.entries(userTotals).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([u]) => u);
      const allKeys = [];
      periodKeys.forEach(k => { allKeys.push(`${k} 👍`); allKeys.push(`${k} 👎`); });
      const data = top5.map(user => {
        const row = { name: user };
        periods.forEach(period => {
          const userFeedback = filtered.filter(f => { const uName = f.userName || f.userEmail?.split('@')[0] || 'Unknown'; return uName === user && period.filter(f); });
          row[`${period.key} 👍`] = userFeedback.filter(f => f.feedbackType === 'thumbs_up').length;
          row[`${period.key} 👎`] = userFeedback.filter(f => f.feedbackType === 'thumbs_down').length;
        });
        return row;
      });
      return { data, periods: allKeys };
    }
  };

  // Generate Areas to Improved Trends data - top reasons grouped by track over 4 time periods
  const generateAreasTrendsData = (filterType, month, year, week, sbus, tracks) => {
    if (!aiFeedbackData || aiFeedbackData.length === 0) return { data: [], periods: [] };
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const shortMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const selectedSBUValues = Array.isArray(sbus) && sbus.length > 0 ? sbus.map(s => s.value) : [];
    const selectedTrackValues = Array.isArray(tracks) && tracks.length > 0 ? tracks.map(t => t.value) : [];

    const periods = [];
    if (filterType === 'month') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      for (let i = 0; i <= 3; i++) {
        let m = selectedMonthIdx - i; let y = selectedYear;
        if (m < 0) { m += 12; y -= 1; }
        periods.push({ key: `${i}|${shortMonthNames[m]} ${y}`, filter: (f) => { if (!f.timestamp) return false; const d = new Date(f.timestamp); return d.getFullYear() === y && d.getMonth() === m; } });
      }
    } else if (filterType === 'week') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      const selectedWeekNum = parseInt(week);
      const firstOfMonth = new Date(selectedYear, selectedMonthIdx, 1);
      const selectedWeekStart = new Date(firstOfMonth);
      selectedWeekStart.setDate(1 + (selectedWeekNum - 1) * 7);
      for (let i = 0; i <= 3; i++) {
        const wStart = new Date(selectedWeekStart); wStart.setDate(wStart.getDate() - (i * 7));
        const wEnd = new Date(wStart); wEnd.setDate(wEnd.getDate() + 6); wEnd.setHours(23, 59, 59, 999);
        const startLabel = `${wStart.getDate()} ${shortMonthNames[wStart.getMonth()]}`;
        const endLabel = `${wEnd.getDate()} ${shortMonthNames[wEnd.getMonth()]}`;
        const wStartCopy = new Date(wStart); const wEndCopy = new Date(wEnd);
        periods.push({ key: `${i}|${startLabel}-${endLabel}`, filter: (f) => { if (!f.timestamp) return false; const d = new Date(f.timestamp); return d >= wStartCopy && d <= wEndCopy; } });
      }
    }
    const periodKeys = periods.map(p => p.key);

    // Filter: only thumbs_down with reason, then by SBU/Track (using userMapping by email)
    let filtered = aiFeedbackData.filter(f => f.feedbackType === 'thumbs_down' && f.reason);
    if (selectedSBUValues.length > 0) {
      filtered = filtered.filter(f => { const m = getUserMapping(f.userEmail); return selectedSBUValues.includes(m.sbu); });
    }
    if (selectedTrackValues.length > 0) {
      filtered = filtered.filter(f => { const m = getUserMapping(f.userEmail); return selectedTrackValues.includes(m.track); });
    }

    // Group by reason, count per period
    const reasonTotals = {};
    filtered.forEach(f => {
      const reason = f.reason;
      const inAny = periods.some(p => p.filter(f));
      if (inAny) reasonTotals[reason] = (reasonTotals[reason] || 0) + 1;
    });
    const top5Reasons = Object.entries(reasonTotals).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([r]) => r);

    const data = top5Reasons.map(reason => {
      const row = { name: reason };
      periods.forEach(period => {
        row[period.key] = filtered.filter(f => f.reason === reason && period.filter(f)).length;
      });
      return row;
    });
    return { data, periods: periodKeys };
  };

  const generateAssignmentGroupData = (activeFilterType, group, month, week, fromDate, toDate, year) => {
    // Use session data instead of ticket data
    if (!sessionData || sessionData.length === 0) return [];
    
    // Check if "All Groups" is selected or if it's an array of groups
    const currentGroups = Array.isArray(group) ? group : [];
    const isAllGroups = currentGroups.length === 0 || currentGroups.some(g => g.value === 'all');
    const selectedGroups = currentGroups.filter(g => g.value !== 'all');
    
    // Filter sessions by selected assignment groups
    let filteredSessions = sessionData;
    
    if (!isAllGroups && selectedGroups.length > 0) {
      filteredSessions = sessionData.filter(session => {
        const sessionGroup = session.assignmentGroup || '';
        return selectedGroups.some(g => sessionGroup === g.value);
      });
    }
    
    // Filter by date/month/year
    if (activeFilterType === 'month' && month && year) {
      filteredSessions = filteredSessions.filter(session => {
        const sessionDate = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
        if (!sessionDate) return false;
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return sessionDate.getFullYear() === parseInt(year) && sessionDate.getMonth() === monthNum;
      });
    } else if (activeFilterType === 'date' && fromDate && toDate) {
      filteredSessions = filteredSessions.filter(session => {
        const sessionDate = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
        if (!sessionDate) return false;
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return sessionDate >= from && sessionDate <= to;
      });
    }
    
    console.log('Filtered sessions for Assignment Group:', filteredSessions.length);
    
    // Group by user and sum their screen time
    const memberScreenTime = {};
    const userGroups = {};
    
    filteredSessions.forEach(session => {
      const userName = session.userName || session.userEmail?.split('@')[0] || 'Unknown';
      const assignmentGroup = session.assignmentGroup || 'Unassigned';
      const duration = session.totalDuration || 0;
      
      if (!memberScreenTime[userName]) {
        memberScreenTime[userName] = 0;
        userGroups[userName] = assignmentGroup;
      }
      
      memberScreenTime[userName] += duration;
    });
    
    // Convert to chart format
    const chartData = Object.keys(memberScreenTime).map(userName => ({
      name: userName,
      screenTime: parseFloat((memberScreenTime[userName] / 3600).toFixed(1)), // Convert seconds to hours
      team: userGroups[userName]
    }));
    
    // Sort by screen time descending and take top 10
    chartData.sort((a, b) => b.screenTime - a.screenTime);
    console.log('Assignment Group Chart Data:', chartData);
    return chartData.slice(0, 20); // Top 20 users
  };

  const generateTeamScreenTimeData = (activeFilterType, month, week, fromDate, toDate, year) => {
    // Use session data for real-time screen time instead of ticket data
    if (!sessionData || sessionData.length === 0) {
      return [];
    }
    
    // Filter sessions by date/month/year
    let filteredSessions = sessionData;
    
    if (activeFilterType === 'month' && month && year) {
      filteredSessions = sessionData.filter(session => {
        const sessionDate = new Date(session.startTime || session.timestamp);
        if (!session.startTime && !session.timestamp) return false;
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return sessionDate.getFullYear() === parseInt(year) && sessionDate.getMonth() === monthNum;
      });
    } else if (activeFilterType === 'date' && fromDate && toDate) {
      filteredSessions = sessionData.filter(session => {
        const sessionDate = new Date(session.startTime || session.timestamp);
        if (!session.startTime && !session.timestamp) return false;
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return sessionDate >= from && sessionDate <= to;
      });
    }
    
    // Group sessions by assignment group and calculate total screen time
    const teamScreenTime = {};
    
    filteredSessions.forEach(session => {
      const team = session.assignmentGroup || 'Unassigned';
      if (!team || team === 'Unassigned') return;
      
      // totalDuration is in seconds, convert to hours
      const screenTimeHours = (session.totalDuration || 0) / 3600;
      
      if (!teamScreenTime[team]) {
        teamScreenTime[team] = 0;
      }
      teamScreenTime[team] += screenTimeHours;
    });
    
    // Convert to array format for chart
    const result = Object.entries(teamScreenTime)
      .map(([name, screenTime]) => ({
        name: name,
        screenTime: parseFloat(screenTime.toFixed(1))
      }))
      .sort((a, b) => b.screenTime - a.screenTime)
      .slice(0, 20); // Top 20 teams
    
    return result;
  };

  // Generate all teams data showing assignment groups filtered by selected track
  const generateAllTeamsData = (activeFilterType, month, week, fromDate, toDate, tracks, sbus, year) => {
    // Use session data instead of ticket data for real-time user screen time
    if (!sessionData || sessionData.length === 0) {
      return [];
    }
    
    // Filter sessions by date/month/year
    let filteredSessions = sessionData;
    
    if (activeFilterType === 'month' && month && year) {
      filteredSessions = sessionData.filter(session => {
        const sessionDate = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
        if (!sessionDate) return false;
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return sessionDate.getFullYear() === parseInt(year) && sessionDate.getMonth() === monthNum;
      });
    } else if (activeFilterType === 'date' && fromDate && toDate) {
      filteredSessions = sessionData.filter(session => {
        const sessionDate = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
        if (!sessionDate) return false;
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return sessionDate >= from && sessionDate <= to;
      });
    }

    const selectedSBUValues = Array.isArray(sbus) && sbus.length > 0 ? sbus.map(s => s.value) : [];
    const selectedTrackValues = Array.isArray(tracks) && tracks.length > 0 ? tracks.map(t => t.value) : [];
    
    // Filter by SBU first (using userMapping by email)
    let sbuFilteredSessions = filteredSessions;
    if (selectedSBUValues.length > 0) {
      sbuFilteredSessions = filteredSessions.filter(session => {
        const mapping = getUserMapping(session.userEmail);
        return selectedSBUValues.includes(mapping.sbu);
      });
    }

    // If Track is NOT selected → aggregate by track (screen time)
    if (selectedTrackValues.length === 0) {
      const trackScreenTime = {};
      
      sbuFilteredSessions.forEach(session => {
        const mapping = getUserMapping(session.userEmail);
        const trackName = mapping.track || 'Unknown';
        const durationInSeconds = session.totalDuration || 0;
        const screenTimeHours = durationInSeconds / 3600;
        
        if (!trackScreenTime[trackName]) {
          trackScreenTime[trackName] = { screenTime: 0, sbu: mapping.sbu, isTrackLevel: true };
        }
        trackScreenTime[trackName].screenTime += screenTimeHours;
      });
      
      const result = Object.entries(trackScreenTime)
        .map(([name, data]) => ({
          name,
          screenTime: parseFloat(data.screenTime.toFixed(1)),
          sbu: data.sbu,
          track: name,
          isTrackLevel: true
        }))
        .sort((a, b) => b.screenTime - a.screenTime)
        .slice(0, 20);
      
      return result;
    }
    
    // If Track IS selected → show individual users within that track
    let finalFilteredSessions = sbuFilteredSessions;
    if (selectedTrackValues.length > 0) {
      finalFilteredSessions = sbuFilteredSessions.filter(session => {
        const mapping = getUserMapping(session.userEmail);
        return selectedTrackValues.includes(mapping.track);
      });
    }
    
    const userScreenTime = {};
    
    finalFilteredSessions.forEach(session => {
      const mapping = getUserMapping(session.userEmail);
      const userName = session.userName || session.userEmail?.split('@')[0] || 'Unknown';
      const durationInSeconds = session.totalDuration || 0;
      const screenTimeHours = durationInSeconds / 3600;
      
      if (!userScreenTime[userName]) {
        userScreenTime[userName] = { screenTime: 0, sbu: mapping.sbu, track: mapping.track, isTrackLevel: false };
      }
      userScreenTime[userName].screenTime += screenTimeHours;
    });
    
    const result = Object.entries(userScreenTime)
      .map(([name, data]) => ({
        name,
        screenTime: parseFloat(data.screenTime.toFixed(1)),
        sbu: data.sbu,
        track: data.track,
        isTrackLevel: false
      }))
      .sort((a, b) => b.screenTime - a.screenTime)
      .slice(0, 20);
    
    console.log('Screen Time by Track Data:', result);
    return result;
  };

  // Generate Screen Time Trends data - shows screen time (hours) for selected month + previous 3 months, or selected week + previous 3 weeks
  const generateScreenTimeTrendsData = (filterType, month, year, week, sbus, tracks) => {
    if (!sessionData || sessionData.length === 0) return { data: [], periods: [] };
    
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const shortMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const selectedSBUValues = Array.isArray(sbus) && sbus.length > 0 ? sbus.map(s => s.value) : [];
    const selectedTrackValues = Array.isArray(tracks) && tracks.length > 0 ? tracks.map(t => t.value) : [];

    // Build time period definitions (4 periods: selected + 3 previous)
    const periods = [];
    if (filterType === 'month') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      for (let i = 0; i <= 3; i++) {
        let m = selectedMonthIdx - i;
        let y = selectedYear;
        if (m < 0) { m += 12; y -= 1; }
        periods.push({
          key: `${i}|${shortMonthNames[m]} ${y}`,
          filter: (session) => {
            const d = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
            if (!d) return false;
            return d.getFullYear() === y && d.getMonth() === m;
          }
        });
      }
    } else if (filterType === 'week') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      const selectedWeekNum = parseInt(week);
      const firstOfMonth = new Date(selectedYear, selectedMonthIdx, 1);
      const selectedWeekStart = new Date(firstOfMonth);
      selectedWeekStart.setDate(1 + (selectedWeekNum - 1) * 7);
      
      for (let i = 0; i <= 3; i++) {
        const wStart = new Date(selectedWeekStart);
        wStart.setDate(wStart.getDate() - (i * 7));
        const wEnd = new Date(wStart);
        wEnd.setDate(wEnd.getDate() + 6);
        wEnd.setHours(23, 59, 59, 999);
        const startLabel = `${wStart.getDate()} ${shortMonthNames[wStart.getMonth()]}`;
        const endLabel = `${wEnd.getDate()} ${shortMonthNames[wEnd.getMonth()]}`;
        const wStartCopy = new Date(wStart);
        const wEndCopy = new Date(wEnd);
        periods.push({
          key: `${i}|${startLabel}-${endLabel}`,
          filter: (session) => {
            const d = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
            if (!d) return false;
            return d >= wStartCopy && d <= wEndCopy;
          }
        });
      }
    }

    const periodKeys = periods.map(p => p.key);

    // Determine if showing tracks or users
    const showUsers = selectedTrackValues.length > 0;

    // Filter sessions by SBU
    let sbuFiltered = sessionData;
    if (selectedSBUValues.length > 0) {
      sbuFiltered = sbuFiltered.filter(session => {
        return selectedSBUValues.includes(getUserMapping(session.userEmail).sbu);
      });
    }

    // Further filter by Track if selected
    let trackFiltered = sbuFiltered;
    if (selectedTrackValues.length > 0) {
      trackFiltered = sbuFiltered.filter(session => {
        return selectedTrackValues.includes(getUserMapping(session.userEmail).track);
      });
    }

    if (!showUsers) {
      // Group by track, sum screen time per period
      const trackTotals = {};
      sbuFiltered.forEach(session => {
        const track = getUserMapping(session.userEmail).track || 'Unknown';
        const inAnyPeriod = periods.some(p => p.filter(session));
        if (inAnyPeriod) {
          const durationHours = (session.totalDuration || 0) / 3600;
          trackTotals[track] = (trackTotals[track] || 0) + durationHours;
        }
      });

      // Get top 5 tracks
      const top5Tracks = Object.entries(trackTotals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([track]) => track);

      // Build data for each top track with period screen times
      const data = top5Tracks.map(track => {
        const row = { name: track };
        periods.forEach(period => {
          const total = sbuFiltered
            .filter(session => getUserMapping(session.userEmail).track === track && period.filter(session))
            .reduce((sum, session) => sum + ((session.totalDuration || 0) / 3600), 0);
          row[period.key] = parseFloat(total.toFixed(1));
        });
        return row;
      });
      return { data, periods: periodKeys };
    } else {
      // Group by user, sum screen time per period
      const userTotals = {};
      trackFiltered.forEach(session => {
        const userName = session.userName || session.userEmail?.split('@')[0] || 'Unknown';
        const inAnyPeriod = periods.some(p => p.filter(session));
        if (inAnyPeriod) {
          const durationHours = (session.totalDuration || 0) / 3600;
          userTotals[userName] = (userTotals[userName] || 0) + durationHours;
        }
      });

      const top5Users = Object.entries(userTotals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([user]) => user);

      const data = top5Users.map(user => {
        const row = { name: user };
        periods.forEach(period => {
          const total = trackFiltered
            .filter(session => {
              const uName = session.userName || session.userEmail?.split('@')[0] || 'Unknown';
              return uName === user && period.filter(session);
            })
            .reduce((sum, session) => sum + ((session.totalDuration || 0) / 3600), 0);
          row[period.key] = parseFloat(total.toFixed(1));
        });
        return row;
      });
      return { data, periods: periodKeys };
    }
  };

  // Generate Login Count data - counts how many times each user logged in
  // Generate Login Trends data - shows login counts for selected month + previous 3 months, or selected week + previous 3 weeks
  const generateLoginTrendsData = (filterType, month, year, week, sbus, tracks) => {
    if (!sessionData || sessionData.length === 0) return { data: [], periods: [] };
    
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const shortMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const selectedSBUValues = Array.isArray(sbus) && sbus.length > 0 ? sbus.map(s => s.value) : [];
    const selectedTrackValues = Array.isArray(tracks) && tracks.length > 0 ? tracks.map(t => t.value) : [];

    // Build time period definitions (4 periods: selected + 3 previous)
    const periods = [];
    if (filterType === 'month') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      for (let i = 0; i <= 3; i++) {
        let m = selectedMonthIdx - i;
        let y = selectedYear;
        if (m < 0) { m += 12; y -= 1; }
        periods.push({
          key: `${i}|${shortMonthNames[m]} ${y}`,
          filter: (session) => {
            const d = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
            if (!d) return false;
            return d.getFullYear() === y && d.getMonth() === m;
          }
        });
      }
    } else if (filterType === 'week') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      const selectedWeekNum = parseInt(week);
      const firstOfMonth = new Date(selectedYear, selectedMonthIdx, 1);
      const selectedWeekStart = new Date(firstOfMonth);
      selectedWeekStart.setDate(1 + (selectedWeekNum - 1) * 7);
      
      for (let i = 0; i <= 3; i++) {
        const wStart = new Date(selectedWeekStart);
        wStart.setDate(wStart.getDate() - (i * 7));
        const wEnd = new Date(wStart);
        wEnd.setDate(wEnd.getDate() + 6);
        wEnd.setHours(23, 59, 59, 999);
        const startLabel = `${wStart.getDate()} ${shortMonthNames[wStart.getMonth()]}`;
        const endLabel = `${wEnd.getDate()} ${shortMonthNames[wEnd.getMonth()]}`;
        const wStartCopy = new Date(wStart);
        const wEndCopy = new Date(wEnd);
        periods.push({
          key: `${i}|${startLabel}-${endLabel}`,
          filter: (session) => {
            const d = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
            if (!d) return false;
            return d >= wStartCopy && d <= wEndCopy;
          }
        });
      }
    }

    const periodKeys = periods.map(p => p.key);

    // Determine if showing tracks or users
    const showUsers = selectedTrackValues.length > 0;

    // Filter sessions by SBU (using userMapping by email)
    let sbuFiltered = sessionData;
    if (selectedSBUValues.length > 0) {
      sbuFiltered = sbuFiltered.filter(session => {
        return selectedSBUValues.includes(getUserMapping(session.userEmail).sbu);
      });
    }

    // Further filter by Track if selected
    let trackFiltered = sbuFiltered;
    if (selectedTrackValues.length > 0) {
      trackFiltered = sbuFiltered.filter(session => {
        return selectedTrackValues.includes(getUserMapping(session.userEmail).track);
      });
    }

    if (!showUsers) {
      // Group by track, count per period
      const trackTotals = {};
      // Count total logins across all periods per track (for ranking)
      sbuFiltered.forEach(session => {
        const track = getUserMapping(session.userEmail).track || 'Unknown';
        // Check if session falls in any of our periods
        const inAnyPeriod = periods.some(p => p.filter(session));
        if (inAnyPeriod) {
          trackTotals[track] = (trackTotals[track] || 0) + 1;
        }
      });

      // Get top 5 tracks
      const top5Tracks = Object.entries(trackTotals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([track]) => track);

      // Build data for each top track with period counts
      const data = top5Tracks.map(track => {
        const row = { name: track };
        periods.forEach(period => {
          const count = sbuFiltered.filter(session => {
            return getUserMapping(session.userEmail).track === track && period.filter(session);
          }).length;
          row[period.key] = count;
        });
        return row;
      });
      return { data, periods: periodKeys };
    } else {
      // Group by user, count per period
      const userTotals = {};
      trackFiltered.forEach(session => {
        const userName = session.userName || session.userEmail?.split('@')[0] || 'Unknown';
        const inAnyPeriod = periods.some(p => p.filter(session));
        if (inAnyPeriod) {
          userTotals[userName] = (userTotals[userName] || 0) + 1;
        }
      });

      const top5Users = Object.entries(userTotals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([user]) => user);

      const data = top5Users.map(user => {
        const row = { name: user };
        periods.forEach(period => {
          const count = trackFiltered.filter(session => {
            const uName = session.userName || session.userEmail?.split('@')[0] || 'Unknown';
            return uName === user && period.filter(session);
          }).length;
          row[period.key] = count;
        });
        return row;
      });
      return { data, periods: periodKeys };
    }
  };

  const generateLoginCountData = (filterType, month, fromDate, toDate, year, sbus, tracks) => {
    if (!sessionData || sessionData.length === 0) return [];
    
    let filteredSessions = sessionData;
    
    // Filter by date/month
    if (filterType === 'month' && month && year) {
      filteredSessions = filteredSessions.filter(session => {
        const sessionDate = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
        if (!sessionDate) return false;
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return sessionDate.getFullYear() === parseInt(year) && sessionDate.getMonth() === monthNum;
      });
    } else if (filterType === 'date' && fromDate && toDate) {
      filteredSessions = filteredSessions.filter(session => {
        const sessionDate = session.startTime ? new Date(session.startTime) : (session.timestamp ? new Date(session.timestamp) : null);
        if (!sessionDate) return false;
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return sessionDate >= from && sessionDate <= to;
      });
    }

    const selectedSBUValues = Array.isArray(sbus) && sbus.length > 0 ? sbus.map(s => s.value) : [];
    const selectedTrackValues = Array.isArray(tracks) && tracks.length > 0 ? tracks.map(t => t.value) : [];
    
    // Filter by SBU first (using userMapping by email)
    let sbuFilteredSessions = filteredSessions;
    if (selectedSBUValues.length > 0) {
      sbuFilteredSessions = filteredSessions.filter(session => {
        const mapping = getUserMapping(session.userEmail);
        return selectedSBUValues.includes(mapping.sbu);
      });
    }

    // If Track is NOT selected → aggregate by track
    if (selectedTrackValues.length === 0) {
      const trackLoginCounts = {};
      
      sbuFilteredSessions.forEach(session => {
        const mapping = getUserMapping(session.userEmail);
        const trackName = mapping.track || 'Unknown';
        
        if (!trackLoginCounts[trackName]) {
          trackLoginCounts[trackName] = { count: 0, sbu: mapping.sbu, isTrackLevel: true };
        }
        trackLoginCounts[trackName].count += 1;
      });
      
      const result = Object.entries(trackLoginCounts)
        .map(([name, data]) => ({
          name,
          loginCount: data.count,
          sbu: data.sbu,
          track: name,
          isTrackLevel: true
        }))
        .sort((a, b) => b.loginCount - a.loginCount)
        .slice(0, 20);
      
      return result;
    }
    
    // If Track IS selected → show individual users within that track
    let finalFilteredSessions = sbuFilteredSessions;
    if (selectedTrackValues.length > 0) {
      finalFilteredSessions = sbuFilteredSessions.filter(session => {
        const mapping = getUserMapping(session.userEmail);
        return selectedTrackValues.includes(mapping.track);
      });
    }
    
    const userLoginCounts = {};
    
    finalFilteredSessions.forEach(session => {
      const mapping = getUserMapping(session.userEmail);
      const userName = session.userName || session.userEmail?.split('@')[0] || 'Unknown';
      
      if (!userLoginCounts[userName]) {
        userLoginCounts[userName] = { count: 0, group: session.assignmentGroup || '', sbu: mapping.sbu, track: mapping.track };
      }
      userLoginCounts[userName].count += 1;
    });
    
    const result = Object.entries(userLoginCounts)
      .map(([name, data]) => ({
        name,
        loginCount: data.count,
        group: data.group,
        sbu: data.sbu,
        track: data.track,
        isTrackLevel: false
      }))
      .sort((a, b) => b.loginCount - a.loginCount)
      .slice(0, 20);
    
    return result;
  };

  // Generate Ticket Process data - shows who clicked Process button (AI recommendation)
  // Uses the user's HOME track (from sessionData/login) not the ticket's assignment group
  // SBU selected + no Track → aggregate by track; Track selected → show individual users
  const generateTicketProcessData = (filterType, month, fromDate, toDate, year, sbus, tracks) => {
    if (!processButtonData || processButtonData.length === 0) return [];
    
    // Build user→track mapping from sessionData using userEmail (lowercase) as key
    // This ensures consistent matching between sessions and process button clicks
    const userTrackMap = {};
    if (sessionData && sessionData.length > 0) {
      sessionData.forEach(session => {
        const userEmail = (session.userEmail || '').toLowerCase().trim();
        const group = session.assignmentGroup || '';
        if (!userEmail) return;
        
        if (!userTrackMap[userEmail]) {
          userTrackMap[userEmail] = { groups: {}, userName: session.userName || userEmail.split('@')[0] };
        }
        // Count occurrences of each assignment group per user to find their primary group
        if (group) {
          userTrackMap[userEmail].groups[group] = (userTrackMap[userEmail].groups[group] || 0) + 1;
        }
      });
    }
    
    // Resolve each user's SBU/Track directly from userMapping by email
    const getUserHomeMapping = (userEmail) => {
      const mapping = getUserMapping(userEmail);
      const emailKey = (userEmail || '').toLowerCase().trim();
      const userData = userTrackMap[emailKey];
      const userName = userData ? userData.userName : (userEmail?.split('@')[0] || 'Unknown');
      const primaryGroup = userData && Object.keys(userData.groups).length > 0 
        ? Object.entries(userData.groups).sort((a, b) => b[1] - a[1])[0][0] 
        : '';
      return { sbu: mapping.sbu, track: mapping.track, group: primaryGroup, userName };
    };
    
    let filteredClicks = processButtonData;
    
    // Filter by date/month
    if (filterType === 'month' && month && year) {
      filteredClicks = filteredClicks.filter(click => {
        const clickDate = click.clickedAt ? new Date(click.clickedAt) : (click.timestamp ? new Date(click.timestamp) : null);
        if (!clickDate) return false;
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        const monthNum = monthNames.indexOf(month);
        return clickDate.getFullYear() === parseInt(year) && clickDate.getMonth() === monthNum;
      });
    } else if (filterType === 'date' && fromDate && toDate) {
      filteredClicks = filteredClicks.filter(click => {
        const clickDate = click.clickedAt ? new Date(click.clickedAt) : (click.timestamp ? new Date(click.timestamp) : null);
        if (!clickDate) return false;
        const from = new Date(fromDate);
        const to = new Date(toDate);
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        return clickDate >= from && clickDate <= to;
      });
    }

    const selectedSBUValues = Array.isArray(sbus) && sbus.length > 0 ? sbus.map(s => s.value) : [];
    const selectedTrackValues = Array.isArray(tracks) && tracks.length > 0 ? tracks.map(t => t.value) : [];
    
    // Filter by SBU using user's HOME track (not ticket's assignment group)
    let sbuFilteredClicks = filteredClicks;
    if (selectedSBUValues.length > 0) {
      sbuFilteredClicks = filteredClicks.filter(click => {
        const homeMapping = getUserHomeMapping(click.userEmail);
        return selectedSBUValues.includes(homeMapping.sbu);
      });
    }

    // If Track is NOT selected → aggregate by user's home track
    if (selectedTrackValues.length === 0) {
      const trackCounts = {};
      
      sbuFilteredClicks.forEach(click => {
        const homeMapping = getUserHomeMapping(click.userEmail);
        const trackName = homeMapping.track || 'Unknown';
        
        if (!trackCounts[trackName]) {
          trackCounts[trackName] = { count: 0, sbu: homeMapping.sbu, isTrackLevel: true };
        }
        trackCounts[trackName].count += 1;
      });
      
      const result = Object.entries(trackCounts)
        .map(([name, data]) => ({
          name,
          processCount: data.count,
          sbu: data.sbu,
          track: name,
          isTrackLevel: true
        }))
        .sort((a, b) => b.processCount - a.processCount)
        .slice(0, 20);
      
      return result;
    }
    
    // If Track IS selected → show individual users within that track (user's home track)
    let finalFilteredClicks = sbuFilteredClicks;
    if (selectedTrackValues.length > 0) {
      finalFilteredClicks = sbuFilteredClicks.filter(click => {
        const homeMapping = getUserHomeMapping(click.userEmail);
        return selectedTrackValues.includes(homeMapping.track);
      });
    }
    
    const userCounts = {};
    
    finalFilteredClicks.forEach(click => {
      const homeMapping = getUserHomeMapping(click.userEmail);
      const displayName = homeMapping.userName || click.userName || click.userEmail?.split('@')[0] || 'Unknown';
      
      if (!userCounts[displayName]) {
        userCounts[displayName] = { count: 0, group: homeMapping.group, sbu: homeMapping.sbu, track: homeMapping.track };
      }
      userCounts[displayName].count += 1;
    });
    
    const result = Object.entries(userCounts)
      .map(([name, data]) => ({
        name,
        processCount: data.count,
        group: data.group,
        sbu: data.sbu,
        track: data.track,
        isTrackLevel: false
      }))
      .sort((a, b) => b.processCount - a.processCount)
      .slice(0, 20);
    
    return result;
  };

  // Generate Ticket Process Trends data - grouped bars per track/user over 4 time periods
  const generateTicketTrendsData = (filterType, month, year, week, sbus, tracks) => {
    if (!processButtonData || processButtonData.length === 0) return { data: [], periods: [] };
    
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const shortMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const selectedSBUValues = Array.isArray(sbus) && sbus.length > 0 ? sbus.map(s => s.value) : [];
    const selectedTrackValues = Array.isArray(tracks) && tracks.length > 0 ? tracks.map(t => t.value) : [];

    // Build user→track mapping from sessionData
    const userTrackMap = {};
    if (sessionData && sessionData.length > 0) {
      sessionData.forEach(session => {
        const userEmail = (session.userEmail || '').toLowerCase().trim();
        const group = session.assignmentGroup || '';
        if (!userEmail) return;
        if (!userTrackMap[userEmail]) {
          userTrackMap[userEmail] = { groups: {}, userName: session.userName || userEmail.split('@')[0] };
        }
        if (group) {
          userTrackMap[userEmail].groups[group] = (userTrackMap[userEmail].groups[group] || 0) + 1;
        }
      });
    }
    const getUserHomeMapping = (userEmail) => {
      const mapping = getUserMapping(userEmail);
      const emailKey = (userEmail || '').toLowerCase().trim();
      const userData = userTrackMap[emailKey];
      const userName = userData ? userData.userName : (userEmail?.split('@')[0] || 'Unknown');
      const primaryGroup = userData && Object.keys(userData.groups).length > 0 
        ? Object.entries(userData.groups).sort((a, b) => b[1] - a[1])[0][0] 
        : '';
      return { sbu: mapping.sbu, track: mapping.track, group: primaryGroup, userName };
    };

    // Build time period definitions (4 periods: selected + 3 previous)
    const periods = [];
    if (filterType === 'month') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      for (let i = 0; i <= 3; i++) {
        let m = selectedMonthIdx - i;
        let y = selectedYear;
        if (m < 0) { m += 12; y -= 1; }
        periods.push({
          key: `${i}|${shortMonthNames[m]} ${y}`,
          filter: (click) => {
            const d = click.clickedAt ? new Date(click.clickedAt) : (click.timestamp ? new Date(click.timestamp) : null);
            if (!d) return false;
            return d.getFullYear() === y && d.getMonth() === m;
          }
        });
      }
    } else if (filterType === 'week') {
      const selectedMonthIdx = monthNames.indexOf(month);
      const selectedYear = parseInt(year);
      const selectedWeekNum = parseInt(week);
      const firstOfMonth = new Date(selectedYear, selectedMonthIdx, 1);
      const selectedWeekStart = new Date(firstOfMonth);
      selectedWeekStart.setDate(1 + (selectedWeekNum - 1) * 7);
      for (let i = 0; i <= 3; i++) {
        const wStart = new Date(selectedWeekStart);
        wStart.setDate(wStart.getDate() - (i * 7));
        const wEnd = new Date(wStart);
        wEnd.setDate(wEnd.getDate() + 6);
        wEnd.setHours(23, 59, 59, 999);
        const startLabel = `${wStart.getDate()} ${shortMonthNames[wStart.getMonth()]}`;
        const endLabel = `${wEnd.getDate()} ${shortMonthNames[wEnd.getMonth()]}`;
        const wStartCopy = new Date(wStart);
        const wEndCopy = new Date(wEnd);
        periods.push({
          key: `${i}|${startLabel}-${endLabel}`,
          filter: (click) => {
            const d = click.clickedAt ? new Date(click.clickedAt) : (click.timestamp ? new Date(click.timestamp) : null);
            if (!d) return false;
            return d >= wStartCopy && d <= wEndCopy;
          }
        });
      }
    }

    const periodKeys = periods.map(p => p.key);
    const showUsers = selectedTrackValues.length > 0;

    // Filter by SBU using user's home track
    let sbuFiltered = processButtonData;
    if (selectedSBUValues.length > 0) {
      sbuFiltered = sbuFiltered.filter(click => {
        const homeMapping = getUserHomeMapping(click.userEmail);
        return selectedSBUValues.includes(homeMapping.sbu);
      });
    }

    let trackFiltered = sbuFiltered;
    if (selectedTrackValues.length > 0) {
      trackFiltered = sbuFiltered.filter(click => {
        const homeMapping = getUserHomeMapping(click.userEmail);
        return selectedTrackValues.includes(homeMapping.track);
      });
    }

    if (!showUsers) {
      const trackTotals = {};
      sbuFiltered.forEach(click => {
        const homeMapping = getUserHomeMapping(click.userEmail);
        const track = homeMapping.track || 'Unknown';
        const inAnyPeriod = periods.some(p => p.filter(click));
        if (inAnyPeriod) {
          trackTotals[track] = (trackTotals[track] || 0) + 1;
        }
      });

      const top5Tracks = Object.entries(trackTotals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([track]) => track);

      const data = top5Tracks.map(track => {
        const row = { name: track };
        periods.forEach(period => {
          const count = sbuFiltered.filter(click => {
            const homeMapping = getUserHomeMapping(click.userEmail);
            return homeMapping.track === track && period.filter(click);
          }).length;
          row[period.key] = count;
        });
        return row;
      });
      return { data, periods: periodKeys };
    } else {
      const userTotals = {};
      trackFiltered.forEach(click => {
        const homeMapping = getUserHomeMapping(click.userEmail);
        const userName = homeMapping.userName || click.userName || click.userEmail?.split('@')[0] || 'Unknown';
        const inAnyPeriod = periods.some(p => p.filter(click));
        if (inAnyPeriod) {
          userTotals[userName] = (userTotals[userName] || 0) + 1;
        }
      });

      const top5Users = Object.entries(userTotals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([user]) => user);

      const data = top5Users.map(user => {
        const row = { name: user };
        periods.forEach(period => {
          const count = trackFiltered.filter(click => {
            const homeMapping = getUserHomeMapping(click.userEmail);
            const uName = homeMapping.userName || click.userName || click.userEmail?.split('@')[0] || 'Unknown';
            return uName === user && period.filter(click);
          }).length;
          row[period.key] = count;
        });
        return row;
      });
      return { data, periods: periodKeys };
    }
  };

  // Load Track Mapping - Demo Data
  useEffect(() => {
    const loadTrackMapping = async () => {
      await loadUserMappingFromCSV();
      // Load SBUs from groupMapping (for Screen Time dashboards)
      const sbus = getAllSBUs();
      const sbuOptions = [
        { value: 'all', label: 'All SBUs' },
        ...sbus.map(sbu => ({ value: sbu, label: sbu }))
      ];
      setAvailableSBUs(sbuOptions);
      // Start with all SBUs selected (excluding the 'all' option itself)
      setSelectedSBU(sbus.map(sbu => ({ value: sbu, label: sbu })));
      
      // Load Tracks from groupMapping (for Screen Time dashboards)
      const tracks = getAllTracks();
      const trackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...tracks.map(track => ({ value: track, label: track }))
      ];
      setAvailableTracks(trackOptions);
      // Start with no tracks selected (user must pick SBU first, then Track)
      setSelectedTrack([]);

      // Load SBUs & Tracks from userMapping (for Login Count, Ticket Process, Feedback, and Trends dashboards)
      const userSbus = getAllUserSBUs();
      const userSbuOptions = [
        { value: 'all', label: 'All SBUs' },
        ...userSbus.map(sbu => ({ value: sbu, label: sbu }))
      ];
      setAvailableUserSBUs(userSbuOptions);

      const userTracks = getAllUserTracks();
      const userTrackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...userTracks.map(track => ({ value: track, label: track }))
      ];
      setAvailableUserTracks(userTrackOptions);

      // Initialize Login Count SBU/Track selections (from userMapping)
      setLoginCountSBU(userSbus.map(sbu => ({ value: sbu, label: sbu })));
      setLoginCountTrack([]);
      setLoginCountAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...userTracks.map(track => ({ value: track, label: track }))
      ]);
      // Initialize Ticket Process SBU/Track selections (from userMapping)
      setTicketProcessSBU(userSbus.map(sbu => ({ value: sbu, label: sbu })));
      setTicketProcessTrack([]);
      setTicketProcessAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...userTracks.map(track => ({ value: track, label: track }))
      ]);
      // Initialize Positive Feedback SBU/Track selections (from userMapping)
      setPositiveFeedbackSBU(userSbus.map(sbu => ({ value: sbu, label: sbu })));
      setPositiveFeedbackTrack([]);
      setPositiveFeedbackAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...userTracks.map(track => ({ value: track, label: track }))
      ]);
      // Initialize Negative Feedback SBU/Track selections (from userMapping)
      setNegativeFeedbackSBU(userSbus.map(sbu => ({ value: sbu, label: sbu })));
      setNegativeFeedbackTrack([]);
      setNegativeFeedbackAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...userTracks.map(track => ({ value: track, label: track }))
      ]);
      // Initialize Trends dashboards SBU to all selected (from userMapping)
      setLoginTrendsSBU(userSbus.map(sbu => ({ value: sbu, label: sbu })));
      setTicketTrendsSBU(userSbus.map(sbu => ({ value: sbu, label: sbu })));
      setFeedbackTrendsSBU(userSbus.map(sbu => ({ value: sbu, label: sbu })));
      setAreasTrendsSBU(userSbus.map(sbu => ({ value: sbu, label: sbu })));
      setScreenTimeTrendsSBU(userSbus.map(sbu => ({ value: sbu, label: sbu })));
      // Initialize Trends dashboards available tracks (from userMapping)
      const trendTrackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...userTracks.map(track => ({ value: track, label: track }))
      ];
      setLoginTrendsAvailableTracks(trendTrackOptions);
      setTicketTrendsAvailableTracks(trendTrackOptions);
      setFeedbackTrendsAvailableTracks(trendTrackOptions);
      setAreasTrendsAvailableTracks(trendTrackOptions);
      setScreenTimeTrendsAvailableTracks(trendTrackOptions);
    };
    
    loadTrackMapping();
  }, []);

  // Load tickets from backend on mount
  useEffect(() => {
    const loadTicketsData = async () => {
      // Try to load from cache first (faster)
      const cachedData = localStorage.getItem('agentAssistTicketsCache');
      if (cachedData) {
        try {
          const cache = JSON.parse(cachedData);
          const tickets = cache.tickets || cache;
          // Ensure tickets is an array and has data
          if (Array.isArray(tickets) && tickets.length > 0) {
            setTicketsData(tickets);
            console.log('Loaded', tickets.length, 'tickets from cache');
            return; // Use cache, don't fetch
          }
        } catch (cacheErr) {
          console.error('Error loading cached data:', cacheErr);
        }
      }
      
      // No cache or cache is empty, fetch from API
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetchData();
        const result = await response.json();
        const tickets = result.result || [];
        
        setTicketsData(tickets);
        
        // Cache the data
        const cacheData = {
          tickets: tickets,
          timestamp: Date.now()
        };
        localStorage.setItem('agentAssistTicketsCache', JSON.stringify(cacheData));
      } catch (err) {
        console.error('Error fetching tickets:', err);
        setError('Failed to load ticket data');
      } finally {
        setLoading(false);
      }
    };
    
    loadTicketsData();
  }, []);
  
  // Function to manually refresh data
  const handleSyncData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetchData();
      const result = await response.json();
      const tickets = result.result || [];
      
      setTicketsData(tickets);
      
      // Cache the data
      const cacheData = {
        tickets: tickets,
        timestamp: Date.now()
      };
      localStorage.setItem('agentAssistTicketsCache', JSON.stringify(cacheData));
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to sync data');
    } finally {
      setLoading(false);
    }
  };

  // Initialize available groups from tickets data
  useEffect(() => {
    if (!ticketsData || ticketsData.length === 0) return;
    
    // Extract unique assignment groups from tickets
    const uniqueGroups = [...new Set(ticketsData
      .map(ticket => ticket.assignment_group || ticket.assignmentGroup)
      .filter(group => group !== null && group !== undefined && group !== '')
    )];
    
    // Create group options with "All Groups" as first option
    const groupOptions = [
      { value: 'all', label: 'All Groups' },
      ...uniqueGroups.map(group => ({
        value: group,
        label: group
      })).sort((a, b) => a.label.localeCompare(b.label))
    ];
    
    setAvailableGroups(groupOptions);
    
    // Set default for Assignment Group filter to "All Groups" (only once when groups are first loaded)
    if (groupOptions.length > 0 && availableGroups.length === 0) {
      // First time loading groups, set default to all individual groups (not the "All Groups" option)
      setSelectedGroup(groupOptions.filter(g => g.value !== 'all')); // All individual groups
      
      // Feedback SBU/Track is initialized in loadTrackMapping, no group init needed here
    }
  }, [ticketsData, availableGroups.length]);

  // Filter available tracks based on selected SBU
  useEffect(() => {
    if (availableSBUs.length === 0) return;
    
    // If SBU is selected, filter tracks to only show those in selected SBUs
    if (selectedSBU && selectedSBU.length > 0) {
      const selectedSBUValues = selectedSBU.map(s => s.value);
      // Get all tracks from the mapping that belong to selected SBUs
      const tracksInSelectedSBUs = new Set();
      Object.values(GROUP_MAPPING || {}).forEach(mapping => {
        if (selectedSBUValues.includes(mapping.sbu)) {
          tracksInSelectedSBUs.add(mapping.track);
        }
      });
      // Include 'Others' track when 'Others' SBU is selected
      if (selectedSBUValues.includes('Others')) {
        tracksInSelectedSBUs.add('Others');
      }
      
      // Filter available tracks
      const allTracks = getAllTracks();
      const filteredTracks = allTracks.filter(track => tracksInSelectedSBUs.has(track));
      const trackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...filteredTracks.map(track => ({ value: track, label: track }))
      ];
      setAvailableTracks(trackOptions);
      
      // Clear selected tracks if they're not in the filtered list
      if (selectedTrack && selectedTrack.length > 0) {
        const validTracks = selectedTrack.filter(t => 
          filteredTracks.includes(t.value) || t.value === 'all'
        );
        if (validTracks.length !== selectedTrack.length) {
          setSelectedTrack(validTracks);
        }
      }
    } else {
      // No SBU selected, show all tracks
      const tracks = getAllTracks();
      const trackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...tracks.map(track => ({ value: track, label: track }))
      ];
      setAvailableTracks(trackOptions);
    }
  }, [selectedSBU, availableSBUs.length]);

  // Filter Ticket Process available tracks based on Ticket Process SBU selection (userMapping)
  useEffect(() => {
    if (availableUserSBUs.length === 0) return;
    
    if (ticketProcessSBU && ticketProcessSBU.length > 0) {
      const selectedSBUValues = ticketProcessSBU.map(s => s.value);
      const tracksInSBUs = new Set();
      Object.values(USERMAIL_MAPPING || {}).forEach(mapping => {
        if (selectedSBUValues.includes(mapping.sbu)) {
          tracksInSBUs.add(mapping.track);
        }
      });
      if (selectedSBUValues.includes('Others')) {
        tracksInSBUs.add('Others');
      }
      
      const filteredTracks = getAllUserTracks().filter(track => tracksInSBUs.has(track));
      const trackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...filteredTracks.map(track => ({ value: track, label: track }))
      ];
      setTicketProcessAvailableTracks(trackOptions);
      
      if (ticketProcessTrack && ticketProcessTrack.length > 0) {
        const validTracks = ticketProcessTrack.filter(t => filteredTracks.includes(t.value));
        if (validTracks.length !== ticketProcessTrack.length) {
          setTicketProcessTrack(validTracks);
        }
      }
    } else {
      const tracks = getAllUserTracks();
      setTicketProcessAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...tracks.map(track => ({ value: track, label: track }))
      ]);
    }
  }, [ticketProcessSBU, availableUserSBUs.length]);

  // Filter Login Count available tracks based on Login Count SBU selection (userMapping)
  useEffect(() => {
    if (availableUserSBUs.length === 0) return;
    
    if (loginCountSBU && loginCountSBU.length > 0) {
      const selectedSBUValues = loginCountSBU.map(s => s.value);
      const tracksInSBUs = new Set();
      Object.values(USERMAIL_MAPPING || {}).forEach(mapping => {
        if (selectedSBUValues.includes(mapping.sbu)) {
          tracksInSBUs.add(mapping.track);
        }
      });
      if (selectedSBUValues.includes('Others')) {
        tracksInSBUs.add('Others');
      }
      
      const filteredTracks = getAllUserTracks().filter(track => tracksInSBUs.has(track));
      const trackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...filteredTracks.map(track => ({ value: track, label: track }))
      ];
      setLoginCountAvailableTracks(trackOptions);
      
      if (loginCountTrack && loginCountTrack.length > 0) {
        const validTracks = loginCountTrack.filter(t => filteredTracks.includes(t.value));
        if (validTracks.length !== loginCountTrack.length) {
          setLoginCountTrack(validTracks);
        }
      }
    } else {
      const tracks = getAllUserTracks();
      setLoginCountAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...tracks.map(track => ({ value: track, label: track }))
      ]);
    }
  }, [loginCountSBU, availableUserSBUs.length]);

  // Filter Login Trends available tracks based on Login Trends SBU selection (userMapping)
  useEffect(() => {
    if (availableUserSBUs.length === 0) return;
    
    if (loginTrendsSBU && loginTrendsSBU.length > 0) {
      const selectedSBUValues = loginTrendsSBU.map(s => s.value);
      const tracksInSBUs = new Set();
      Object.values(USERMAIL_MAPPING || {}).forEach(mapping => {
        if (selectedSBUValues.includes(mapping.sbu)) {
          tracksInSBUs.add(mapping.track);
        }
      });
      if (selectedSBUValues.includes('Others')) {
        tracksInSBUs.add('Others');
      }
      
      const filteredTracks = getAllUserTracks().filter(track => tracksInSBUs.has(track));
      const trackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...filteredTracks.map(track => ({ value: track, label: track }))
      ];
      setLoginTrendsAvailableTracks(trackOptions);
      
      if (loginTrendsTrack && loginTrendsTrack.length > 0) {
        const validTracks = loginTrendsTrack.filter(t => filteredTracks.includes(t.value));
        if (validTracks.length !== loginTrendsTrack.length) {
          setLoginTrendsTrack(validTracks);
        }
      }
    } else {
      const tracks = getAllUserTracks();
      setLoginTrendsAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...tracks.map(track => ({ value: track, label: track }))
      ]);
    }
  }, [loginTrendsSBU, availableUserSBUs.length]);

  // Filter Ticket Trends available tracks based on Ticket Trends SBU selection (userMapping)
  useEffect(() => {
    if (availableUserSBUs.length === 0) return;
    
    if (ticketTrendsSBU && ticketTrendsSBU.length > 0) {
      const selectedSBUValues = ticketTrendsSBU.map(s => s.value);
      const tracksInSBUs = new Set();
      Object.values(USERMAIL_MAPPING || {}).forEach(mapping => {
        if (selectedSBUValues.includes(mapping.sbu)) {
          tracksInSBUs.add(mapping.track);
        }
      });
      if (selectedSBUValues.includes('Others')) {
        tracksInSBUs.add('Others');
      }
      
      const filteredTracks = getAllUserTracks().filter(track => tracksInSBUs.has(track));
      const trackOptions = [
        { value: 'all', label: 'All Tracks' },
        ...filteredTracks.map(track => ({ value: track, label: track }))
      ];
      setTicketTrendsAvailableTracks(trackOptions);
      
      if (ticketTrendsTrack && ticketTrendsTrack.length > 0) {
        const validTracks = ticketTrendsTrack.filter(t => filteredTracks.includes(t.value));
        if (validTracks.length !== ticketTrendsTrack.length) {
          setTicketTrendsTrack(validTracks);
        }
      }
    } else {
      const tracks = getAllUserTracks();
      setTicketTrendsAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...tracks.map(track => ({ value: track, label: track }))
      ]);
    }
  }, [ticketTrendsSBU, availableUserSBUs.length]);

  // Cascading SBU→Track filter for Feedback Trends (userMapping)
  useEffect(() => {
    if (availableUserSBUs.length === 0) return;
    if (feedbackTrendsSBU && feedbackTrendsSBU.length > 0) {
      const selectedSBUValues = feedbackTrendsSBU.map(s => s.value);
      const tracksInSBUs = new Set();
      Object.values(USERMAIL_MAPPING || {}).forEach(mapping => { if (selectedSBUValues.includes(mapping.sbu)) tracksInSBUs.add(mapping.track); });
      if (selectedSBUValues.includes('Others')) tracksInSBUs.add('Others');
      const filteredTracks = getAllUserTracks().filter(track => tracksInSBUs.has(track));
      setFeedbackTrendsAvailableTracks([{ value: 'all', label: 'All Tracks' }, ...filteredTracks.map(track => ({ value: track, label: track }))]);
      if (feedbackTrendsTrack && feedbackTrendsTrack.length > 0) {
        const validTracks = feedbackTrendsTrack.filter(t => filteredTracks.includes(t.value));
        if (validTracks.length !== feedbackTrendsTrack.length) setFeedbackTrendsTrack(validTracks);
      }
    } else {
      const tracks = getAllUserTracks();
      setFeedbackTrendsAvailableTracks([{ value: 'all', label: 'All Tracks' }, ...tracks.map(track => ({ value: track, label: track }))]);
    }
  }, [feedbackTrendsSBU, availableUserSBUs.length]);

  // Cascading SBU→Track filter for Areas Trends (userMapping)
  useEffect(() => {
    if (availableUserSBUs.length === 0) return;
    if (areasTrendsSBU && areasTrendsSBU.length > 0) {
      const selectedSBUValues = areasTrendsSBU.map(s => s.value);
      const tracksInSBUs = new Set();
      Object.values(USERMAIL_MAPPING || {}).forEach(mapping => { if (selectedSBUValues.includes(mapping.sbu)) tracksInSBUs.add(mapping.track); });
      if (selectedSBUValues.includes('Others')) tracksInSBUs.add('Others');
      const filteredTracks = getAllUserTracks().filter(track => tracksInSBUs.has(track));
      setAreasTrendsAvailableTracks([{ value: 'all', label: 'All Tracks' }, ...filteredTracks.map(track => ({ value: track, label: track }))]);
      if (areasTrendsTrack && areasTrendsTrack.length > 0) {
        const validTracks = areasTrendsTrack.filter(t => filteredTracks.includes(t.value));
        if (validTracks.length !== areasTrendsTrack.length) setAreasTrendsTrack(validTracks);
      }
    } else {
      const tracks = getAllUserTracks();
      setAreasTrendsAvailableTracks([{ value: 'all', label: 'All Tracks' }, ...tracks.map(track => ({ value: track, label: track }))]);
    }
  }, [areasTrendsSBU, availableUserSBUs.length]);

  // Cascading SBU→Track filter for Screen Time Trends (userMapping)
  useEffect(() => {
    if (availableUserSBUs.length === 0) return;
    if (screenTimeTrendsSBU && screenTimeTrendsSBU.length > 0) {
      const selectedSBUValues = screenTimeTrendsSBU.map(s => s.value);
      const tracksInSBUs = new Set();
      Object.values(USERMAIL_MAPPING || {}).forEach(mapping => { if (selectedSBUValues.includes(mapping.sbu)) tracksInSBUs.add(mapping.track); });
      if (selectedSBUValues.includes('Others')) tracksInSBUs.add('Others');
      const filteredTracks = getAllUserTracks().filter(track => tracksInSBUs.has(track));
      setScreenTimeTrendsAvailableTracks([{ value: 'all', label: 'All Tracks' }, ...filteredTracks.map(track => ({ value: track, label: track }))]);
      if (screenTimeTrendsTrack && screenTimeTrendsTrack.length > 0) {
        const validTracks = screenTimeTrendsTrack.filter(t => filteredTracks.includes(t.value));
        if (validTracks.length !== screenTimeTrendsTrack.length) setScreenTimeTrendsTrack(validTracks);
      }
    } else {
      const tracks = getAllUserTracks();
      setScreenTimeTrendsAvailableTracks([{ value: 'all', label: 'All Tracks' }, ...tracks.map(track => ({ value: track, label: track }))]);
    }
  }, [screenTimeTrendsSBU, availableUserSBUs.length]);

  // Cascading SBU→Track filter for Positive Feedback (userMapping)
  useEffect(() => {
    if (positiveFeedbackSBU && positiveFeedbackSBU.length > 0 && availableUserSBUs.length > 0) {
      const selectedSbuValues = positiveFeedbackSBU.map(s => s.value);
      const allTracks = getAllUserTracks();
      const filtered = allTracks.filter(track => {
        if (track === 'Others' && selectedSbuValues.includes('Others')) return true;
        const entries = Object.entries(USERMAIL_MAPPING);
        return entries.some(([, mapping]) => mapping.track === track && selectedSbuValues.includes(mapping.sbu));
      });
      setPositiveFeedbackAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...filtered.map(t => ({ value: t, label: t }))
      ]);
      // Remove any currently selected tracks that are no longer available
      if (positiveFeedbackTrack && positiveFeedbackTrack.length > 0) {
        const valid = positiveFeedbackTrack.filter(t => filtered.includes(t.value));
        if (valid.length !== positiveFeedbackTrack.length) {
          setPositiveFeedbackTrack(valid);
        }
      }
    }
  }, [positiveFeedbackSBU, availableUserSBUs.length]);

  // Cascading SBU→Track filter for Negative Feedback (userMapping)
  useEffect(() => {
    if (negativeFeedbackSBU && negativeFeedbackSBU.length > 0 && availableUserSBUs.length > 0) {
      const selectedSbuValues = negativeFeedbackSBU.map(s => s.value);
      const allTracks = getAllUserTracks();
      const filtered = allTracks.filter(track => {
        if (track === 'Others' && selectedSbuValues.includes('Others')) return true;
        const entries = Object.entries(USERMAIL_MAPPING);
        return entries.some(([, mapping]) => mapping.track === track && selectedSbuValues.includes(mapping.sbu));
      });
      setNegativeFeedbackAvailableTracks([
        { value: 'all', label: 'All Tracks' },
        ...filtered.map(t => ({ value: t, label: t }))
      ]);
      // Remove any currently selected tracks that are no longer available
      if (negativeFeedbackTrack && negativeFeedbackTrack.length > 0) {
        const valid = negativeFeedbackTrack.filter(t => filtered.includes(t.value));
        if (valid.length !== negativeFeedbackTrack.length) {
          setNegativeFeedbackTrack(valid);
        }
      }
    }
  }, [negativeFeedbackSBU, availableUserSBUs.length]);

  // Update data when filters or AI feedback data changes
  useEffect(() => {
    if (aiFeedbackData.length > 0) {
      setPositiveFeedbackData(generatePositiveFeedbackData(positiveFeedbackFilterType, selectedPositiveFeedbackMonth, selectedPositiveFeedbackFromDate, selectedPositiveFeedbackToDate, selectedPositiveFeedbackYear, positiveFeedbackSBU, positiveFeedbackTrack));
    }
  }, [aiFeedbackData, selectedPositiveFeedbackMonth, selectedPositiveFeedbackFromDate, selectedPositiveFeedbackToDate, selectedPositiveFeedbackYear, positiveFeedbackSBU, positiveFeedbackTrack, positiveFeedbackFilterType]);

  useEffect(() => {
    if (aiFeedbackData.length > 0) {
      setNegativeFeedbackData(generateNegativeFeedbackData(negativeFeedbackFilterType, selectedNegativeFeedbackMonth, selectedNegativeFeedbackFromDate, selectedNegativeFeedbackToDate, selectedNegativeFeedbackYear, negativeFeedbackSBU, negativeFeedbackTrack));
    }
  }, [aiFeedbackData, selectedNegativeFeedbackMonth, selectedNegativeFeedbackFromDate, selectedNegativeFeedbackToDate, selectedNegativeFeedbackYear, negativeFeedbackSBU, negativeFeedbackTrack, negativeFeedbackFilterType]);

  useEffect(() => {
    if (sessionData.length > 0) {
      setAssignmentGroupData(generateAssignmentGroupData(groupFilterType, selectedGroup, selectedGroupMonth, selectedGroupWeek, selectedGroupFromDate, selectedGroupToDate, selectedGroupYear));
    }
  }, [sessionData, selectedGroup, selectedGroupMonth, selectedGroupWeek, selectedGroupFromDate, selectedGroupToDate, selectedGroupYear, groupFilterType]);

  useEffect(() => {
    if (sessionData.length > 0) {
      setTeamScreenTimeData(generateTeamScreenTimeData(teamFilterType, selectedTeamMonth, selectedTeamWeek, selectedTeamFromDate, selectedTeamToDate, selectedTeamYear));
    }
  }, [selectedTeamMonth, selectedTeamWeek, selectedTeamFromDate, selectedTeamToDate, selectedTeamYear, teamFilterType, sessionData]);

  useEffect(() => {
    if (sessionData.length > 0) {
      setAllTeamsData(generateAllTeamsData(allTeamsFilterType, selectedAllTeamsMonth, selectedAllTeamsWeek, selectedAllTeamsFromDate, selectedAllTeamsToDate, selectedTrack, selectedSBU, selectedAllTeamsYear));
    }
  }, [selectedAllTeamsMonth, selectedAllTeamsWeek, selectedAllTeamsFromDate, selectedAllTeamsToDate, selectedAllTeamsYear, allTeamsFilterType, sessionData, selectedTrack, selectedSBU, availableTracks, availableSBUs]);

  // Login Count data update
  useEffect(() => {
    if (sessionData.length > 0) {
      setLoginCountData(generateLoginCountData(loginCountFilterType, loginCountMonth, loginCountFromDate, loginCountToDate, loginCountYear, loginCountSBU, loginCountTrack));
    }
  }, [sessionData, loginCountFilterType, loginCountMonth, loginCountYear, loginCountFromDate, loginCountToDate, loginCountSBU, loginCountTrack]);

  // Login Trends data update
  useEffect(() => {
    if (sessionData.length > 0) {
      const result = generateLoginTrendsData(loginTrendsFilterType, loginTrendsMonth, loginTrendsYear, loginTrendsWeek, loginTrendsSBU, loginTrendsTrack);
      setLoginTrendsData(result.data || []);
      setLoginTrendsPeriods(result.periods || []);
    }
  }, [sessionData, loginTrendsFilterType, loginTrendsMonth, loginTrendsYear, loginTrendsWeek, loginTrendsSBU, loginTrendsTrack]);

  // Ticket Process data update (depends on sessionData for user home track lookup)
  useEffect(() => {
    if (processButtonData.length > 0) {
      setTicketProcessData(generateTicketProcessData(ticketProcessFilterType, ticketProcessMonth, ticketProcessFromDate, ticketProcessToDate, ticketProcessYear, ticketProcessSBU, ticketProcessTrack));
    }
  }, [processButtonData, sessionData, ticketProcessFilterType, ticketProcessMonth, ticketProcessYear, ticketProcessFromDate, ticketProcessToDate, ticketProcessSBU, ticketProcessTrack]);

  // Ticket Trends data update
  useEffect(() => {
    if (processButtonData.length > 0) {
      const result = generateTicketTrendsData(ticketTrendsFilterType, ticketTrendsMonth, ticketTrendsYear, ticketTrendsWeek, ticketTrendsSBU, ticketTrendsTrack);
      setTicketTrendsData(result.data || []);
      setTicketTrendsPeriods(result.periods || []);
    }
  }, [processButtonData, sessionData, ticketTrendsFilterType, ticketTrendsMonth, ticketTrendsYear, ticketTrendsWeek, ticketTrendsSBU, ticketTrendsTrack]);

  // Feedback Trends data update
  useEffect(() => {
    if (aiFeedbackData.length > 0) {
      const result = generateFeedbackTrendsData(feedbackTrendsFilterType, feedbackTrendsMonth, feedbackTrendsYear, feedbackTrendsWeek, feedbackTrendsSBU, feedbackTrendsTrack);
      setFeedbackTrendsData(result.data || []);
      setFeedbackTrendsPeriods(result.periods || []);
    }
  }, [aiFeedbackData, feedbackTrendsFilterType, feedbackTrendsMonth, feedbackTrendsYear, feedbackTrendsWeek, feedbackTrendsSBU, feedbackTrendsTrack]);

  // Areas Trends data update
  useEffect(() => {
    if (aiFeedbackData.length > 0) {
      const result = generateAreasTrendsData(areasTrendsFilterType, areasTrendsMonth, areasTrendsYear, areasTrendsWeek, areasTrendsSBU, areasTrendsTrack);
      setAreasTrendsData(result.data || []);
      setAreasTrendsPeriods(result.periods || []);
    }
  }, [aiFeedbackData, areasTrendsFilterType, areasTrendsMonth, areasTrendsYear, areasTrendsWeek, areasTrendsSBU, areasTrendsTrack]);

  // Screen Time Trends data update
  useEffect(() => {
    if (sessionData.length > 0) {
      const result = generateScreenTimeTrendsData(screenTimeTrendsFilterType, screenTimeTrendsMonth, screenTimeTrendsYear, screenTimeTrendsWeek, screenTimeTrendsSBU, screenTimeTrendsTrack);
      setScreenTimeTrendsData(result.data || []);
      setScreenTimeTrendsPeriods(result.periods || []);
    }
  }, [sessionData, screenTimeTrendsFilterType, screenTimeTrendsMonth, screenTimeTrendsYear, screenTimeTrendsWeek, screenTimeTrendsSBU, screenTimeTrendsTrack]);

  const formatScreenTime = (hours) => {
    if (hours >= 100) {
      return `${hours.toFixed(0)} hrs`;
    }
    return `${hours.toFixed(1)} hrs`;
  };

  // Function to download data as Excel (CSV format)
  const downloadExcel = (data, filename) => {
    if (!data || data.length === 0) {
      alert('No data to download');
      return;
    }

    // Escape a CSV value: wrap in quotes, double any internal quotes
    // For date/time values use ="value" formula so Excel treats them as text
    const escapeCsv = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val);
      // Detect date (MM/DD/YYYY) or time (HH:MM:SS AM/PM) patterns
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(str) || /^\d{2}:\d{2}:\d{2}\s[AP]M$/.test(str)) {
        return `"=""${str}"""`;
      }
      return `"${str.replace(/"/g, '""')}"`;
    };

    const headers = Object.keys(data[0]);
    const headerRow = headers.map(h => escapeCsv(h)).join(',');
    const rows = data.map(row =>
      headers.map(h => escapeCsv(row[h])).join(',')
    );
    const csv = [headerRow, ...rows].join('\r\n');

    // UTF-8 BOM so Excel opens with correct encoding
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper to split an ISO timestamp into separate Date and Time strings
  // Prefixes with space so Excel treats them as plain text, not date/time
  const formatDateTimeParts = (isoString) => {
    if (!isoString) return { date: '', time: '' };
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return { date: '', time: '' };
    const pad = (n) => String(n).padStart(2, '0');
    const date = `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
    let hours = d.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const time = `${pad(hours)}:${pad(d.getMinutes())}:${pad(d.getSeconds())} ${ampm}`;
    return { date, time };
  };

  // Helper: filter feedback data by date/month and SBU/Track (reused across feedback downloads)
  const getFilteredFeedback = (filterType, month, fromDate, toDate, year, sbus, tracks) => {
    let filtered = aiFeedbackData;
    if (filterType === 'month' && month && year) {
      filtered = filtered.filter(f => {
        if (!f.timestamp) return false;
        const fd = new Date(f.timestamp);
        const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        return fd.getFullYear() === parseInt(year) && fd.getMonth() === monthNames.indexOf(month);
      });
    } else if (filterType === 'date' && fromDate && toDate) {
      filtered = filtered.filter(f => {
        if (!f.timestamp) return false;
        const fd = new Date(f.timestamp);
        const from = new Date(fromDate); from.setHours(0,0,0,0);
        const to = new Date(toDate); to.setHours(23,59,59,999);
        return fd >= from && fd <= to;
      });
    }
    if (sbus && Array.isArray(sbus) && sbus.length > 0) {
      const sbuValues = sbus.map(s => s.value);
      const trackValues = (tracks && Array.isArray(tracks) && tracks.length > 0) ? tracks.map(t => t.value) : null;
      filtered = filtered.filter(f => {
        const mapping = getUserMapping(f.userEmail);
        if (!mapping) return false;
        if (!sbuValues.includes(mapping.sbu)) return false;
        if (trackValues && !trackValues.includes(mapping.track)) return false;
        return true;
      });
    }
    return filtered;
  };

  // Helper: filter session data by date/month and SBU/Track (reused across session downloads)
  const getFilteredSessions = (filterType, month, fromDate, toDate, year, sbus, tracks) => {
    let filtered = sessionData || [];
    if (filterType === 'month' && month && year) {
      filtered = filtered.filter(s => {
        const sd = s.startTime ? new Date(s.startTime) : (s.timestamp ? new Date(s.timestamp) : null);
        if (!sd) return false;
        const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        return sd.getFullYear() === parseInt(year) && sd.getMonth() === monthNames.indexOf(month);
      });
    } else if (filterType === 'date' && fromDate && toDate) {
      filtered = filtered.filter(s => {
        const sd = s.startTime ? new Date(s.startTime) : (s.timestamp ? new Date(s.timestamp) : null);
        if (!sd) return false;
        const from = new Date(fromDate); from.setHours(0,0,0,0);
        const to = new Date(toDate); to.setHours(23,59,59,999);
        return sd >= from && sd <= to;
      });
    }
    if (sbus && Array.isArray(sbus) && sbus.length > 0) {
      const sbuValues = sbus.map(s => s.value);
      filtered = filtered.filter(s => sbuValues.includes(getUserMapping(s.userEmail).sbu));
    }
    if (tracks && Array.isArray(tracks) && tracks.length > 0) {
      const trackValues = tracks.map(t => t.value);
      filtered = filtered.filter(s => trackValues.includes(getUserMapping(s.userEmail).track));
    }
    return filtered;
  };

  // Helper: filter process button clicks by date/month and SBU/Track
  const getFilteredProcessClicks = (filterType, month, fromDate, toDate, year, sbus, tracks) => {
    let filtered = processButtonData || [];
    if (filterType === 'month' && month && year) {
      filtered = filtered.filter(c => {
        const cd = c.clickedAt ? new Date(c.clickedAt) : (c.timestamp ? new Date(c.timestamp) : null);
        if (!cd) return false;
        const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        return cd.getFullYear() === parseInt(year) && cd.getMonth() === monthNames.indexOf(month);
      });
    } else if (filterType === 'date' && fromDate && toDate) {
      filtered = filtered.filter(c => {
        const cd = c.clickedAt ? new Date(c.clickedAt) : (c.timestamp ? new Date(c.timestamp) : null);
        if (!cd) return false;
        const from = new Date(fromDate); from.setHours(0,0,0,0);
        const to = new Date(toDate); to.setHours(23,59,59,999);
        return cd >= from && cd <= to;
      });
    }
    if (sbus && Array.isArray(sbus) && sbus.length > 0) {
      const sbuValues = sbus.map(s => s.value);
      filtered = filtered.filter(c => sbuValues.includes(getUserMapping(c.userEmail).sbu));
    }
    if (tracks && Array.isArray(tracks) && tracks.length > 0) {
      const trackValues = tracks.map(t => t.value);
      filtered = filtered.filter(c => trackValues.includes(getUserMapping(c.userEmail).track));
    }
    return filtered;
  };

  const handleDownloadPositiveFeedback = () => {
    const filtered = getFilteredFeedback(positiveFeedbackFilterType, selectedPositiveFeedbackMonth, selectedPositiveFeedbackFromDate, selectedPositiveFeedbackToDate, selectedPositiveFeedbackYear, positiveFeedbackSBU, positiveFeedbackTrack);
    const downloadData = filtered.map(f => {
      const mapping = getUserMapping(f.userEmail);
      const dt = formatDateTimeParts(f.timestamp);
      return {
        'User Name': f.userName || (f.userEmail ? f.userEmail.split('@')[0] : ''),
        'User Email': f.userEmail || '',
        'Ticket Number': f.ticketNumber || '',
        'Feedback Type': f.feedbackType === 'thumbs_up' ? 'Satisfied' : f.feedbackType === 'thumbs_down' ? 'Not Satisfied' : (f.feedbackType || ''),
        'Reason': f.reason || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Assignment Group': f.assignmentGroup || '',
        'Date': dt.date,
        'Time': dt.time,
        'Comments': f.comments || ''
      };
    });
    // Sort descending: group by Track, largest group first
    const trackCounts = {};
    downloadData.forEach(r => { trackCounts[r['Track']] = (trackCounts[r['Track']] || 0) + 1; });
    downloadData.sort((a, b) => (trackCounts[b['Track']] || 0) - (trackCounts[a['Track']] || 0));
    const sbuName = Array.isArray(positiveFeedbackSBU) && positiveFeedbackSBU.length > 0 ? positiveFeedbackSBU.map(s => s.label).join('_') : 'All_SBU';
    const filename = `Feedback_${sbuName}_${selectedPositiveFeedbackMonth}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadNegativeFeedback = () => {
    const filtered = getFilteredFeedback(negativeFeedbackFilterType, selectedNegativeFeedbackMonth, selectedNegativeFeedbackFromDate, selectedNegativeFeedbackToDate, selectedNegativeFeedbackYear, negativeFeedbackSBU, negativeFeedbackTrack);
    const downloadData = filtered.filter(f => f.feedbackType === 'thumbs_down').map(f => {
      const mapping = getUserMapping(f.userEmail);
      const dt = formatDateTimeParts(f.timestamp);
      return {
        'User Name': f.userName || (f.userEmail ? f.userEmail.split('@')[0] : ''),
        'User Email': f.userEmail || '',
        'Ticket Number': f.ticketNumber || '',
        'Feedback Type': f.feedbackType || '',
        'Reason': f.reason || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Assignment Group': f.assignmentGroup || '',
        'Date': dt.date,
        'Time': dt.time,
        'Comments': f.comments || ''
      };
    });
    // Sort descending: group by Track, largest group first
    const trackCounts = {};
    downloadData.forEach(r => { trackCounts[r['Track']] = (trackCounts[r['Track']] || 0) + 1; });
    downloadData.sort((a, b) => (trackCounts[b['Track']] || 0) - (trackCounts[a['Track']] || 0));
    const sbuName = Array.isArray(negativeFeedbackSBU) && negativeFeedbackSBU.length > 0 ? negativeFeedbackSBU.map(s => s.label).join('_') : 'All_SBU';
    const filename = `Neg_Feedback_${sbuName}_${selectedNegativeFeedbackMonth}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadLoginTrends = () => {
    const filtered = getFilteredSessions(loginTrendsFilterType, loginTrendsMonth, null, null, loginTrendsYear, loginTrendsSBU, loginTrendsTrack);
    const downloadData = filtered.map(s => {
      const mapping = getUserMapping(s.userEmail);
      const start = formatDateTimeParts(s.startTime);
      const end = formatDateTimeParts(s.endTime);
      return {
        'User Name': s.userName || (s.userEmail ? s.userEmail.split('@')[0] : ''),
        'User Email': s.userEmail || '',
        'Assignment Group': s.assignmentGroup || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Start Date': start.date,
        'Start Time': start.time,
        'End Date': end.date,
        'End Time': end.time,
        'Total Duration (min)': s.totalDuration ? (s.totalDuration / 60).toFixed(1) : '0'
      };
    });
    // Sort descending: group by Track, largest group first
    const trackCounts = {};
    downloadData.forEach(r => { trackCounts[r['Track']] = (trackCounts[r['Track']] || 0) + 1; });
    downloadData.sort((a, b) => (trackCounts[b['Track']] || 0) - (trackCounts[a['Track']] || 0));
    const sbuName = Array.isArray(loginTrendsSBU) && loginTrendsSBU.length > 0 ? loginTrendsSBU.map(s => s.label).join('_') : 'All_SBU';
    const filename = `Login_Trends_${sbuName}_${loginTrendsMonth}_${loginTrendsYear}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadTicketTrends = () => {
    const filtered = getFilteredProcessClicks(ticketTrendsFilterType, ticketTrendsMonth, null, null, ticketTrendsYear, ticketTrendsSBU, ticketTrendsTrack);
    const downloadData = filtered.map(c => {
      const mapping = getUserMapping(c.userEmail);
      const dt = formatDateTimeParts(c.clickedAt || c.timestamp);
      return {
        'User Name': c.userName || (c.userEmail ? c.userEmail.split('@')[0] : ''),
        'User Email': c.userEmail || '',
        'Ticket Number': c.ticketNumber || '',
        'Short Description': c.shortDescription || '',
        'Assignment Group': c.assignmentGroup || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Clicked Date': dt.date,
        'Clicked Time': dt.time,
        'Priority': c.priority || ''
      };
    });
    // Sort descending: group by Track, largest group first
    const trackCounts = {};
    downloadData.forEach(r => { trackCounts[r['Track']] = (trackCounts[r['Track']] || 0) + 1; });
    downloadData.sort((a, b) => (trackCounts[b['Track']] || 0) - (trackCounts[a['Track']] || 0));
    const sbuName = Array.isArray(ticketTrendsSBU) && ticketTrendsSBU.length > 0 ? ticketTrendsSBU.map(s => s.label).join('_') : 'All_SBU';
    const filename = `Ticket_Trends_${sbuName}_${ticketTrendsMonth}_${ticketTrendsYear}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadFeedbackTrends = () => {
    const filtered = getFilteredFeedback(feedbackTrendsFilterType, feedbackTrendsMonth, null, null, feedbackTrendsYear, feedbackTrendsSBU, feedbackTrendsTrack);
    const downloadData = filtered.map(f => {
      const mapping = getUserMapping(f.userEmail);
      const dt = formatDateTimeParts(f.timestamp);
      return {
        'User Name': f.userName || (f.userEmail ? f.userEmail.split('@')[0] : ''),
        'User Email': f.userEmail || '',
        'Ticket Number': f.ticketNumber || '',
        'Feedback Type': f.feedbackType || '',
        'Reason': f.reason || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Assignment Group': f.assignmentGroup || '',
        'Date': dt.date,
        'Time': dt.time,
        'Comments': f.comments || ''
      };
    });
    // Sort descending: group by Track, largest group first
    const trackCounts = {};
    downloadData.forEach(r => { trackCounts[r['Track']] = (trackCounts[r['Track']] || 0) + 1; });
    downloadData.sort((a, b) => (trackCounts[b['Track']] || 0) - (trackCounts[a['Track']] || 0));
    const sbuName = Array.isArray(feedbackTrendsSBU) && feedbackTrendsSBU.length > 0 ? feedbackTrendsSBU.map(s => s.label).join('_') : 'All_SBU';
    const filename = `FB_Trends_${sbuName}_${feedbackTrendsMonth}_${feedbackTrendsYear}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadAreasTrends = () => {
    const filtered = getFilteredFeedback(areasTrendsFilterType, areasTrendsMonth, null, null, areasTrendsYear, areasTrendsSBU, areasTrendsTrack);
    const downloadData = filtered.filter(f => f.feedbackType === 'thumbs_down' && f.reason).map(f => {
      const mapping = getUserMapping(f.userEmail);
      const dt = formatDateTimeParts(f.timestamp);
      return {
        'User Name': f.userName || (f.userEmail ? f.userEmail.split('@')[0] : ''),
        'User Email': f.userEmail || '',
        'Ticket Number': f.ticketNumber || '',
        'Reason': f.reason || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Assignment Group': f.assignmentGroup || '',
        'Date': dt.date,
        'Time': dt.time,
        'Comments': f.comments || ''
      };
    });
    // Sort descending: group by Track, largest group first
    const trackCounts = {};
    downloadData.forEach(r => { trackCounts[r['Track']] = (trackCounts[r['Track']] || 0) + 1; });
    downloadData.sort((a, b) => (trackCounts[b['Track']] || 0) - (trackCounts[a['Track']] || 0));
    const sbuName = Array.isArray(areasTrendsSBU) && areasTrendsSBU.length > 0 ? areasTrendsSBU.map(s => s.label).join('_') : 'All_SBU';
    const filename = `Areas_Trends_${sbuName}_${areasTrendsMonth}_${areasTrendsYear}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadScreenTimeTrends = () => {
    const filtered = getFilteredSessions(screenTimeTrendsFilterType, screenTimeTrendsMonth, null, null, screenTimeTrendsYear, screenTimeTrendsSBU, screenTimeTrendsTrack);
    const downloadData = filtered.map(s => {
      const mapping = getUserMapping(s.userEmail);
      const start = formatDateTimeParts(s.startTime);
      const end = formatDateTimeParts(s.endTime);
      return {
        'User Name': s.userName || (s.userEmail ? s.userEmail.split('@')[0] : ''),
        'User Email': s.userEmail || '',
        'Assignment Group': s.assignmentGroup || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Start Date': start.date,
        'Start Time': start.time,
        'End Date': end.date,
        'End Time': end.time,
        'Total Duration (min)': s.totalDuration ? (s.totalDuration / 60).toFixed(1) : '0'
      };
    });
    const trackCounts = {};
    downloadData.forEach(r => { trackCounts[r['Track']] = (trackCounts[r['Track']] || 0) + 1; });
    downloadData.sort((a, b) => (trackCounts[b['Track']] || 0) - (trackCounts[a['Track']] || 0));
    const sbuName = Array.isArray(screenTimeTrendsSBU) && screenTimeTrendsSBU.length > 0 ? screenTimeTrendsSBU.map(s => s.label).join('_') : 'All_SBU';
    const filename = `ScreenTime_Trends_${sbuName}_${screenTimeTrendsMonth}_${screenTimeTrendsYear}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadGroupData = () => {
    const isAllGroups = Array.isArray(selectedGroup) && selectedGroup.some(g => g.value === 'all');
    const selectedGroups = (Array.isArray(selectedGroup) ? selectedGroup : []).filter(g => g.value !== 'all');
    let filtered = sessionData || [];
    if (!isAllGroups && selectedGroups.length > 0) {
      filtered = filtered.filter(s => selectedGroups.some(g => (s.assignmentGroup || '') === g.value));
    }
    if (groupFilterType === 'month' && selectedGroupMonth && selectedGroupYear) {
      filtered = filtered.filter(s => {
        const sd = s.startTime ? new Date(s.startTime) : (s.timestamp ? new Date(s.timestamp) : null);
        if (!sd) return false;
        const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        return sd.getFullYear() === parseInt(selectedGroupYear) && sd.getMonth() === monthNames.indexOf(selectedGroupMonth);
      });
    } else if (groupFilterType === 'date' && selectedGroupFromDate && selectedGroupToDate) {
      filtered = filtered.filter(s => {
        const sd = s.startTime ? new Date(s.startTime) : (s.timestamp ? new Date(s.timestamp) : null);
        if (!sd) return false;
        const from = new Date(selectedGroupFromDate); from.setHours(0,0,0,0);
        const to = new Date(selectedGroupToDate); to.setHours(23,59,59,999);
        return sd >= from && sd <= to;
      });
    }
    const downloadData = filtered.map(s => {
      const start = formatDateTimeParts(s.startTime);
      const end = formatDateTimeParts(s.endTime);
      return {
        'User Name': s.userName || (s.userEmail ? s.userEmail.split('@')[0] : ''),
        'User Email': s.userEmail || '',
        'Assignment Group': s.assignmentGroup || '',
        'Start Date': start.date,
        'Start Time': start.time,
        'End Date': end.date,
        'End Time': end.time,
        'Total Duration (min)': s.totalDuration ? (s.totalDuration / 60).toFixed(1) : '0'
      };
    });
    // Sort descending: group by Assignment Group, largest group first
    const grpCounts = {};
    downloadData.forEach(r => { grpCounts[r['Assignment Group']] = (grpCounts[r['Assignment Group']] || 0) + 1; });
    downloadData.sort((a, b) => (grpCounts[b['Assignment Group']] || 0) - (grpCounts[a['Assignment Group']] || 0));
    const groupName = isAllGroups ? 'All_Groups' : (Array.isArray(selectedGroup) ? selectedGroup.map(g => g.label).join('_') : selectedGroup?.label || 'Group');
    const filename = `Group_${groupName}_${selectedGroupMonth}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadTeamData = () => {
    let filtered = sessionData || [];
    if (teamFilterType === 'month' && selectedTeamMonth && selectedTeamYear) {
      filtered = filtered.filter(s => {
        const sd = new Date(s.startTime || s.timestamp);
        if (!s.startTime && !s.timestamp) return false;
        const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        return sd.getFullYear() === parseInt(selectedTeamYear) && sd.getMonth() === monthNames.indexOf(selectedTeamMonth);
      });
    } else if (teamFilterType === 'date' && selectedTeamFromDate && selectedTeamToDate) {
      filtered = filtered.filter(s => {
        const sd = new Date(s.startTime || s.timestamp);
        if (!s.startTime && !s.timestamp) return false;
        const from = new Date(selectedTeamFromDate); from.setHours(0,0,0,0);
        const to = new Date(selectedTeamToDate); to.setHours(23,59,59,999);
        return sd >= from && sd <= to;
      });
    }
    const downloadData = filtered.map(s => {
      const start = formatDateTimeParts(s.startTime);
      const end = formatDateTimeParts(s.endTime);
      return {
        'User Name': s.userName || (s.userEmail ? s.userEmail.split('@')[0] : ''),
        'User Email': s.userEmail || '',
        'Assignment Group': s.assignmentGroup || '',
        'Start Date': start.date,
        'Start Time': start.time,
        'End Date': end.date,
        'End Time': end.time,
        'Total Duration (min)': s.totalDuration ? (s.totalDuration / 60).toFixed(1) : '0'
      };
    });
    // Sort descending: group by Assignment Group, largest group first
    const grpCounts = {};
    downloadData.forEach(r => { grpCounts[r['Assignment Group']] = (grpCounts[r['Assignment Group']] || 0) + 1; });
    downloadData.sort((a, b) => (grpCounts[b['Assignment Group']] || 0) - (grpCounts[a['Assignment Group']] || 0));
    const filename = `Team_Time_${selectedTeamMonth}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadAllTeamsData = () => {
    let filtered = sessionData || [];
    if (allTeamsFilterType === 'month' && selectedAllTeamsMonth && selectedAllTeamsYear) {
      filtered = filtered.filter(s => {
        const sd = s.startTime ? new Date(s.startTime) : (s.timestamp ? new Date(s.timestamp) : null);
        if (!sd) return false;
        const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        return sd.getFullYear() === parseInt(selectedAllTeamsYear) && sd.getMonth() === monthNames.indexOf(selectedAllTeamsMonth);
      });
    } else if (allTeamsFilterType === 'date' && selectedAllTeamsFromDate && selectedAllTeamsToDate) {
      filtered = filtered.filter(s => {
        const sd = s.startTime ? new Date(s.startTime) : (s.timestamp ? new Date(s.timestamp) : null);
        if (!sd) return false;
        const from = new Date(selectedAllTeamsFromDate); from.setHours(0,0,0,0);
        const to = new Date(selectedAllTeamsToDate); to.setHours(23,59,59,999);
        return sd >= from && sd <= to;
      });
    }
    if (Array.isArray(selectedSBU) && selectedSBU.length > 0) {
      const sbuVals = selectedSBU.map(s => s.value);
      filtered = filtered.filter(s => sbuVals.includes(getGroupMapping(s.assignmentGroup).sbu));
    }
    if (Array.isArray(selectedTrack) && selectedTrack.length > 0) {
      const trackVals = selectedTrack.map(t => t.value);
      filtered = filtered.filter(s => trackVals.includes(getGroupMapping(s.assignmentGroup).track));
    }
    const downloadData = filtered.map(s => {
      const mapping = getGroupMapping(s.assignmentGroup);
      const start = formatDateTimeParts(s.startTime);
      const end = formatDateTimeParts(s.endTime);
      return {
        'User Name': s.userName || (s.userEmail ? s.userEmail.split('@')[0] : ''),
        'User Email': s.userEmail || '',
        'Assignment Group': s.assignmentGroup || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Start Date': start.date,
        'Start Time': start.time,
        'End Date': end.date,
        'End Time': end.time,
        'Total Duration (min)': s.totalDuration ? (s.totalDuration / 60).toFixed(1) : '0'
      };
    });
    // Sort descending: group by Track, largest group first
    const trackCounts = {};
    downloadData.forEach(r => { trackCounts[r['Track']] = (trackCounts[r['Track']] || 0) + 1; });
    downloadData.sort((a, b) => (trackCounts[b['Track']] || 0) - (trackCounts[a['Track']] || 0));
    const trackName = Array.isArray(selectedTrack) && selectedTrack.length > 0 ? selectedTrack.map(t => t.label).join('_') : 'All_Tracks';
    const sbuName = Array.isArray(selectedSBU) && selectedSBU.length > 0 ? selectedSBU.map(s => s.label).join('_') : 'All_SBU';
    const filename = `Track_Time_${sbuName}_${trackName}_${selectedAllTeamsMonth}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadLoginCount = () => {
    const filtered = getFilteredSessions(loginCountFilterType, loginCountMonth, loginCountFromDate, loginCountToDate, loginCountYear, loginCountSBU, loginCountTrack);
    const downloadData = filtered.map(s => {
      const mapping = getUserMapping(s.userEmail);
      const start = formatDateTimeParts(s.startTime);
      const end = formatDateTimeParts(s.endTime);
      return {
        'User Name': s.userName || (s.userEmail ? s.userEmail.split('@')[0] : ''),
        'User Email': s.userEmail || '',
        'Assignment Group': s.assignmentGroup || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Start Date': start.date,
        'Start Time': start.time,
        'End Date': end.date,
        'End Time': end.time,
        'Total Duration (min)': s.totalDuration ? (s.totalDuration / 60).toFixed(1) : '0'
      };
    });
    // Sort descending: group by Track, largest group first
    const trackCounts = {};
    downloadData.forEach(r => { trackCounts[r['Track']] = (trackCounts[r['Track']] || 0) + 1; });
    downloadData.sort((a, b) => (trackCounts[b['Track']] || 0) - (trackCounts[a['Track']] || 0));
    const filename = `Login_Count_${loginCountMonth}_${loginCountYear}`;
    downloadExcel(downloadData, filename);
  };

  const handleDownloadTicketProcess = () => {
    const filtered = getFilteredProcessClicks(ticketProcessFilterType, ticketProcessMonth, ticketProcessFromDate, ticketProcessToDate, ticketProcessYear, ticketProcessSBU, ticketProcessTrack);
    if (!filtered || filtered.length === 0) return;
    const downloadData = filtered.map(c => {
      const mapping = getUserMapping(c.userEmail);
      const dt = formatDateTimeParts(c.clickedAt || c.timestamp);
      return {
        'User Name': c.userName || (c.userEmail ? c.userEmail.split('@')[0] : ''),
        'User Email': c.userEmail || '',
        'Ticket Number': c.ticketNumber || '',
        'Short Description': c.shortDescription || '',
        'Assignment Group': c.assignmentGroup || '',
        'Assigned To': c.assignedTo || '',
        'SBU': mapping.sbu,
        'Track': mapping.track,
        'Clicked Date': dt.date,
        'Clicked Time': dt.time,
        'Priority': c.priority || '',
        'Category': c.category || ''
      };
    });
    // Sort descending: group by Track, largest group first
    const trackCounts = {};
    downloadData.forEach(r => { trackCounts[r['Track']] = (trackCounts[r['Track']] || 0) + 1; });
    downloadData.sort((a, b) => (trackCounts[b['Track']] || 0) - (trackCounts[a['Track']] || 0));
    const filename = `Ticket_Process_${ticketProcessMonth}_${ticketProcessYear}`;
    downloadExcel(downloadData, filename);
  };

  return (
    <div className="analytics-wrapper">
      <NavBar userName={userName} userEmail={userEmail} onLogout={onLogout} canViewUserAnalytics={canViewUserAnalytics} />
      
      <div className="screen-time-page">
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">
              <span className="card-title-icon">
                <FaClock className="header-icon-purple" />
                <span>User Analytics</span>
              </span>
            </h1>
            <p className="dashboard-subtitle">Monitor user engagement and ticket resolution time (Real-time Data)</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button onClick={handleSyncData} className="sync-data-btn" disabled={loading}>
              {loading ? '⏳ Syncing...' : '🔄 Sync Data'}
            </button>
            <Link to="/AgentAssist" className="analytics-link">
              Home
            </Link>
          </div>
        </div>

        <div className="global-filters-panel">
          <div className="global-filters-row">
            <div className="global-filter-field">
              <label>SBU</label>
              <select value={globalSBUValue} onChange={(e) => { setGlobalSBUValue(e.target.value); setGlobalTrackValue('all'); }}>
                <option value="all">All SBU</option>
                {availableUserSBUs.filter(s => s.value !== 'all').map((sbu) => (
                  <option key={sbu.value} value={sbu.value}>{sbu.label}</option>
                ))}
              </select>
            </div>

            <div className="global-filter-field">
              <label>Track</label>
              <select value={globalTrackValue} onChange={(e) => setGlobalTrackValue(e.target.value)}>
                <option value="all">All Tracks</option>
                {globalTrackOptions.filter(t => t.value !== 'all').map((track) => (
                  <option key={track.value} value={track.value}>{track.label}</option>
                ))}
              </select>
            </div>

            <div className="global-filter-field">
              <label>Period</label>
              <div className="global-filter-type-group">
                <button
                  className={globalFilterType === 'month' ? 'active' : ''}
                  onClick={() => setGlobalFilterType('month')}
                >
                  Month
                </button>
                <button
                  className={globalFilterType === 'date' ? 'active' : ''}
                  onClick={() => setGlobalFilterType('date')}
                >
                  Date
                </button>
                <button
                  className={globalFilterType === 'week' ? 'active' : ''}
                  onClick={() => setGlobalFilterType('week')}
                >
                  Week
                </button>
              </div>
            </div>

            {(globalFilterType === 'month' || globalFilterType === 'week') && (
              <MonthSelector
                year={globalYear}
                month={globalMonth}
                onYearChange={(y) => setGlobalYear(String(y))}
                onMonthChange={setGlobalMonth}
                label={globalFilterType === 'week' ? 'Week Base Month:' : 'Month:'}
              />
            )}

            {globalFilterType === 'date' && (
              <DateRangeSelector
                fromDate={globalFromDate}
                toDate={globalToDate}
                onFromDateChange={setGlobalFromDate}
                onToDateChange={setGlobalToDate}
              />
            )}

            {globalFilterType === 'week' && (
              <div className="global-filter-field">
                <label>Week</label>
                <select value={globalWeek} onChange={(e) => setGlobalWeek(e.target.value)}>
                  <option value="1">Week 1</option>
                  <option value="2">Week 2</option>
                  <option value="3">Week 3</option>
                  <option value="4">Week 4</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-container">
            <div className="loading-title">Loading screen time data...</div>
            <div className="loading-subtitle">Please wait while we fetch the latest information</div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="error-container">
            <strong>Error:</strong> {error?.message || error}
          </div>
        )}

        {/* Data Display */}
        {!loading && (
          <>
        {/* Agent Assist Login Count Dashboard */}
        <div className="chart-card">
          <div className="chart-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 className="chart-title">🔐 Agent Assist Login Count</h3>
                <p className="chart-subtitle">{loginCountTrack?.length > 0 ? 'Top 20 users by login count for selected track(s)' : loginCountSBU?.length > 0 ? 'Top 20 tracks by login count for selected SBU — Select a Track to see users' : 'Select SBU → Track to see how many times each user logged in'}</p>
              </div>
              <span className="total-count-badge">Total Login Count <span className="badge-count">{loginCountData.reduce((sum, d) => sum + (d.loginCount || 0), 0)}</span></span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
            <div className="filters-row">
              {/* SBU Selector */}
              <div className="sbu-selector" ref={loginCountSBUDropdownRef} style={{ 
                position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '180px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>SBU:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowLoginCountSBUDropdown(!showLoginCountSBUDropdown)}
                    style={{
                      padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}
                  >
                    <span style={{ fontSize: '12px', color: loginCountSBU?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500' }}>
                      {getSelectedLoginCountSBULabel()}
                    </span>
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showLoginCountSBUDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showLoginCountSBUDropdown && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white',
                      border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '220px', overflowY: 'auto', zIndex: 1000
                    }}>
                      <div onClick={handleSelectAllLoginCountSBU} style={{
                        padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                        borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb', fontWeight: '600', position: 'sticky', top: 0, zIndex: 1
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef3c7'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      >
                        <input type="checkbox" checked={isAllLoginCountSBUSelected()} onChange={handleSelectAllLoginCountSBU}
                          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }} onClick={(e) => e.stopPropagation()} />
                        <span style={{ fontSize: '12px', color: '#1f2937' }}>Select All SBU</span>
                      </div>
                      {availableUserSBUs.filter(s => s.value !== 'all').map((sbu) => {
                        const isSelected = Array.isArray(loginCountSBU) && loginCountSBU.some(s => s.value === sbu.value);
                        return (
                          <div key={sbu.value} onClick={() => handleToggleLoginCountSBU(sbu)} style={{
                            padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            backgroundColor: isSelected ? '#fffbeb' : 'white', transition: 'background-color 0.15s ease'
                          }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#fffbeb' : 'white'; }}
                          >
                            <input type="checkbox" checked={isSelected} onChange={() => handleToggleLoginCountSBU(sbu)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }} onClick={(e) => e.stopPropagation()} />
                            <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{sbu.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Track Selector */}
              <div className="track-selector" ref={loginCountTrackDropdownRef} style={{ 
                position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '200px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>Track:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowLoginCountTrackDropdown(!showLoginCountTrackDropdown)}
                    style={{
                      padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}
                  >
                    <span style={{ fontSize: '12px', color: loginCountTrack?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500', flex: 1 }}>
                      {getSelectedLoginCountTrackLabel()}
                    </span>
                    {loginCountTrack?.length > 0 && <span onClick={(e) => { e.stopPropagation(); setLoginCountTrack([]); }} style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>×</span>}
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showLoginCountTrackDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showLoginCountTrackDropdown && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white',
                      border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '320px', overflowY: 'auto', zIndex: 1000
                    }}>
                      {loginCountAvailableTracks.filter(t => t.value !== 'all').map((track) => {
                        const isSelected = Array.isArray(loginCountTrack) && loginCountTrack.some(t => t.value === track.value);
                        return (
                          <div key={track.value} onClick={() => handleToggleLoginCountTrack(track)} style={{
                            padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            backgroundColor: isSelected ? '#fffbeb' : 'white', transition: 'background-color 0.15s ease'
                          }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#fffbeb' : 'white'; }}
                          >
                            <input type="radio" name="loginCountTrackRadio" checked={isSelected} onChange={() => handleToggleLoginCountTrack(track)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }} onClick={(e) => e.stopPropagation()} />
                            <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{track.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <FilterTypeButtons filterType={loginCountFilterType} onFilterTypeChange={setLoginCountFilterType} />
              
              {loginCountFilterType === 'month' && (
                <MonthSelector
                  year={loginCountYear} month={loginCountMonth}
                  onYearChange={setLoginCountYear} onMonthChange={setLoginCountMonth}
                />
              )}
              {loginCountFilterType === 'date' && (
                <DateRangeSelector
                  fromDate={loginCountFromDate} toDate={loginCountToDate}
                  onFromDateChange={setLoginCountFromDate} onToDateChange={setLoginCountToDate}
                />
              )}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={loginCountData} margin={{ top: 25, right: 16, bottom: 5, left: 8 }}>
              <defs>
                <linearGradient id="loginCountGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" />
                  <stop offset="100%" stopColor="#d97706" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="name" stroke="#4a5568" interval={0} height={36}
                tick={({ x, y, payload, index }) => {
                  if (index % 2 !== 0) return <g />;
                  const raw = payload.value || '';
                  const label = raw.length > 12 ? raw.substring(0, 11) + '…' : raw;
                  return (
                    <g transform={`translate(${x},${y})`}>
                      <text x={0} y={0} dy={12} textAnchor="middle" fill="#4a5568" fontSize={8}>{label}</text>
                    </g>
                  );
                }}
              />
              <YAxis stroke="#4a5568" style={{ fontSize: '11px' }} />
              <Tooltip 
                contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                formatter={(value, name, props) => [value, 'Login Count']}
                labelFormatter={(label) => {
                  const item = loginCountData.find(d => d.name === label);
                  if (item?.isTrackLevel) return `Track: ${label}`;
                  return item ? `${label} (${item.track})` : label;
                }}
              />
              <Bar dataKey="loginCount" fill="url(#loginCountGradient)" barSize={12} radius={[8, 8, 0, 0]}>
                <LabelList dataKey="loginCount" position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '11px' }} formatter={(value) => value > 0 ? value : ''} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="download-button-container">
            <button onClick={handleDownloadLoginCount} className="download-btn">📥 Download</button>
          </div>
        </div>

        {/* Ticket Process Dashboard */}
        <div className="chart-card">
          <div className="chart-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 className="chart-title">🎫 Ticket Process</h3>
                <p className="chart-subtitle">{ticketProcessTrack?.length > 0 ? 'Top 20 users by process button clicks for selected track(s)' : ticketProcessSBU?.length > 0 ? 'Top 20 tracks by process button clicks for selected SBU — Select a Track to see users' : 'Select SBU → Track to see who asked for AI recommendation'}</p>
              </div>
              <span className="total-count-badge">Total Process Count <span className="badge-count">{ticketProcessData.reduce((sum, d) => sum + (d.processCount || 0), 0)}</span></span>
            </div>
          </div>
          {/* Row 1: SBU & Track Filters */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '4px' }}>
              {/* SBU Selector */}
              <div className="sbu-selector" ref={ticketProcessSBUDropdownRef} style={{ 
                position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', minWidth: '180px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>SBU:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowTicketProcessSBUDropdown(!showTicketProcessSBUDropdown)}
                    style={{
                      padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}
                  >
                    <span style={{ fontSize: '12px', color: ticketProcessSBU?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500' }}>
                      {getSelectedTicketProcessSBULabel()}
                    </span>
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showTicketProcessSBUDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showTicketProcessSBUDropdown && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white',
                      border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '220px', overflowY: 'auto', zIndex: 1000
                    }}>
                      <div onClick={handleSelectAllTicketProcessSBU} style={{
                        padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                        borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb', fontWeight: '600', position: 'sticky', top: 0, zIndex: 1
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#eef2ff'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      >
                        <input type="checkbox" checked={isAllTicketProcessSBUSelected()} onChange={handleSelectAllTicketProcessSBU}
                          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6366f1' }} onClick={(e) => e.stopPropagation()} />
                        <span style={{ fontSize: '12px', color: '#1f2937' }}>Select All SBU</span>
                      </div>
                      {availableUserSBUs.filter(s => s.value !== 'all').map((sbu) => {
                        const isSelected = Array.isArray(ticketProcessSBU) && ticketProcessSBU.some(s => s.value === sbu.value);
                        return (
                          <div key={sbu.value} onClick={() => handleToggleTicketProcessSBU(sbu)} style={{
                            padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            backgroundColor: isSelected ? '#eef2ff' : 'white', transition: 'background-color 0.15s ease'
                          }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#eef2ff' : 'white'; }}
                          >
                            <input type="checkbox" checked={isSelected} onChange={() => handleToggleTicketProcessSBU(sbu)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6366f1' }} onClick={(e) => e.stopPropagation()} />
                            <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{sbu.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Track Selector */}
              <div className="track-selector" ref={ticketProcessTrackDropdownRef} style={{ 
                position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', minWidth: '200px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>Track:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowTicketProcessTrackDropdown(!showTicketProcessTrackDropdown)}
                    style={{
                      padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}
                  >
                    <span style={{ fontSize: '12px', color: ticketProcessTrack?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500', flex: 1 }}>
                      {getSelectedTicketProcessTrackLabel()}
                    </span>
                    {ticketProcessTrack?.length > 0 && <span onClick={(e) => { e.stopPropagation(); setTicketProcessTrack([]); }} style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>×</span>}
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showTicketProcessTrackDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showTicketProcessTrackDropdown && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white',
                      border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '320px', overflowY: 'auto', zIndex: 1000
                    }}>
                      {ticketProcessAvailableTracks.filter(t => t.value !== 'all').map((track) => {
                        const isSelected = Array.isArray(ticketProcessTrack) && ticketProcessTrack.some(t => t.value === track.value);
                        return (
                          <div key={track.value} onClick={() => handleToggleTicketProcessTrack(track)} style={{
                            padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            backgroundColor: isSelected ? '#eef2ff' : 'white', transition: 'background-color 0.15s ease'
                          }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#eef2ff' : 'white'; }}
                          >
                            <input type="radio" name="ticketProcessTrackRadio" checked={isSelected} onChange={() => handleToggleTicketProcessTrack(track)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#6366f1' }} onClick={(e) => e.stopPropagation()} />
                            <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{track.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <FilterTypeButtons filterType={ticketProcessFilterType} onFilterTypeChange={setTicketProcessFilterType} />
              
              {ticketProcessFilterType === 'month' && (
                <MonthSelector
                  year={ticketProcessYear} month={ticketProcessMonth}
                  onYearChange={setTicketProcessYear} onMonthChange={setTicketProcessMonth}
                />
              )}
              {ticketProcessFilterType === 'date' && (
                <DateRangeSelector
                  fromDate={ticketProcessFromDate} toDate={ticketProcessToDate}
                  onFromDateChange={setTicketProcessFromDate} onToDateChange={setTicketProcessToDate}
                />
              )}
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={ticketProcessData} margin={{ top: 25, right: 16, bottom: 5, left: 8 }}>
              <defs>
                <linearGradient id="ticketProcessGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" />
                  <stop offset="100%" stopColor="#4f46e5" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="name" stroke="#4a5568" interval={0} height={36}
                tick={({ x, y, payload, index }) => {
                  if (index % 2 !== 0) return <g />;
                  const raw = payload.value || '';
                  const label = raw.length > 12 ? raw.substring(0, 11) + '…' : raw;
                  return (
                    <g transform={`translate(${x},${y})`}>
                      <text x={0} y={0} dy={12} textAnchor="middle" fill="#4a5568" fontSize={8}>{label}</text>
                    </g>
                  );
                }}
              />
              <YAxis stroke="#4a5568" style={{ fontSize: '11px' }} />
              <Tooltip 
                contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                formatter={(value, name, props) => [value, 'Process Count']}
                labelFormatter={(label) => {
                  const item = ticketProcessData.find(d => d.name === label);
                  if (item?.isTrackLevel) return `Track: ${label}`;
                  return item ? `${label} (${item.track})` : label;
                }}
              />
              <Bar dataKey="processCount" fill="url(#ticketProcessGradient)" barSize={12} radius={[8, 8, 0, 0]}>
                <LabelList dataKey="processCount" position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '11px' }} formatter={(value) => value > 0 ? value : ''} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="download-button-container">
            <button onClick={handleDownloadTicketProcess} className="download-btn">📥 Download</button>
          </div>
        </div>

        <div className="charts-grid">
          {/* Dashboard 3A: Overall Feedback */}
          <div className="chart-card">
            <div className="chart-header">
              <h3 className="chart-title">😊 Overall Feedback</h3>
              <p className="chart-subtitle">User satisfaction metrics</p>
            </div>
            {/* Filters: SBU, Track, Month/Date on one row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                {/* SBU Filter */}
                <div className="feedback-filter-item feedback-filter-group">
                  <label className="feedback-filter-label">SBU:</label>
                  <div ref={positiveFeedbackSBUDropdownRef} className="feedback-dropdown-wrapper">
                    <div 
                      onClick={() => setShowPositiveFeedbackSBUDropdown(!showPositiveFeedbackSBUDropdown)}
                      className="feedback-group-dropdown-trigger"
                    >
                      <span className="feedback-group-label">{getSelectedPositiveFeedbackSBULabel()}</span>
                      <span className="feedback-dropdown-arrow">▼</span>
                    </div>
                    {showPositiveFeedbackSBUDropdown && (
                      <div className="feedback-group-dropdown-menu">
                        <div onClick={handleSelectAllPositiveFeedbackSBU} className="feedback-group-select-all">
                          <input type="checkbox" checked={isAllPositiveFeedbackSBUSelected()} onChange={handleSelectAllPositiveFeedbackSBU} className="feedback-group-checkbox positive" onClick={(e) => e.stopPropagation()} />
                          <span className="feedback-group-option-label">Select All SBU</span>
                        </div>
                        {availableUserSBUs.filter(s => s.value !== 'all').map((sbu) => (
                          <div key={sbu.value} onClick={() => handleTogglePositiveFeedbackSBU(sbu)} className="feedback-group-option">
                            <input type="checkbox" checked={Array.isArray(positiveFeedbackSBU) && positiveFeedbackSBU.some(s => s.value === sbu.value)} onChange={() => handleTogglePositiveFeedbackSBU(sbu)} className="feedback-group-checkbox positive" onClick={(e) => e.stopPropagation()} />
                            <span className="feedback-group-option-label">{sbu.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Track Filter */}
                <div className="feedback-filter-item feedback-filter-group">
                  <label className="feedback-filter-label">Track:</label>
                  <div ref={positiveFeedbackTrackDropdownRef} className="feedback-dropdown-wrapper">
                    <div 
                      onClick={() => setShowPositiveFeedbackTrackDropdown(!showPositiveFeedbackTrackDropdown)}
                      className="feedback-group-dropdown-trigger"
                    >
                      <span className="feedback-group-label" style={{ flex: 1 }}>{getSelectedPositiveFeedbackTrackLabel()}</span>
                      {positiveFeedbackTrack?.length > 0 && <span onClick={(e) => { e.stopPropagation(); setPositiveFeedbackTrack([]); }} style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>×</span>}
                      <span className="feedback-dropdown-arrow">▼</span>
                    </div>
                    {showPositiveFeedbackTrackDropdown && (
                      <div className="feedback-group-dropdown-menu">
                        {positiveFeedbackAvailableTracks.filter(t => t.value !== 'all').map((track) => (
                          <div key={track.value} onClick={() => handleTogglePositiveFeedbackTrack(track)} className="feedback-group-option">
                            <input type="radio" name="positiveFeedbackTrackRadio" checked={Array.isArray(positiveFeedbackTrack) && positiveFeedbackTrack.some(t => t.value === track.value)} onChange={() => handleTogglePositiveFeedbackTrack(track)} className="feedback-group-checkbox positive" onClick={(e) => e.stopPropagation()} />
                            <span className="feedback-group-option-label">{track.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

            </div>
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={positiveFeedbackData} margin={{ top: 25, right: 20, bottom: 5, left: 10 }} barSize={24}>
                  <defs>
                    <linearGradient id="satisfiedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#059669" />
                    </linearGradient>
                    <linearGradient id="notSatisfiedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ef4444" />
                      <stop offset="100%" stopColor="#dc2626" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#4a5568"
                    style={{ fontSize: '11px', fontWeight: '500' }}
                    angle={0}
                    textAnchor="middle"
                    height={30}
                    interval={0}
                  />
                  <YAxis stroke="#4a5568" style={{ fontSize: '12px' }} />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                    formatter={(value) => [value, 'Count']}
                  />
                  <Bar 
                    dataKey="count" 
                    barSize={12}
                    radius={[8, 8, 0, 0]}
                  >
                    {positiveFeedbackData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={index === 0 ? 'url(#satisfiedGradient)' : 'url(#notSatisfiedGradient)'} />
                    ))}
                    <LabelList dataKey="count" position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '12px' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            <div className="download-button-container">
              <button onClick={handleDownloadPositiveFeedback} className="download-btn">
                📥 Download
              </button>
            </div>
          </div>

          {/* Dashboard 3B: Areas to be improved */}
          <div className="chart-card">
            <div className="chart-header">
              <h3 className="chart-title">Areas to be Improved</h3>
              <p className="chart-subtitle">Feedback areas needing attention</p>
            </div>
            {/* Filters: SBU, Track, Month/Date on one row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                {/* SBU Filter */}
                <div className="feedback-filter-item feedback-filter-group">
                  <label className="feedback-filter-label">SBU:</label>
                  <div ref={negativeFeedbackSBUDropdownRef} className="feedback-dropdown-wrapper">
                    <div 
                      onClick={() => setShowNegativeFeedbackSBUDropdown(!showNegativeFeedbackSBUDropdown)}
                      className="feedback-group-dropdown-trigger"
                    >
                      <span className="feedback-group-label">{getSelectedNegativeFeedbackSBULabel()}</span>
                      <span className="feedback-dropdown-arrow">▼</span>
                    </div>
                    {showNegativeFeedbackSBUDropdown && (
                      <div className="feedback-group-dropdown-menu">
                        <div onClick={handleSelectAllNegativeFeedbackSBU} className="feedback-group-select-all">
                          <input type="checkbox" checked={isAllNegativeFeedbackSBUSelected()} onChange={handleSelectAllNegativeFeedbackSBU} className="feedback-group-checkbox negative" onClick={(e) => e.stopPropagation()} />
                          <span className="feedback-group-option-label">Select All SBU</span>
                        </div>
                        {availableUserSBUs.filter(s => s.value !== 'all').map((sbu) => (
                          <div key={sbu.value} onClick={() => handleToggleNegativeFeedbackSBU(sbu)} className="feedback-group-option">
                            <input type="checkbox" checked={Array.isArray(negativeFeedbackSBU) && negativeFeedbackSBU.some(s => s.value === sbu.value)} onChange={() => handleToggleNegativeFeedbackSBU(sbu)} className="feedback-group-checkbox negative" onClick={(e) => e.stopPropagation()} />
                            <span className="feedback-group-option-label">{sbu.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Track Filter */}
                <div className="feedback-filter-item feedback-filter-group">
                  <label className="feedback-filter-label">Track:</label>
                  <div ref={negativeFeedbackTrackDropdownRef} className="feedback-dropdown-wrapper">
                    <div 
                      onClick={() => setShowNegativeFeedbackTrackDropdown(!showNegativeFeedbackTrackDropdown)}
                      className="feedback-group-dropdown-trigger"
                    >
                      <span className="feedback-group-label" style={{ flex: 1 }}>{getSelectedNegativeFeedbackTrackLabel()}</span>
                      {negativeFeedbackTrack?.length > 0 && <span onClick={(e) => { e.stopPropagation(); setNegativeFeedbackTrack([]); }} style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>×</span>}
                      <span className="feedback-dropdown-arrow">▼</span>
                    </div>
                    {showNegativeFeedbackTrackDropdown && (
                      <div className="feedback-group-dropdown-menu">
                        {negativeFeedbackAvailableTracks.filter(t => t.value !== 'all').map((track) => (
                          <div key={track.value} onClick={() => handleToggleNegativeFeedbackTrack(track)} className="feedback-group-option">
                            <input type="radio" name="negativeFeedbackTrackRadio" checked={Array.isArray(negativeFeedbackTrack) && negativeFeedbackTrack.some(t => t.value === track.value)} onChange={() => handleToggleNegativeFeedbackTrack(track)} className="feedback-group-checkbox negative" onClick={(e) => e.stopPropagation()} />
                            <span className="feedback-group-option-label">{track.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
            </div>
              <ResponsiveContainer width="100%" height={210}>
                <BarChart data={negativeFeedbackData} margin={{ top: 25, right: 20, bottom: 5, left: 10 }} barSize={24}>
                  <defs>
                    <linearGradient id="negativeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ef4444" />
                      <stop offset="100%" stopColor="#dc2626" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#4a5568"
                    style={{ fontSize: '11px', fontWeight: '500' }}
                    angle={0}
                    textAnchor="middle"
                    height={30}
                    interval={0}
                  />
                  <YAxis stroke="#4a5568" style={{ fontSize: '12px' }} />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                    formatter={(value) => [value, 'Count']}
                  />
                  <Bar 
                    dataKey="count" 
                    barSize={12}
                    fill="url(#negativeGradient)" 
                    radius={[8, 8, 0, 0]}
                  >
                    <LabelList dataKey="count" position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '12px' }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            <div className="download-button-container">
              <button onClick={handleDownloadNegativeFeedback} className="download-btn">
                📥 Download
              </button>
            </div>
          </div>
        </div>

        {/* 4. Screen Time as per All Teams - Shows teams with their assignment groups combined */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">Screen Time by Track</h3>
            <p className="chart-subtitle">{selectedTrack?.length > 0 ? 'Top 20 users by screen time for selected track(s)' : selectedSBU?.length > 0 ? 'Top 20 tracks by screen time for selected SBU — Select a Track to see users' : 'Select SBU → Track to see screen time distribution'}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
            <div className="filters-row">
              {/* SBU Selector with Checkbox - FIRST */}
              <div className="sbu-selector" ref={sbuDropdownRef} style={{ 
                position: 'relative', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px', 
                marginRight: '8px',
                minWidth: '180px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>
                  SBU:
                </label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowSBUDropdown(!showSBUDropdown)}
                    style={{
                      padding: '6px 10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      backgroundColor: 'white',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#10b981';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#d1d5db';
                      e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    <span style={{ 
                      fontSize: '12px', 
                      color: selectedSBU?.length > 0 ? '#1f2937' : '#9ca3af',
                      fontWeight: '500'
                    }}>
                      {getSelectedSBULabel()}
                    </span>
                    <span style={{ 
                      fontSize: '10px', 
                      color: '#6b7280',
                      transform: showSBUDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }}>▼</span>
                  </div>
                  
                  {showSBUDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '220px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      animation: 'slideDown 0.2s ease'
                    }}>
                      {/* Select All Option */}
                      <div 
                        onClick={handleSelectAllSBU}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          borderBottom: '2px solid #e5e7eb',
                          backgroundColor: '#f9fafb',
                          fontWeight: '600',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1fae5'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      >
                        <input
                          type="checkbox"
                          checked={isAllSBUSelected()}
                          onChange={handleSelectAllSBU}
                          style={{
                            width: '16px',
                            height: '16px',
                            cursor: 'pointer',
                            accentColor: '#10b981'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span style={{ fontSize: '12px', color: '#1f2937' }}>Select All SBU</span>
                      </div>

                      {/* Individual SBU Options */}
                      {availableSBUs.filter(s => s.value !== 'all').map((sbu) => {
                        const isSelected = Array.isArray(selectedSBU) && selectedSBU.some(s => s.value === sbu.value);
                        return (
                          <div
                            key={sbu.value}
                            onClick={() => handleToggleSBU(sbu)}
                            style={{
                              padding: '10px 16px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              backgroundColor: isSelected ? '#f0fdf4' : 'white',
                              transition: 'background-color 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = isSelected ? '#f0fdf4' : 'white';
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleSBU(sbu)}
                              style={{
                                width: '16px',
                                height: '16px',
                                cursor: 'pointer',
                                accentColor: '#10b981'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span style={{ 
                              fontSize: '12px', 
                              color: '#374151',
                              fontWeight: isSelected ? '500' : '400'
                            }}>
                              {sbu.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Track Selector with Checkbox - SECOND */}
              <div className="track-selector" ref={trackDropdownRef} style={{ 
                position: 'relative', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px', 
                marginRight: '8px',
                minWidth: '200px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>
                  Track:
                </label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowTrackDropdown(!showTrackDropdown)}
                    style={{
                      padding: '6px 10px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      backgroundColor: 'white',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#10b981';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#d1d5db';
                      e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    <span style={{ 
                      fontSize: '12px', 
                      color: selectedTrack?.length > 0 ? '#1f2937' : '#9ca3af',
                      fontWeight: '500',
                      flex: 1
                    }}>
                      {getSelectedTrackLabel()}
                    </span>
                    {selectedTrack?.length > 0 && <span onClick={(e) => { e.stopPropagation(); setSelectedTrack([]); }} style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>×</span>}
                    <span style={{ 
                      fontSize: '10px', 
                      color: '#6b7280',
                      transform: showTrackDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }}>▼</span>
                  </div>
                  
                  {showTrackDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '320px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      animation: 'slideDown 0.2s ease'
                    }}>
                      {/* Individual Track Options */}
                      {availableTracks.filter(t => t.value !== 'all').map((track) => {
                        const isSelected = Array.isArray(selectedTrack) && selectedTrack.some(t => t.value === track.value);
                        return (
                          <div
                            key={track.value}
                            onClick={() => handleToggleTrack(track)}
                            style={{
                              padding: '10px 16px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              backgroundColor: isSelected ? '#f0fdf4' : 'white',
                              transition: 'background-color 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = isSelected ? '#f0fdf4' : 'white';
                            }}
                          >
                            <input
                              type="radio"
                              name="selectedTrackRadio"
                              checked={isSelected}
                              onChange={() => handleToggleTrack(track)}
                              style={{
                                width: '16px',
                                height: '16px',
                                cursor: 'pointer',
                                accentColor: '#10b981'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span style={{ 
                              fontSize: '12px', 
                              color: '#374151',
                              fontWeight: isSelected ? '500' : '400'
                            }}>
                              {track.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Filter Type Selector */}
              <FilterTypeButtons 
                filterType={allTeamsFilterType} 
                onFilterTypeChange={setAllTeamsFilterType} 
              />
              
              {/* Conditional Filter Selectors */}
              {allTeamsFilterType === 'month' && (
                <MonthSelector
                  year={selectedAllTeamsYear}
                  month={selectedAllTeamsMonth}
                  onYearChange={setSelectedAllTeamsYear}
                  onMonthChange={setSelectedAllTeamsMonth}
                />
              )}
              
              {allTeamsFilterType === 'date' && (
                <DateRangeSelector
                  fromDate={selectedAllTeamsFromDate}
                  toDate={selectedAllTeamsToDate}
                  onFromDateChange={setSelectedAllTeamsFromDate}
                  onToDateChange={setSelectedAllTeamsToDate}
                />
              )}
          </div>
          </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={allTeamsData} margin={{ top: 25, right: 16, bottom: 5, left: 8 }}>
                <defs>
                  <linearGradient id="trackGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" />
                    <stop offset="100%" stopColor="#059669" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="name" 
                  stroke="#4a5568"
                  interval={0}
                  height={36}
                  tick={({ x, y, payload, index }) => {
                    if (index % 2 !== 0) return <g />;
                    const raw = payload.value || '';
                    const label = raw.length > 12 ? raw.substring(0, 11) + '…' : raw;
                    return (
                      <g transform={`translate(${x},${y})`}>
                        <text x={0} y={0} dy={12} textAnchor="middle" fill="#4a5568" fontSize={8}>{label}</text>
                      </g>
                    );
                  }}
                />
                <YAxis stroke="#4a5568" style={{ fontSize: '11px' }} />
                <Tooltip 
                  contentStyle={{ 
                    background: 'white', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                  formatter={(value) => [formatScreenTime(value), 'Screen Time']}
                  labelFormatter={(label) => {
                    const item = allTeamsData.find(d => d.name === label);
                    if (item?.isTrackLevel) return `Track: ${label}`;
                    return item ? `${label} (${item.track})` : label;
                  }}
                />
                <Bar 
                  dataKey="screenTime" 
                  barSize={12}
                  fill="url(#trackGradient)" 
                  radius={[8, 8, 0, 0]}
                >
                  <LabelList dataKey="screenTime" position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '11px' }} formatter={(value) => value > 0 ? Math.round(value) : ''} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          <div className="download-button-container">
            <button onClick={handleDownloadAllTeamsData} className="download-btn">
              📥 Download
            </button>
          </div>
        </div>

        {/* Trends of Login Count Dashboard */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">📈 Trends of Login Count</h3>
            <p className="chart-subtitle">{loginTrendsTrack?.length > 0 ? 'Top 5 users by login trend for selected track(s)' : loginTrendsSBU?.length > 0 ? 'Top 5 tracks by login trend for selected SBU — Select a Track to see users' : 'Select SBU → Track to see login trends over time'}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
            <div className="filters-row">
              {/* SBU Selector */}
              <div className="sbu-selector" ref={loginTrendsSBUDropdownRef} style={{ 
                position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '180px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>SBU:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowLoginTrendsSBUDropdown(!showLoginTrendsSBUDropdown)}
                    style={{
                      padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}
                  >
                    <span style={{ fontSize: '12px', color: loginTrendsSBU?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500' }}>
                      {getSelectedLoginTrendsSBULabel()}
                    </span>
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showLoginTrendsSBUDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showLoginTrendsSBUDropdown && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white',
                      border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '220px', overflowY: 'auto', zIndex: 1000
                    }}>
                      <div onClick={handleSelectAllLoginTrendsSBU} style={{
                        padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                        borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb', fontWeight: '600', position: 'sticky', top: 0, zIndex: 1
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef3c7'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      >
                        <input type="checkbox" checked={isAllLoginTrendsSBUSelected()} onChange={handleSelectAllLoginTrendsSBU}
                          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }} onClick={(e) => e.stopPropagation()} />
                        <span style={{ fontSize: '12px', color: '#1f2937' }}>Select All SBU</span>
                      </div>
                      {availableUserSBUs.filter(s => s.value !== 'all').map((sbu) => {
                        const isSelected = Array.isArray(loginTrendsSBU) && loginTrendsSBU.some(s => s.value === sbu.value);
                        return (
                          <div key={sbu.value} onClick={() => handleToggleLoginTrendsSBU(sbu)} style={{
                            padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            backgroundColor: isSelected ? '#fffbeb' : 'white', transition: 'background-color 0.15s ease'
                          }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#fffbeb' : 'white'; }}
                          >
                            <input type="checkbox" checked={isSelected} onChange={() => handleToggleLoginTrendsSBU(sbu)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }} onClick={(e) => e.stopPropagation()} />
                            <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{sbu.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Track Selector */}
              <div className="track-selector" ref={loginTrendsTrackDropdownRef} style={{ 
                position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '200px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>Track:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowLoginTrendsTrackDropdown(!showLoginTrendsTrackDropdown)}
                    style={{
                      padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}
                  >
                    <span style={{ fontSize: '12px', color: loginTrendsTrack?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500', flex: 1 }}>
                      {getSelectedLoginTrendsTrackLabel()}
                    </span>
                    {loginTrendsTrack?.length > 0 && <span onClick={(e) => { e.stopPropagation(); setLoginTrendsTrack([]); }} style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>×</span>}
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showLoginTrendsTrackDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showLoginTrendsTrackDropdown && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white',
                      border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '320px', overflowY: 'auto', zIndex: 1000
                    }}>
                      {loginTrendsAvailableTracks.filter(t => t.value !== 'all').map((track) => {
                        const isSelected = Array.isArray(loginTrendsTrack) && loginTrendsTrack.some(t => t.value === track.value);
                        return (
                          <div key={track.value} onClick={() => handleToggleLoginTrendsTrack(track)} style={{
                            padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            backgroundColor: isSelected ? '#fffbeb' : 'white', transition: 'background-color 0.15s ease'
                          }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#fffbeb' : 'white'; }}
                          >
                            <input type="radio" name="loginTrendsTrackRadio" checked={isSelected} onChange={() => handleToggleLoginTrendsTrack(track)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }} onClick={(e) => e.stopPropagation()} />
                            <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{track.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Month/Week Toggle */}
              <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
                <button
                  onClick={() => setLoginTrendsFilterType('month')}
                  style={{
                    padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer',
                    backgroundColor: loginTrendsFilterType === 'month' ? '#f59e0b' : 'white',
                    color: loginTrendsFilterType === 'month' ? 'white' : '#4b5563',
                    transition: 'all 0.2s ease'
                  }}
                >Month</button>
                <button
                  onClick={() => setLoginTrendsFilterType('week')}
                  style={{
                    padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer',
                    borderLeft: '1px solid #d1d5db',
                    backgroundColor: loginTrendsFilterType === 'week' ? '#f59e0b' : 'white',
                    color: loginTrendsFilterType === 'week' ? 'white' : '#4b5563',
                    transition: 'all 0.2s ease'
                  }}
                >Week</button>
              </div>

              {/* Month Picker */}
              {(() => {
                const monthNamesLocal = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                const monthIndex = monthNamesLocal.indexOf(loginTrendsMonth);
                return (
                  <div style={{ width: '120px' }}>
                    <DatePicker
                      selected={new Date(parseInt(loginTrendsYear), monthIndex)}
                      onChange={(date) => {
                        if (date) {
                          setLoginTrendsYear(date.getFullYear().toString());
                          setLoginTrendsMonth(monthNamesLocal[date.getMonth()]);
                        }
                      }}
                      dateFormat="MM/yyyy"
                      showMonthYearPicker
                      className="date-input"
                      placeholderText="Select Month"
                      popperPlacement="bottom-start"
                      style={{ width: '100%' }}
                    />
                  </div>
                );
              })()}

              {/* Week Selector (only when week mode) */}
              {loginTrendsFilterType === 'week' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Week:</label>
                  <select
                    value={loginTrendsWeek}
                    onChange={(e) => setLoginTrendsWeek(e.target.value)}
                    style={{
                      padding: '5px 8px', fontSize: '11px', border: '1px solid #d1d5db', borderRadius: '6px',
                      backgroundColor: 'white', color: '#374151', cursor: 'pointer'
                    }}
                  >
                    <option value="1">Week 1</option>
                    <option value="2">Week 2</option>
                    <option value="3">Week 3</option>
                    <option value="4">Week 4</option>
                  </select>
                </div>
              )}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={loginTrendsData} margin={{ top: 25, right: 20, bottom: 5, left: 10 }}>
              <defs>
                <linearGradient id="trendColor0" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#94a3b8" />
                  <stop offset="100%" stopColor="#64748b" />
                </linearGradient>
                <linearGradient id="trendColor1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#a78bfa" />
                  <stop offset="100%" stopColor="#7c3aed" />
                </linearGradient>
                <linearGradient id="trendColor2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#60a5fa" />
                  <stop offset="100%" stopColor="#2563eb" />
                </linearGradient>
                <linearGradient id="trendColor3" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#34d399" />
                  <stop offset="100%" stopColor="#059669" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="name" stroke="#4a5568" interval={0} height={70}
                tick={({ x, y, payload }) => {
                  const label = payload.value || '';
                  const maxChars = 10;
                  const words = label.split(/[\s&]+/);
                  const lines = [];
                  let current = '';
                  words.forEach(w => {
                    if (current && (current + ' ' + w).length > maxChars) { lines.push(current); current = w; }
                    else { current = current ? current + ' ' + w : w; }
                  });
                  if (current) lines.push(current);
                  return (<g transform={`translate(${x},${y})`}>{lines.map((line, i) => (<text key={i} x={0} y={0} dy={10 + i * 11} textAnchor="middle" fill="#4a5568" fontSize={8}>{line}</text>))}</g>);
                }}
              />
              <YAxis stroke="#4a5568" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                itemSorter={(item) => loginTrendsPeriods.indexOf(item.dataKey)}
                formatter={(value, name) => [value, name.replace(/^\d+\|/, '')]}
              />
              <Legend wrapperStyle={{ paddingTop: '4px' }} formatter={(value) => value.replace(/^\d+\|/, '')} />
              {loginTrendsPeriods.map((period, idx) => (
                <Bar key={period} dataKey={period} fill={`url(#trendColor${idx})`} barSize={12} radius={[6, 6, 0, 0]}>
                  <LabelList dataKey={period} position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '10px' }} formatter={(value) => value > 0 ? value : ''} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
          <div className="download-button-container">
            <button onClick={handleDownloadLoginTrends} className="download-btn">📥 Download</button>
          </div>
        </div>

        {/* Trends of Ticket Process Dashboard */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">📈 Trends of Ticket Process</h3>
            <p className="chart-subtitle">{ticketTrendsTrack?.length > 0 ? 'Top 5 users by process trend for selected track(s)' : ticketTrendsSBU?.length > 0 ? 'Top 5 tracks by process trend for selected SBU — Select a Track to see users' : 'Select SBU → Track to see ticket process trends over time'}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
            <div className="filters-row">
              {/* SBU Selector */}
              <div className="sbu-selector" ref={ticketTrendsSBUDropdownRef} style={{ 
                position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '180px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>SBU:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowTicketTrendsSBUDropdown(!showTicketTrendsSBUDropdown)}
                    style={{
                      padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}
                  >
                    <span style={{ fontSize: '12px', color: ticketTrendsSBU?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500' }}>
                      {getSelectedTicketTrendsSBULabel()}
                    </span>
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showTicketTrendsSBUDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showTicketTrendsSBUDropdown && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white',
                      border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '220px', overflowY: 'auto', zIndex: 1000
                    }}>
                      <div onClick={handleSelectAllTicketTrendsSBU} style={{
                        padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                        borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb', fontWeight: '600', position: 'sticky', top: 0, zIndex: 1
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef3c7'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      >
                        <input type="checkbox" checked={isAllTicketTrendsSBUSelected()} onChange={handleSelectAllTicketTrendsSBU}
                          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }} onClick={(e) => e.stopPropagation()} />
                        <span style={{ fontSize: '12px', color: '#1f2937' }}>Select All SBU</span>
                      </div>
                      {availableUserSBUs.filter(s => s.value !== 'all').map((sbu) => {
                        const isSelected = Array.isArray(ticketTrendsSBU) && ticketTrendsSBU.some(s => s.value === sbu.value);
                        return (
                          <div key={sbu.value} onClick={() => handleToggleTicketTrendsSBU(sbu)} style={{
                            padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            backgroundColor: isSelected ? '#fffbeb' : 'white', transition: 'background-color 0.15s ease'
                          }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#fffbeb' : 'white'; }}
                          >
                            <input type="checkbox" checked={isSelected} onChange={() => handleToggleTicketTrendsSBU(sbu)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }} onClick={(e) => e.stopPropagation()} />
                            <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{sbu.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Track Selector */}
              <div className="track-selector" ref={ticketTrendsTrackDropdownRef} style={{ 
                position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '200px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>Track:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowTicketTrendsTrackDropdown(!showTicketTrendsTrackDropdown)}
                    style={{
                      padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}
                  >
                    <span style={{ fontSize: '12px', color: ticketTrendsTrack?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500', flex: 1 }}>
                      {getSelectedTicketTrendsTrackLabel()}
                    </span>
                    {ticketTrendsTrack?.length > 0 && <span onClick={(e) => { e.stopPropagation(); setTicketTrendsTrack([]); }} style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>×</span>}
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showTicketTrendsTrackDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showTicketTrendsTrackDropdown && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white',
                      border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '320px', overflowY: 'auto', zIndex: 1000
                    }}>
                      {ticketTrendsAvailableTracks.filter(t => t.value !== 'all').map((track) => {
                        const isSelected = Array.isArray(ticketTrendsTrack) && ticketTrendsTrack.some(t => t.value === track.value);
                        return (
                          <div key={track.value} onClick={() => handleToggleTicketTrendsTrack(track)} style={{
                            padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            backgroundColor: isSelected ? '#fffbeb' : 'white', transition: 'background-color 0.15s ease'
                          }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#fffbeb' : 'white'; }}
                          >
                            <input type="radio" name="ticketTrendsTrackRadio" checked={isSelected} onChange={() => handleToggleTicketTrendsTrack(track)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }} onClick={(e) => e.stopPropagation()} />
                            <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{track.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Month/Week Toggle */}
              <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
                <button
                  onClick={() => setTicketTrendsFilterType('month')}
                  style={{
                    padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer',
                    backgroundColor: ticketTrendsFilterType === 'month' ? '#f59e0b' : 'white',
                    color: ticketTrendsFilterType === 'month' ? 'white' : '#4b5563',
                    transition: 'all 0.2s ease'
                  }}
                >Month</button>
                <button
                  onClick={() => setTicketTrendsFilterType('week')}
                  style={{
                    padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer',
                    borderLeft: '1px solid #d1d5db',
                    backgroundColor: ticketTrendsFilterType === 'week' ? '#f59e0b' : 'white',
                    color: ticketTrendsFilterType === 'week' ? 'white' : '#4b5563',
                    transition: 'all 0.2s ease'
                  }}
                >Week</button>
              </div>

              {/* Month Picker */}
              {(() => {
                const monthNamesLocal = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                const monthIndex = monthNamesLocal.indexOf(ticketTrendsMonth);
                return (
                  <div style={{ width: '120px' }}>
                    <DatePicker
                      selected={new Date(parseInt(ticketTrendsYear), monthIndex)}
                      onChange={(date) => {
                        if (date) {
                          setTicketTrendsYear(date.getFullYear().toString());
                          setTicketTrendsMonth(monthNamesLocal[date.getMonth()]);
                        }
                      }}
                      dateFormat="MM/yyyy"
                      showMonthYearPicker
                      className="date-input"
                      placeholderText="Select Month"
                      popperPlacement="bottom-start"
                      style={{ width: '100%' }}
                    />
                  </div>
                );
              })()}

              {/* Week Selector (only when week mode) */}
              {ticketTrendsFilterType === 'week' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Week:</label>
                  <select
                    value={ticketTrendsWeek}
                    onChange={(e) => setTicketTrendsWeek(e.target.value)}
                    style={{
                      padding: '5px 8px', fontSize: '11px', border: '1px solid #d1d5db', borderRadius: '6px',
                      backgroundColor: 'white', color: '#374151', cursor: 'pointer'
                    }}
                  >
                    <option value="1">Week 1</option>
                    <option value="2">Week 2</option>
                    <option value="3">Week 3</option>
                    <option value="4">Week 4</option>
                  </select>
                </div>
              )}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={ticketTrendsData} margin={{ top: 25, right: 20, bottom: 5, left: 10 }}>
              <defs>
                <linearGradient id="ticketTrendColor0" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#94a3b8" />
                  <stop offset="100%" stopColor="#64748b" />
                </linearGradient>
                <linearGradient id="ticketTrendColor1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#a78bfa" />
                  <stop offset="100%" stopColor="#7c3aed" />
                </linearGradient>
                <linearGradient id="ticketTrendColor2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#60a5fa" />
                  <stop offset="100%" stopColor="#2563eb" />
                </linearGradient>
                <linearGradient id="ticketTrendColor3" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#34d399" />
                  <stop offset="100%" stopColor="#059669" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="name" stroke="#4a5568" interval={0} height={70}
                tick={({ x, y, payload }) => {
                  const label = payload.value || '';
                  const maxChars = 10;
                  const words = label.split(/[\s&]+/);
                  const lines = [];
                  let current = '';
                  words.forEach(w => {
                    if (current && (current + ' ' + w).length > maxChars) { lines.push(current); current = w; }
                    else { current = current ? current + ' ' + w : w; }
                  });
                  if (current) lines.push(current);
                  return (<g transform={`translate(${x},${y})`}>{lines.map((line, i) => (<text key={i} x={0} y={0} dy={10 + i * 11} textAnchor="middle" fill="#4a5568" fontSize={8}>{line}</text>))}</g>);
                }}
              />
              <YAxis stroke="#4a5568" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                itemSorter={(item) => ticketTrendsPeriods.indexOf(item.dataKey)}
                formatter={(value, name) => [value, name.replace(/^\d+\|/, '')]}
              />
              <Legend wrapperStyle={{ paddingTop: '4px' }} formatter={(value) => value.replace(/^\d+\|/, '')} />
              {ticketTrendsPeriods.map((period, idx) => (
                <Bar key={period} dataKey={period} fill={`url(#ticketTrendColor${idx})`} barSize={12} radius={[6, 6, 0, 0]}>
                  <LabelList dataKey={period} position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '10px' }} formatter={(value) => value > 0 ? value : ''} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
          <div className="download-button-container">
            <button onClick={handleDownloadTicketTrends} className="download-btn">📥 Download</button>
          </div>
        </div>

        {/* Trends of Overall Feedback Dashboard */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">📈 Trends of Overall Feedback</h3>
            <p className="chart-subtitle">{feedbackTrendsTrack?.length > 0 ? 'Top 5 users by feedback trend for selected track(s)' : feedbackTrendsSBU?.length > 0 ? 'Top 5 tracks by feedback trend for selected SBU — Select a Track to see users' : 'Select SBU → Track to see feedback trends over time'}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
            <div className="filters-row">
              <div className="sbu-selector" ref={feedbackTrendsSBUDropdownRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '180px' }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>SBU:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div onClick={() => setShowFeedbackTrendsSBUDropdown(!showFeedbackTrendsSBUDropdown)} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}>
                    <span style={{ fontSize: '12px', color: feedbackTrendsSBU?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500' }}>{getSelectedFeedbackTrendsSBULabel()}</span>
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showFeedbackTrendsSBUDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showFeedbackTrendsSBUDropdown && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)', maxHeight: '220px', overflowY: 'auto', zIndex: 1000 }}>
                      <div onClick={handleSelectAllFeedbackTrendsSBU} style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb', fontWeight: '600', position: 'sticky', top: 0, zIndex: 1 }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef3c7'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}>
                        <input type="checkbox" checked={isAllFeedbackTrendsSBUSelected()} onChange={handleSelectAllFeedbackTrendsSBU} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }} onClick={(e) => e.stopPropagation()} />
                        <span style={{ fontSize: '12px', color: '#1f2937' }}>Select All SBU</span>
                      </div>
                      {availableUserSBUs.filter(s => s.value !== 'all').map((sbu) => {
                        const isSelected = Array.isArray(feedbackTrendsSBU) && feedbackTrendsSBU.some(s => s.value === sbu.value);
                        return (<div key={sbu.value} onClick={() => handleToggleFeedbackTrendsSBU(sbu)} style={{ padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: isSelected ? '#fffbeb' : 'white', transition: 'background-color 0.15s ease' }} onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#fffbeb' : 'white'; }}>
                          <input type="checkbox" checked={isSelected} onChange={() => handleToggleFeedbackTrendsSBU(sbu)} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }} onClick={(e) => e.stopPropagation()} />
                          <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{sbu.label}</span>
                        </div>);
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="track-selector" ref={feedbackTrendsTrackDropdownRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '200px' }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>Track:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div onClick={() => setShowFeedbackTrendsTrackDropdown(!showFeedbackTrendsTrackDropdown)} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}>
                    <span style={{ fontSize: '12px', color: feedbackTrendsTrack?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500', flex: 1 }}>{getSelectedFeedbackTrendsTrackLabel()}</span>
                    {feedbackTrendsTrack?.length > 0 && <span onClick={(e) => { e.stopPropagation(); setFeedbackTrendsTrack([]); }} style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>×</span>}
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showFeedbackTrendsTrackDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showFeedbackTrendsTrackDropdown && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)', maxHeight: '320px', overflowY: 'auto', zIndex: 1000 }}>
                      {feedbackTrendsAvailableTracks.filter(t => t.value !== 'all').map((track) => {
                        const isSelected = Array.isArray(feedbackTrendsTrack) && feedbackTrendsTrack.some(t => t.value === track.value);
                        return (<div key={track.value} onClick={() => handleToggleFeedbackTrendsTrack(track)} style={{ padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: isSelected ? '#fffbeb' : 'white', transition: 'background-color 0.15s ease' }} onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#fffbeb' : 'white'; }}>
                          <input type="radio" name="feedbackTrendsTrackRadio" checked={isSelected} onChange={() => handleToggleFeedbackTrendsTrack(track)} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }} onClick={(e) => e.stopPropagation()} />
                          <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{track.label}</span>
                        </div>);
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
                <button onClick={() => setFeedbackTrendsFilterType('month')} style={{ padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer', backgroundColor: feedbackTrendsFilterType === 'month' ? '#f59e0b' : 'white', color: feedbackTrendsFilterType === 'month' ? 'white' : '#4b5563', transition: 'all 0.2s ease' }}>Month</button>
                <button onClick={() => setFeedbackTrendsFilterType('week')} style={{ padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer', borderLeft: '1px solid #d1d5db', backgroundColor: feedbackTrendsFilterType === 'week' ? '#f59e0b' : 'white', color: feedbackTrendsFilterType === 'week' ? 'white' : '#4b5563', transition: 'all 0.2s ease' }}>Week</button>
              </div>
              {(() => {
                const monthNamesLocal = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                const monthIndex = monthNamesLocal.indexOf(feedbackTrendsMonth);
                return (<div style={{ width: '120px' }}><DatePicker selected={new Date(parseInt(feedbackTrendsYear), monthIndex)} onChange={(date) => { if (date) { setFeedbackTrendsYear(date.getFullYear().toString()); setFeedbackTrendsMonth(monthNamesLocal[date.getMonth()]); } }} dateFormat="MM/yyyy" showMonthYearPicker className="date-input" placeholderText="Select Month" popperPlacement="bottom-start" /></div>);
              })()}
              {feedbackTrendsFilterType === 'week' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Week:</label>
                  <select value={feedbackTrendsWeek} onChange={(e) => setFeedbackTrendsWeek(e.target.value)} style={{ padding: '5px 8px', fontSize: '11px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: 'white', color: '#374151', cursor: 'pointer' }}>
                    <option value="1">Week 1</option><option value="2">Week 2</option><option value="3">Week 3</option><option value="4">Week 4</option>
                  </select>
                </div>
              )}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={feedbackTrendsData} margin={{ top: 25, right: 20, bottom: 5, left: 10 }}>
              <defs>
                {feedbackTrendsPeriods.map((period, idx) => (
                  <linearGradient key={`fbTrend${idx}`} id={`fbTrendColor${idx}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={period.includes('👍') ? '#86efac' : '#fca5a5'} />
                    <stop offset="100%" stopColor={period.includes('👍') ? '#22c55e' : '#ef4444'} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#4a5568" interval={0} height={70} tick={({ x, y, payload }) => { const label = payload.value || ''; const maxChars = 10; const words = label.split(/[\s&]+/); const lines = []; let current = ''; words.forEach(w => { if (current && (current + ' ' + w).length > maxChars) { lines.push(current); current = w; } else { current = current ? current + ' ' + w : w; } }); if (current) lines.push(current); return (<g transform={`translate(${x},${y})`}>{lines.map((line, i) => (<text key={i} x={0} y={0} dy={10 + i * 11} textAnchor="middle" fill="#4a5568" fontSize={8}>{line}</text>))}</g>); }} />
              <YAxis stroke="#4a5568" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                itemSorter={(item) => feedbackTrendsPeriods.indexOf(item.dataKey)}
                formatter={(value, name) => {
                  const stripped = name.replace(/^\d+\|/, '');
                  const label = stripped.includes('👍') ? stripped.replace('👍', '- Satisfied') : stripped.includes('👎') ? stripped.replace('👎', '- Not Satisfied') : stripped;
                  return [value, label];
                }}
              />
              <Legend wrapperStyle={{ paddingTop: '4px' }} formatter={(value) => { const s = value.replace(/^\d+\|/, ''); return s.includes('👍') ? s.replace('👍', 'Satisfied') : s.includes('👎') ? s.replace('👎', 'Not Satisfied') : s; }} />
              {feedbackTrendsPeriods.map((period, idx) => (
                <Bar key={period} dataKey={period} fill={`url(#fbTrendColor${idx})`} barSize={12} radius={[6, 6, 0, 0]}>
                  <LabelList dataKey={period} position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '9px' }} formatter={(value) => value > 0 ? value : ''} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
          <div className="download-button-container">
            <button onClick={handleDownloadFeedbackTrends} className="download-btn">📥 Download</button>
          </div>
        </div>

        {/* Trends of Areas to be Improved Dashboard */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">📈 Trends of Areas to be Improved</h3>
            <p className="chart-subtitle">Top 5 improvement reasons trend over time — Select SBU/Track to filter</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
            <div className="filters-row">
              <div className="sbu-selector" ref={areasTrendsSBUDropdownRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '180px' }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>SBU:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div onClick={() => setShowAreasTrendsSBUDropdown(!showAreasTrendsSBUDropdown)} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}>
                    <span style={{ fontSize: '12px', color: areasTrendsSBU?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500' }}>{getSelectedAreasTrendsSBULabel()}</span>
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showAreasTrendsSBUDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showAreasTrendsSBUDropdown && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)', maxHeight: '220px', overflowY: 'auto', zIndex: 1000 }}>
                      <div onClick={handleSelectAllAreasTrendsSBU} style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb', fontWeight: '600', position: 'sticky', top: 0, zIndex: 1 }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef3c7'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}>
                        <input type="checkbox" checked={isAllAreasTrendsSBUSelected()} onChange={handleSelectAllAreasTrendsSBU} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }} onClick={(e) => e.stopPropagation()} />
                        <span style={{ fontSize: '12px', color: '#1f2937' }}>Select All SBU</span>
                      </div>
                      {availableUserSBUs.filter(s => s.value !== 'all').map((sbu) => {
                        const isSelected = Array.isArray(areasTrendsSBU) && areasTrendsSBU.some(s => s.value === sbu.value);
                        return (<div key={sbu.value} onClick={() => handleToggleAreasTrendsSBU(sbu)} style={{ padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: isSelected ? '#fffbeb' : 'white', transition: 'background-color 0.15s ease' }} onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#fffbeb' : 'white'; }}>
                          <input type="checkbox" checked={isSelected} onChange={() => handleToggleAreasTrendsSBU(sbu)} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }} onClick={(e) => e.stopPropagation()} />
                          <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{sbu.label}</span>
                        </div>);
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="track-selector" ref={areasTrendsTrackDropdownRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '200px' }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>Track:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div onClick={() => setShowAreasTrendsTrackDropdown(!showAreasTrendsTrackDropdown)} style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}>
                    <span style={{ fontSize: '12px', color: areasTrendsTrack?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500', flex: 1 }}>{getSelectedAreasTrendsTrackLabel()}</span>
                    {areasTrendsTrack?.length > 0 && <span onClick={(e) => { e.stopPropagation(); setAreasTrendsTrack([]); }} style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>×</span>}
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showAreasTrendsTrackDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showAreasTrendsTrackDropdown && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)', maxHeight: '320px', overflowY: 'auto', zIndex: 1000 }}>
                      {areasTrendsAvailableTracks.filter(t => t.value !== 'all').map((track) => {
                        const isSelected = Array.isArray(areasTrendsTrack) && areasTrendsTrack.some(t => t.value === track.value);
                        return (<div key={track.value} onClick={() => handleToggleAreasTrendsTrack(track)} style={{ padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: isSelected ? '#fffbeb' : 'white', transition: 'background-color 0.15s ease' }} onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#fffbeb' : 'white'; }}>
                          <input type="radio" name="areasTrendsTrackRadio" checked={isSelected} onChange={() => handleToggleAreasTrendsTrack(track)} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }} onClick={(e) => e.stopPropagation()} />
                          <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{track.label}</span>
                        </div>);
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
                <button onClick={() => setAreasTrendsFilterType('month')} style={{ padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer', backgroundColor: areasTrendsFilterType === 'month' ? '#f59e0b' : 'white', color: areasTrendsFilterType === 'month' ? 'white' : '#4b5563', transition: 'all 0.2s ease' }}>Month</button>
                <button onClick={() => setAreasTrendsFilterType('week')} style={{ padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer', borderLeft: '1px solid #d1d5db', backgroundColor: areasTrendsFilterType === 'week' ? '#f59e0b' : 'white', color: areasTrendsFilterType === 'week' ? 'white' : '#4b5563', transition: 'all 0.2s ease' }}>Week</button>
              </div>
              {(() => {
                const monthNamesLocal = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                const monthIndex = monthNamesLocal.indexOf(areasTrendsMonth);
                return (<div style={{ width: '120px' }}><DatePicker selected={new Date(parseInt(areasTrendsYear), monthIndex)} onChange={(date) => { if (date) { setAreasTrendsYear(date.getFullYear().toString()); setAreasTrendsMonth(monthNamesLocal[date.getMonth()]); } }} dateFormat="MM/yyyy" showMonthYearPicker className="date-input" placeholderText="Select Month" popperPlacement="bottom-start" /></div>);
              })()}
              {areasTrendsFilterType === 'week' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Week:</label>
                  <select value={areasTrendsWeek} onChange={(e) => setAreasTrendsWeek(e.target.value)} style={{ padding: '5px 8px', fontSize: '11px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: 'white', color: '#374151', cursor: 'pointer' }}>
                    <option value="1">Week 1</option><option value="2">Week 2</option><option value="3">Week 3</option><option value="4">Week 4</option>
                  </select>
                </div>
              )}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={areasTrendsData} margin={{ top: 25, right: 20, bottom: 5, left: 10 }}>
              <defs>
                <linearGradient id="areasTrendColor0" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#94a3b8" /><stop offset="100%" stopColor="#64748b" /></linearGradient>
                <linearGradient id="areasTrendColor1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#a78bfa" /><stop offset="100%" stopColor="#7c3aed" /></linearGradient>
                <linearGradient id="areasTrendColor2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#60a5fa" /><stop offset="100%" stopColor="#2563eb" /></linearGradient>
                <linearGradient id="areasTrendColor3" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#34d399" /><stop offset="100%" stopColor="#059669" /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#4a5568" interval={0} height={70} tick={({ x, y, payload }) => { const label = payload.value || ''; const maxChars = 10; const words = label.split(/[\s&]+/); const lines = []; let current = ''; words.forEach(w => { if (current && (current + ' ' + w).length > maxChars) { lines.push(current); current = w; } else { current = current ? current + ' ' + w : w; } }); if (current) lines.push(current); return (<g transform={`translate(${x},${y})`}>{lines.map((line, i) => (<text key={i} x={0} y={0} dy={10 + i * 11} textAnchor="middle" fill="#4a5568" fontSize={8}>{line}</text>))}</g>); }} />
              <YAxis stroke="#4a5568" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                itemSorter={(item) => areasTrendsPeriods.indexOf(item.dataKey)}
                formatter={(value, name) => [value, name.replace(/^\d+\|/, '')]}
              />
              <Legend wrapperStyle={{ paddingTop: '4px' }} formatter={(value) => value.replace(/^\d+\|/, '')} />
              {areasTrendsPeriods.map((period, idx) => (
                <Bar key={period} dataKey={period} fill={`url(#areasTrendColor${idx})`} barSize={12} radius={[6, 6, 0, 0]}>
                  <LabelList dataKey={period} position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '10px' }} formatter={(value) => value > 0 ? value : ''} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
          <div className="download-button-container">
            <button onClick={handleDownloadAreasTrends} className="download-btn">📥 Download</button>
          </div>
        </div>

        {/* Trends of Screen Time by Track Dashboard */}
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">📈 Trends of Screen Time by Track</h3>
            <p className="chart-subtitle">{screenTimeTrendsTrack?.length > 0 ? 'Top 5 users by screen time trend for selected track(s)' : screenTimeTrendsSBU?.length > 0 ? 'Top 5 tracks by screen time trend for selected SBU — Select a Track to see users' : 'Select SBU → Track to see screen time trends over time'}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
            <div className="filters-row">
              {/* SBU Selector */}
              <div className="sbu-selector" ref={screenTimeTrendsSBUDropdownRef} style={{ 
                position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '180px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>SBU:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowScreenTimeTrendsSBUDropdown(!showScreenTimeTrendsSBUDropdown)}
                    style={{
                      padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}
                  >
                    <span style={{ fontSize: '12px', color: screenTimeTrendsSBU?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500' }}>
                      {getSelectedScreenTimeTrendsSBULabel()}
                    </span>
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showScreenTimeTrendsSBUDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showScreenTimeTrendsSBUDropdown && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white',
                      border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '220px', overflowY: 'auto', zIndex: 1000
                    }}>
                      <div onClick={handleSelectAllScreenTimeTrendsSBU} style={{
                        padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                        borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb', fontWeight: '600', position: 'sticky', top: 0, zIndex: 1
                      }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef3c7'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      >
                        <input type="checkbox" checked={isAllScreenTimeTrendsSBUSelected()} onChange={handleSelectAllScreenTimeTrendsSBU}
                          style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }} onClick={(e) => e.stopPropagation()} />
                        <span style={{ fontSize: '12px', color: '#1f2937' }}>Select All SBU</span>
                      </div>
                      {availableUserSBUs.filter(s => s.value !== 'all').map((sbu) => {
                        const isSelected = Array.isArray(screenTimeTrendsSBU) && screenTimeTrendsSBU.some(s => s.value === sbu.value);
                        return (
                          <div key={sbu.value} onClick={() => handleToggleScreenTimeTrendsSBU(sbu)} style={{
                            padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            backgroundColor: isSelected ? '#fffbeb' : 'white', transition: 'background-color 0.15s ease'
                          }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#fffbeb' : 'white'; }}
                          >
                            <input type="checkbox" checked={isSelected} onChange={() => handleToggleScreenTimeTrendsSBU(sbu)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }} onClick={(e) => e.stopPropagation()} />
                            <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{sbu.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Track Selector */}
              <div className="track-selector" ref={screenTimeTrendsTrackDropdownRef} style={{ 
                position: 'relative', display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px', minWidth: '200px'
              }}>
                <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', whiteSpace: 'nowrap' }}>Track:</label>
                <div style={{ position: 'relative', flex: 1 }}>
                  <div
                    onClick={() => setShowScreenTimeTrendsTrackDropdown(!showScreenTimeTrendsTrackDropdown)}
                    style={{
                      padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'all 0.2s ease', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(245, 158, 11, 0.1)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)'; }}
                  >
                    <span style={{ fontSize: '12px', color: screenTimeTrendsTrack?.length > 0 ? '#1f2937' : '#9ca3af', fontWeight: '500', flex: 1 }}>
                      {getSelectedScreenTimeTrendsTrackLabel()}
                    </span>
                    {screenTimeTrendsTrack?.length > 0 && <span onClick={(e) => { e.stopPropagation(); setScreenTimeTrendsTrack([]); }} style={{ fontSize: '14px', color: '#9ca3af', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }} onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'} onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}>×</span>}
                    <span style={{ fontSize: '10px', color: '#6b7280', transform: showScreenTimeTrendsTrackDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>▼</span>
                  </div>
                  {showScreenTimeTrendsTrackDropdown && (
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: 'white',
                      border: '1px solid #e5e7eb', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                      maxHeight: '320px', overflowY: 'auto', zIndex: 1000
                    }}>
                      {screenTimeTrendsAvailableTracks.filter(t => t.value !== 'all').map((track) => {
                        const isSelected = Array.isArray(screenTimeTrendsTrack) && screenTimeTrendsTrack.some(t => t.value === track.value);
                        return (
                          <div key={track.value} onClick={() => handleToggleScreenTimeTrendsTrack(track)} style={{
                            padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            backgroundColor: isSelected ? '#fffbeb' : 'white', transition: 'background-color 0.15s ease'
                          }}
                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isSelected ? '#fffbeb' : 'white'; }}
                          >
                            <input type="radio" name="screenTimeTrendsTrackRadio" checked={isSelected} onChange={() => handleToggleScreenTimeTrendsTrack(track)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#f59e0b' }} onClick={(e) => e.stopPropagation()} />
                            <span style={{ fontSize: '12px', color: '#374151', fontWeight: isSelected ? '500' : '400' }}>{track.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Month/Week Toggle */}
              <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
                <button
                  onClick={() => setScreenTimeTrendsFilterType('month')}
                  style={{
                    padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer',
                    backgroundColor: screenTimeTrendsFilterType === 'month' ? '#f59e0b' : 'white',
                    color: screenTimeTrendsFilterType === 'month' ? 'white' : '#4b5563',
                    transition: 'all 0.2s ease'
                  }}
                >Month</button>
                <button
                  onClick={() => setScreenTimeTrendsFilterType('week')}
                  style={{
                    padding: '5px 12px', fontSize: '11px', fontWeight: '600', border: 'none', cursor: 'pointer',
                    borderLeft: '1px solid #d1d5db',
                    backgroundColor: screenTimeTrendsFilterType === 'week' ? '#f59e0b' : 'white',
                    color: screenTimeTrendsFilterType === 'week' ? 'white' : '#4b5563',
                    transition: 'all 0.2s ease'
                  }}
                >Week</button>
              </div>

              {/* Month Picker */}
              {(() => {
                const monthNamesLocal = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                const monthIndex = monthNamesLocal.indexOf(screenTimeTrendsMonth);
                return (
                  <div style={{ width: '120px' }}>
                    <DatePicker
                      selected={new Date(parseInt(screenTimeTrendsYear), monthIndex)}
                      onChange={(date) => {
                        if (date) {
                          setScreenTimeTrendsYear(date.getFullYear().toString());
                          setScreenTimeTrendsMonth(monthNamesLocal[date.getMonth()]);
                        }
                      }}
                      dateFormat="MM/yyyy"
                      showMonthYearPicker
                      className="date-input"
                      placeholderText="Select Month"
                      popperPlacement="bottom-start"
                      style={{ width: '100%' }}
                    />
                  </div>
                );
              })()}

              {/* Week Selector (only when week mode) */}
              {screenTimeTrendsFilterType === 'week' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500' }}>Week:</label>
                  <select
                    value={screenTimeTrendsWeek}
                    onChange={(e) => setScreenTimeTrendsWeek(e.target.value)}
                    style={{
                      padding: '5px 8px', fontSize: '11px', border: '1px solid #d1d5db', borderRadius: '6px',
                      backgroundColor: 'white', color: '#374151', cursor: 'pointer'
                    }}
                  >
                    <option value="1">Week 1</option>
                    <option value="2">Week 2</option>
                    <option value="3">Week 3</option>
                    <option value="4">Week 4</option>
                  </select>
                </div>
              )}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={screenTimeTrendsData} margin={{ top: 25, right: 20, bottom: 5, left: 10 }}>
              <defs>
                <linearGradient id="stTrendColor0" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#94a3b8" />
                  <stop offset="100%" stopColor="#64748b" />
                </linearGradient>
                <linearGradient id="stTrendColor1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#a78bfa" />
                  <stop offset="100%" stopColor="#7c3aed" />
                </linearGradient>
                <linearGradient id="stTrendColor2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#60a5fa" />
                  <stop offset="100%" stopColor="#2563eb" />
                </linearGradient>
                <linearGradient id="stTrendColor3" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#34d399" />
                  <stop offset="100%" stopColor="#059669" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis 
                dataKey="name" stroke="#4a5568" interval={0} height={70}
                tick={({ x, y, payload }) => {
                  const label = payload.value || '';
                  const maxChars = 10;
                  const words = label.split(/[\s&]+/);
                  const lines = [];
                  let current = '';
                  words.forEach(w => {
                    if (current && (current + ' ' + w).length > maxChars) { lines.push(current); current = w; }
                    else { current = current ? current + ' ' + w : w; }
                  });
                  if (current) lines.push(current);
                  return (<g transform={`translate(${x},${y})`}>{lines.map((line, i) => (<text key={i} x={0} y={0} dy={10 + i * 11} textAnchor="middle" fill="#4a5568" fontSize={8}>{line}</text>))}</g>);
                }}
              />
              <YAxis stroke="#4a5568" style={{ fontSize: '12px' }} />
              <Tooltip 
                contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                itemSorter={(item) => screenTimeTrendsPeriods.indexOf(item.dataKey)}
                formatter={(value, name) => [formatScreenTime(value), name.replace(/^\d+\|/, '')]}
              />
              <Legend wrapperStyle={{ paddingTop: '4px' }} formatter={(value) => value.replace(/^\d+\|/, '')} />
              {screenTimeTrendsPeriods.map((period, idx) => (
                <Bar key={period} dataKey={period} fill={`url(#stTrendColor${idx})`} barSize={12} radius={[6, 6, 0, 0]}>
                  <LabelList dataKey={period} position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '10px' }} formatter={(value) => value > 0 ? Math.round(value) : ''} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
          <div className="download-button-container">
            <button onClick={handleDownloadScreenTimeTrends} className="download-btn">📥 Download</button>
          </div>
        </div>

        {/* Dashboard 2: Screen Time by Users - DISABLED */}
        {false && (
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">Screen Time by Users</h3>
            <p className="chart-subtitle">Individual user screen time distribution</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
              <div className="group-selector">
                <label className="filter-label-nowrap">
                  Group:
                </label>
                <div ref={groupDropdownRef} className="dropdown-container-width">
                  <div 
                    onClick={() => setShowGroupDropdown(!showGroupDropdown)}
                    className="dropdown-button"
                  >
                    <span className={selectedGroup?.length > 0 ? 'dropdown-label selected' : 'dropdown-label'}>
                      {getSelectedGroupLabel()}
                    </span>
                    <span className={showGroupDropdown ? 'dropdown-arrow open' : 'dropdown-arrow'}>▼</span>
                  </div>
                  
                  {showGroupDropdown && (
                    <div className="dropdown-menu">
                      {/* Select All Option */}
                      <div 
                        onClick={handleSelectAllGroups}
                        className="dropdown-select-all dropdown-select-all blue"
                      >
                        <input 
                          type="checkbox" 
                          checked={isAllGroupsSelected()}
                          onChange={handleSelectAllGroups}
                          className="dropdown-checkbox"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="dropdown-select-all-label">Select All Groups</span>
                      </div>
                      
                      {/* Individual Group Options */}
                      {availableGroups.filter(g => g.value !== 'all').map((group, index) => (
                        <div 
                          key={group.value}
                          onClick={() => handleToggleGroup(group)}
                          className={Array.isArray(selectedGroup) && selectedGroup.some(g => g.value === group.value) ? 'dropdown-item selected' : 'dropdown-item'}
                        >
                          <input 
                            type="checkbox" 
                            checked={Array.isArray(selectedGroup) && selectedGroup.some(g => g.value === group.value)}
                            onChange={() => handleToggleGroup(group)}
                            className="dropdown-checkbox"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className={Array.isArray(selectedGroup) && selectedGroup.some(g => g.value === group.value) ? 'dropdown-item-label selected' : 'dropdown-item-label'}>{group.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Filter Type Selector */}
              <FilterTypeButtons 
                filterType={groupFilterType} 
                onFilterTypeChange={setGroupFilterType} 
              />
              
              {groupFilterType === 'month' && (
                <MonthSelector
                  year={selectedGroupYear}
                  month={selectedGroupMonth}
                  onYearChange={setSelectedGroupYear}
                  onMonthChange={setSelectedGroupMonth}
                />
              )}
              
              {groupFilterType === 'date' && (
                <DateRangeSelector
                  fromDate={selectedGroupFromDate}
                  toDate={selectedGroupToDate}
                  onFromDateChange={setSelectedGroupFromDate}
                  onToDateChange={setSelectedGroupToDate}
                />
              )}
          </div>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={assignmentGroupData} margin={{ top: 25, right: 16, bottom: 5, left: 8 }}>
                <defs>
                  <linearGradient id="usersGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#2563eb" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="name"
                  stroke="#4a5568"
                  interval={0}
                  height={80}
                  tick={({ x, y, payload }) => (
                    <g transform={`translate(${x},${y})`}>
                      <text x={0} y={0} dy={8} textAnchor="end" fill="#4a5568" fontSize={10} transform="rotate(-50)">
                        {payload.value}
                      </text>
                    </g>
                  )}
                />
                <YAxis stroke="#4a5568" style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ 
                    background: 'white', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                  formatter={(value) => [formatScreenTime(value), 'Screen Time']}
                />
                <Legend wrapperStyle={{ paddingTop: '8px' }} />
                <Bar 
                  dataKey="screenTime" 
                  barSize={12}
                  fill="url(#usersGradient)" 
                  radius={[8, 8, 0, 0]}
                  name="Screen Time (hrs)"
                >
                  <LabelList dataKey="screenTime" position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '11px' }} formatter={(value) => value > 0 ? Math.round(value) : ''} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          <div className="download-button-container">
            <button onClick={handleDownloadGroupData} className="download-btn">
              📥 Download
            </button>
          </div>
        </div>
        )}

        {/* Dashboard 1: Top 20 Screen Time by Assignment Groups - DISABLED */}
        {false && (
        <div className="chart-card">
          <div className="chart-header">
            <h3 className="chart-title">Top 20 Screen Time by Assignment Groups</h3>
            <p className="chart-subtitle">Assignment group screen time ranking</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '4px' }}>
              {/* Filter Type Selector */}
              <FilterTypeButtons 
                filterType={teamFilterType} 
                onFilterTypeChange={setTeamFilterType} 
              />
              
              {teamFilterType === 'month' && (
                <MonthSelector
                  year={selectedTeamYear}
                  month={selectedTeamMonth}
                  onYearChange={setSelectedTeamYear}
                  onMonthChange={setSelectedTeamMonth}
                />
              )}
              
              {teamFilterType === 'date' && (
                <DateRangeSelector
                  fromDate={selectedTeamFromDate}
                  toDate={selectedTeamToDate}
                  onFromDateChange={setSelectedTeamFromDate}
                  onToDateChange={setSelectedTeamToDate}
                />
              )}
          </div>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={teamScreenTimeData} margin={{ top: 25, right: 16, bottom: 5, left: 8 }}>
                <defs>
                  <linearGradient id="teamGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7B3FF2" />
                    <stop offset="100%" stopColor="#5A2FC2" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="name" 
                  stroke="#4a5568"
                  interval={0}
                  height={80}
                  tick={({ x, y, payload }) => (
                    <g transform={`translate(${x},${y})`}>
                      <text x={0} y={0} dy={8} textAnchor="end" fill="#4a5568" fontSize={10} transform="rotate(-50)">
                        {payload.value}
                      </text>
                    </g>
                  )}
                />
                <YAxis stroke="#4a5568" style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ 
                    background: 'white', 
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                  formatter={(value) => [formatScreenTime(value), 'Screen Time']}
                />
                <Legend wrapperStyle={{ paddingTop: '8px' }} />
                <Bar 
                  dataKey="screenTime" 
                  barSize={12}
                  fill="url(#teamGradient)" 
                  radius={[8, 8, 0, 0]}
                  name="Screen Time (hrs)"
                >
                  <LabelList dataKey="screenTime" position="top" style={{ fill: '#4a5568', fontWeight: 700, fontSize: '11px' }} formatter={(value) => value > 0 ? Math.round(value) : ''} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          <div className="download-button-container">
            <button onClick={handleDownloadTeamData} className="download-btn">
              📥 Download
            </button>
          </div>
        </div>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default UserAnalyticsTest;
